import axios from 'axios';
import * as annotationsTransport from '@/utils/courses-annotations-transport';
import {
  SEALED_WRITE_ERRORS,
  isEncryptedWriteRequiredError,
  isSealedWriteError,
} from '@/utils/courses-encryption/sealed-writes';
import {
  REVISION_CONFLICT_MESSAGE,
  isStaleRevisionError,
  onRevisionConflict,
} from '@/utils/courses-encryption/annotation-revisions';
import { ACCESS_NOTICE_STATE, UNREADABLE_REASON } from '@/constants/courses-encryption';
import { LOOKUP_TIMEOUT_MS, withLookupRetry } from '@/utils/transient-retry';
import { appendNotesById } from '@/utils/courses-notes-append';
import { isPersistedNoteId, newTempNoteId } from '@/utils/course-note-ids';
import { TagNameConflictError, isTagNameConflict } from '@/utils/courses-tags';
import {
  extractNotePlainText,
  isEmptyNoteBody,
  normalizeNoteBody,
} from '@/utils/course-note-rich-text';
import {
  HEX_TO_COLOR_ENUM,
  COLOR_ENUM_TO_HEX,
  DEFAULT_HIGHLIGHT_COLOR,
  DEFAULT_HIGHLIGHT_ENUM,
} from '@/constants/highlights';
import {
  BLANK_PLACEHOLDER,
  buildBlockOffsets,
  buildProcessedContent,
  collectWords,
  stripInlineMarkup,
} from '@/pages/courses/lesson-content-words';
import {
  findStoryInPages,
  isLifeStoriesBlock,
  isStoryAnchor,
  storyBlocks,
  storyIdFromAnchor,
  storyScopeKey,
} from '@/pages/courses/story-blocks';
import { notesCorpus, noteScopeOf } from '@/utils/courses-notes-corpus';
import {
  AUTOSAVE_TYPE,
  autosave,
  blankKey,
  blankScopeOf,
  noteKey,
  pendingOverlay,
} from '@/utils/courses-autosave';
import { AUTOSAVE_EVENT, countAutosaveEvent } from '@/utils/courses-autosave/telemetry';
import { classify, isRetryable } from '@/utils/courses-autosave/policy';
import { DEFAULT_MAX_CONFLICT_FORCES } from '@/utils/courses-autosave/engine';
import { enrolOnFirstBlank } from '@/utils/courses-encryption/enrol-on-first-blank';
import { COVER_LETTER_PAGE, ACCESS_FRESH_MS } from '@/constants/courses';
import { normalizeIdrCode } from '@/utils/idr-code';
import { isYoutubeUrl } from '@/utils/youtube';
import { newIdempotencyKey } from '@/utils/payments-api';
import {
  isNsMockEnabled,
  buildAccess as buildNsMockAccess,
  grantTrial as grantNsMockTrial,
  grantFull as grantNsMockFull,
  setPendingPurchaseSlug,
  getPendingPurchaseSlug,
  clearPendingPurchaseSlug,
  PROGRAM_SLUGS as NS_MOCK_PROGRAM_SLUGS,
  redeemBookCode as redeemNsMockBookCode,
  linkCg as linkNsMockCg,
  getMockCgs,
  mockPriceCents as nsMockPriceCents,
} from '@/utils/next-steps-mock';

// DEV-ONLY: the Next Steps Mock Mode console (src/pages/next-steps-mock) needs
// every server-driven course action to short-circuit into the local mock
// state machine instead of hitting the API. Gated on BOTH the mock toggle and
// non-production so this is fully inert in a production build regardless of
// what ends up in localStorage.
const nsMockActive = () => isNsMockEnabled() && process.env.VUE_APP_NODE_ENV !== 'production';

// ── localStorage read-cache keys ───────────────────────────────────────────
// API is the source of truth; these keys only cache the last successfully
// loaded API responses so the hub can paint instantly. They are NEVER a write
// path for a failed mutation (see the contract §1).
// Highlights and notes are SCOPED TO A READER VIEW — a leader marks up the
// Student's Guide and the Leader's Guide separately — so their caches are keyed
// per view. Sharing one key would paint the other guide's marks on a cold start,
// before the refetch lands. Tags ARE member-wide — one list across both parts and
// both guides (review 2026-09-10) — so one key is the whole truth for them.
const HIGHLIGHTS_STORAGE_KEY = view => `next-steps-highlights:${view}`;
const ALL_NOTES_STORAGE_KEY = view => `next-steps-all-notes:${view}`;
const ALL_HIGHLIGHTS_STORAGE_KEY = view => `next-steps-all-highlights:${view}`;
const TAGS_STORAGE_KEY = 'next-steps-note-tags-list';
// Persists the student/leader audience toggle so the choice survives a refresh.
const VIEW_STORAGE_KEY = 'next-steps-view';

// Key for the remembered reading position. The view is part of it because the two
// guides are different documents behind one slug — see `lessonPages` in the state.
function lessonPageKey(slug, view) {
  return `${slug}:${view === 'leader' ? 'leader' : 'student'}`;
}

// The persisted toggle, read the same way everywhere: anything but the literal
// 'leader' is the student view. Needed at module scope because the initial state
// hydrates the per-view caches before any getter exists.
function readView() {
  return readJson(VIEW_STORAGE_KEY, 'student') === 'leader' ? 'leader' : 'student';
}
// Persists the Next Steps light/dark choice. Deliberately NOT read/written via
// readJson/writeJson: the landing page has been storing a RAW 'dark' | 'light'
// string under this key, and JSON.parse('dark') throws — routing it through the
// JSON helpers would silently reset every member who already chose dark.
const THEME_STORAGE_KEY = 'courses-theme';

function readTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
  } catch (e) {
    return false;
  }
}

function writeTheme(isDark) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  } catch (e) {
    /* non-blocking */
  }
}

function loadHighlightsFromStorage(view) {
  try {
    const stored = localStorage.getItem(HIGHLIGHTS_STORAGE_KEY(view));
    const parsed = stored ? JSON.parse(stored) : {};
    return Object.entries(parsed).reduce((acc, [pageId, highlights]) => {
      acc[pageId] = (Array.isArray(highlights) ? highlights : []).map(highlight =>
        withNotes({
          ...highlight,
          notes: (highlight.notes || []).map(mapApiNote).filter(Boolean),
        }),
      );
      return acc;
    }, {});
  } catch (e) {
    return {};
  }
}

function saveHighlightsToStorage(view, highlights) {
  try {
    localStorage.setItem(HIGHLIGHTS_STORAGE_KEY(view), JSON.stringify(highlights));
  } catch (e) {
    console.error('Failed to save highlights to localStorage:', e);
  }
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* non-blocking */
  }
}

// The keys that hold an annotation's content in the clear. For an enrolled
// member none of them may be written: the read path opens envelopes at the
// seam, so what the store holds is plaintext, and the encryption spec (section
// 6.7, decision 28; #1548) moves the read cache to IndexedDB as envelopes.
// Until that wiring lands, `encryptedMember` (set by the plaintext-cache
// guard, courses-encryption/plaintext-caches.js) turns every writer below into
// a no-op and sweeps what these keys held from before enrolment. Preferences
// (`next-steps-view`) and the corpus registry are not content and stay.
export const PLAINTEXT_CACHE_PREFIXES = Object.freeze([
  'next-steps-highlights:',
  'next-steps-all-notes:',
  'next-steps-all-highlights:',
  TAGS_STORAGE_KEY,
  'next-steps-answers',
]);

/** Remove every plaintext cache key; returns the keys removed. */
export function sweepPlaintextCaches() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && PLAINTEXT_CACHE_PREFIXES.some(prefix => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach(key => localStorage.removeItem(key));
    return keys;
  } catch (e) {
    return [];
  }
}

/** A cache write that an enrolled member never makes. */
function persistJson(state, key, value) {
  if (state.encryptedMember) return;
  writeJson(key, value);
}

// Reviver: cached notes persist createdAt as ISO strings; revive to Date on load.
function reviveAllNotes(raw) {
  return (Array.isArray(raw) ? raw : []).map(mapApiNote).filter(Boolean);
}

// ── Color mapping (hex ↔ API enum) ─────────────────────────────────────────
// Both directions derive from the shared palette so the swatches the reader
// taps and the enum the API stores can never drift apart.
function hexToEnum(hex) {
  return HEX_TO_COLOR_ENUM[hex] || DEFAULT_HIGHLIGHT_ENUM;
}
function enumToHex(e) {
  return COLOR_ENUM_TO_HEX[e] || DEFAULT_HIGHLIGHT_COLOR;
}

// ── Note text extraction (rich text → plain text) ─────────────────────────
function extractNoteText(note) {
  if (!note) return { text: '' };
  const body = normalizeNoteBody(note.body ?? note.content ?? note.text ?? '');
  return { text: extractNotePlainText(body) };
}

// Normalize and sanitize the note body before it enters optimistic state or the
// API payload. Legacy strings still become one-paragraph rich docs.
function buildNoteBody(content) {
  return normalizeNoteBody(content);
}

// Map an API note (with embedded tags) to the shape the in-lesson drawer
// consumes. The drawer reuses RichTextEditor (HTML/text `content`) and
// TagBadge/TagManager (tag-id arrays). The contract embeds tags as
// `[{ tag: { id, name, color, type } }]`; we surface both the flat id list
// (`tags`) and the embedded objects (`tagObjects`) so display never needs a
// second lookup.
function mapApiNote(apiNote) {
  if (!apiNote) return null;
  const body = normalizeNoteBody(apiNote.body ?? apiNote.content ?? apiNote.text ?? '');
  const { text } = extractNoteText({ ...apiNote, body });
  const tagObjects = (apiNote.tags || [])
    .filter(Boolean)
    .map(t => t.tag || t)
    .map(t => ({ id: t.id, name: t.name, color: t.color, type: t.type }));
  const mapped = {
    id: apiNote.id,
    body,
    text,
    tags: tagObjects.map(t => t.id),
    tagObjects,
  };
  // Cross-lesson (Notes Hub) consumers also need the note's lesson + highlight
  // linkage, a `content` alias (the hub renders it as plain text), and a
  // revived createdAt. These are present on the /api/notes payload but not on
  // the embedded highlight.notes[] payload, so guard each field.
  if (apiNote.lessonSlug !== undefined) mapped.lessonSlug = apiNote.lessonSlug;
  // The hub searches titles locally; this client never sets one, but the API
  // contract carries it, so a title written elsewhere is still findable.
  if (apiNote.title !== undefined) mapped.title = apiNote.title;
  // Carried so the hub can scope notes to one Next Steps part client-side too —
  // the server also scopes GET /notes by program, this holds before that lands
  // and against the cross-program read-cache. Absent on embedded highlight.notes[].
  if (apiNote.programSlug !== undefined) mapped.programSlug = apiNote.programSlug;
  if (apiNote.highlightId !== undefined) mapped.highlightId = apiNote.highlightId || null;
  // Set by the read path on a row this device could not open (see
  // courses-annotations-read-path.js); the body above is empty for such a row and
  // the reason is what the Hub renders in its place.
  if (apiNote.unreadable) mapped.unreadable = apiNote.unreadable;
  mapped.content = text;
  mapped.createdAt = apiNote.createdAt ? new Date(apiNote.createdAt) : new Date();
  return mapped;
}

// A highlight's notes, normalised to an array.
//
// This used to also maintain `note` and `noteId` scalars mirroring notes[0], from
// when a highlight could carry exactly one note. Every consumer moved to notes[]
// when the multi-note drawer landed, but the scalars stayed — written at eleven call
// sites, mapped into parsedHighlights, and threaded through the `highlight-tapped`
// payload, for nobody: handleHighlightTapped reads `stored.notes` and ignores both,
// and the "NotesHub shadow-note derivation" the old comment cited no longer exists.
// A second representation of the same data with no reader is a standing invitation
// for the two to disagree and for the wrong one to be believed.
function withNotes(highlight) {
  return { ...highlight, notes: highlight.notes || [] };
}

// Locate a single note across the page-keyed highlights map. Used by the
// optimistic note actions to snapshot prior state for rollback.
function findHighlightNote(state, highlightId, noteId) {
  for (const highlights of Object.values(state.highlights || {})) {
    const hl = highlights.find(h => h.id === highlightId);
    if (hl) return (hl.notes || []).find(n => n.id === noteId) || null;
  }
  return null;
}

// A note without one tag, in both shapes the caches hold it in. Returns the note
// itself when it did not carry the tag, so the callers below can tell.
function stripNoteTag(note, tagId) {
  if (!(note.tags || []).includes(tagId) && !(note.tagObjects || []).some(t => t.id === tagId)) {
    return note;
  }
  return {
    ...note,
    tags: (note.tags || []).filter(id => id !== tagId),
    tagObjects: (note.tagObjects || []).filter(t => t.id !== tagId),
  };
}

// An array with `fn` applied, or the ORIGINAL array when nothing changed.
const mapKeepingIdentity = (rows, fn) => {
  const next = (rows || []).map(fn);
  return next.some((row, i) => row !== rows[i]) ? next : rows;
};

// The page-keyed reader cache with `fn` applied to every note on every highlight.
// Every level keeps its identity when nothing under it changed: the whole cache
// is walked on each tag delete, and rebuilding pages that hold no such note
// would invalidate every computed and v-for over them for nothing.
function mapHighlightNotes(highlights, fn) {
  let changed = false;
  const next = Object.entries(highlights || {}).reduce((acc, [pageId, rows]) => {
    acc[pageId] = mapKeepingIdentity(rows, hl => {
      const notes = mapKeepingIdentity(hl.notes, fn);
      return notes === hl.notes ? hl : { ...hl, notes };
    });
    changed = changed || acc[pageId] !== rows;
    return acc;
  }, {});
  return changed ? next : highlights;
}

// The stored row for a highlight, wherever it is filed. Looked up by id rather
// than by the caller's `pageId` because a healed mark can have moved bucket
// since the caller last read it, and the row is what a PATCH re-states the
// unchanged half of its content from (see saveHighlight).
/**
 * The id a note is CURRENTLY stored under. A drawer or hub that was synced while
 * POST /notes was in flight still names the note by its optimistic temp id; once
 * the server row replaced it, that name resolves here instead of 404ing.
 */
function resolveNoteId(state, noteId) {
  return (state.noteIdAliases || {})[noteId] ?? noteId;
}

const sameNoteBody = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function findStoredHighlight(state, highlightId) {
  for (const highlights of Object.values(state.highlights || {})) {
    const hl = highlights.find(h => h.id === highlightId);
    if (hl) return hl;
  }
  return null;
}

// What the annotations transport needs from the rest of the store: the bearer
// token every request carries, and the member id its hooks will bind an envelope
// to once encryption lands (courses-annotations-transport.js). The id is the
// profile's; a store with no profile module — most unit specs — sends null, which
// the identity hooks ignore.
/**
 * Item 1f: a fetched row must not overwrite one the member is still typing.
 *
 * Rule 1 keeps the store level with the field on every keystroke, so the only
 * thing that can clobber an unsent edit is a READ landing with older server
 * content — the Hub refetching on `ionViewWillEnter`, or a lesson's answers
 * arriving after the member has started filling a blank. The engine is the one
 * that knows which rows have an edit in hand, so it is the one asked.
 *
 * Narrow on purpose. It keeps the LOCAL row wholesale rather than merging
 * fields: a note's body and its plain text are derived from each other, and a
 * half-merged row would show one member's words under another's search index.
 * Anything with nothing pending is taken from the server as before.
 *
 * The SECOND source of "pending" is the outbox: a body an earlier session
 * sealed to disk and this one has not managed to send yet. There is no local
 * row to keep for those — an enrolled member's `allNotes` is empty at launch —
 * so the opened body (`pendingOverlay`) is laid over the server's row instead.
 * Body, text and content move together, so the derived-fields worry above does
 * not apply.
 *
 * @param {Array} incoming rows from a fetch
 * @param {Array} current what the store holds
 * @param {(row: any) => string|null} keyOf the row's autosave key
 */
function preservePending(incoming, current, keyOf) {
  if (!Array.isArray(incoming) || !incoming.length) return incoming;
  const held = new Map();
  (current || []).forEach(row => {
    const key = keyOf(row);
    if (key && autosave.hasPending(key)) held.set(key, row);
  });
  return incoming.map(row => {
    const key = keyOf(row);
    if (!key) return row;
    if (held.has(key)) return held.get(key);
    const overlay = pendingOverlay.patchFor(key);
    return overlay ? { ...row, ...overlay } : row;
  });
}

/**
 * POST a note the store already holds under `tempId`, then swap the server's row
 * in. The one path every Hub create takes — the dialog's, a card's new note on
 * its first keystroke, and the retry when a failed one's editor closes — so all
 * of them send the same body and re-key the same way.
 *
 * Rethrows; `createNoteRow` decides what a failure means for the temp row.
 */
async function postStandaloneNote(
  { commit, dispatch, state, rootState },
  { tempId, body, lessonSlug, highlightId, tagIds, program },
) {
  let writtenRow = null;
  const auth = annotationAuth(rootState);
  const data = await annotationsTransport.createNote(
    auth,
    {
      // The part the note belongs to. Passed in by the caller (the hub's current
      // part, or the reader's lesson program) because the note's part must match
      // its lesson's — `state.currentProgramSlug` is a stale fallback (only the
      // reader/landing set it; the hub can show a different part).
      programSlug: program || state.currentProgramSlug || 'next-steps-p1',
      lessonSlug,
      body,
      highlightId: highlightId || undefined,
      tagIds: tagIds && tagIds.length ? tagIds : undefined,
      // A standalone hub note has no highlight to inherit from, so the active
      // toggle is the only record of which guide it belongs to.
      view: state.view,
    },
    { onRawRow: row => (writtenRow = row) },
  );
  const current = state.allNotes.find(n => n.id === tempId);
  // THE ROW WENT WHILE THE POST WAS OUT. A new note is a store row from the tap
  // of +, so its bin is live before the server has heard of it: the member
  // deleted it (or the highlight it hangs off) and the local removal was all
  // that could happen then. The note now exists on the server with nobody
  // holding it, and would come back on the next fetch. Take it down. Nothing
  // else removes a `pendingCreate` row — `setAllNotes` keeps them on purpose.
  if (!current) {
    await annotationsTransport.deleteNote(auth, data.id).catch(() => {});
    return null;
  }
  const mapped = mapApiNote({
    ...data,
    body: data.body || body,
    lessonSlug,
    highlightId,
  }) || { ...current, id: data.id };
  // The member may have kept typing while the POST was out; that body wins, and
  // it goes into the swap itself rather than a second pass over `allNotes`.
  const typed = current.body;
  const caughtUp = typed !== undefined && !sameNoteBody(typed, body);
  const typedText = caughtUp ? extractNotePlainText(typed) : null;
  commit('replaceNoteInAll', {
    noteId: tempId,
    note: {
      ...mapped,
      ...(caughtUp ? { body: typed, text: typedText, content: typedText } : {}),
      pendingCreate: undefined,
      createFailed: undefined,
    },
  });
  // Queued, not PATCHed directly — see `queueNoteBody`.
  if (caughtUp) queueNoteBody(rootState, mapped.id, typed, { standalone: true });
  // Not awaited: the note is written and on screen. This only stops the corpus
  // being one reconcile behind, which would show as a just-written note missing
  // for a moment on the next Hub open.
  dispatch('syncNoteToCorpus', { id: data.id, program, row: writtenRow }).catch(() => {});
  return mapped;
}

/**
 * Creates on the wire right now, by temp id. One request per row at a time: a
 * second trigger while the POST is out — a keystroke, the editor closing — gets
 * the same promise, and `postStandaloneNote`'s catch-up carries what was typed
 * meanwhile. Module-level like the engine's slots, because it is about requests
 * in the air, not about what is on screen.
 */
const noteCreatesInFlight = new Map();
// The same idea for the two writes a member retaps on a slow network (#1814):
// a completion per lesson, a highlight save per selection (or per mark, for a
// recolour). See `updateProgress` and `saveHighlight` for the rules.
const progressWritesInFlight = new Map(); // lessonId -> { isCompleted, promise }
const highlightWritesInFlight = new Map(); // key -> promise

/**
 * Send a `pendingCreate` row to the server, and settle it whichever way the
 * request goes.
 *
 * Creates are direct, not queued (plan 9.6): nothing replays one on a timer or
 * across an app kill. So a failure that CAN be retried — no network, a 5xx —
 * keeps the row where it is, flagged `createFailed` with the same "not saved"
 * line every other row gets, and the next attempt is when the member closes
 * the editor (`settleNoteEditorClosed`), not the next keystroke: offline that
 * would be a failed request per keystroke. A verdict from the server (400, 404
 * on the highlight) removes the row; nothing later can make it acceptable.
 *
 * `retainOnFailure: false` is the dialog's and the reader's shape — a create
 * the member is not typing into, whose failure they are told about at once.
 *
 * @returns {Promise<object|null>} the server's row, or null when the row was
 *   gone by the time the POST landed
 */
function createNoteRow(ctx, tempId, { retainOnFailure }) {
  const { commit, state } = ctx;
  const running = noteCreatesInFlight.get(tempId);
  if (running) return running;
  const row = state.allNotes.find(n => n.id === tempId);
  if (!row) return Promise.resolve(null);
  const run = (async () => {
    try {
      const mapped = await postStandaloneNote(ctx, {
        tempId,
        body: row.body,
        lessonSlug: row.lessonSlug,
        highlightId: row.highlightId || null,
        tagIds: row.tags && row.tags.length ? row.tags : undefined,
        program: row.programSlug,
      });
      commit('courses-autosave/clearFailed', noteKey(tempId), { root: true });
      return mapped;
    } catch (error) {
      if (retainOnFailure && isRetryable(classify(error))) {
        commit('updateNoteInAll', { noteId: tempId, patch: { createFailed: true } });
        commit('courses-autosave/setFailed', { key: noteKey(tempId), error }, { root: true });
      } else {
        commit('removeNoteFromAll', tempId);
        commit('courses-autosave/clearFailed', noteKey(tempId), { root: true });
      }
      commit('setError', error.response?.data?.message || error.message || 'Failed to add note');
      throw error;
    } finally {
      noteCreatesInFlight.delete(tempId);
    }
  })();
  noteCreatesInFlight.set(tempId, run);
  return run;
}

/**
 * Every note autosave goes through here: a keystroke on a Hub note, a keystroke
 * on a drawer note, and the body a member typed while a note's create was in
 * flight. Rule 1: the caller has already committed to Vuex; only the request is
 * deferred. Rule 2: no rollback on failure — the engine reports it instead
 * (`onAutosaveFailure`).
 *
 * The catch-up after a create used to be a direct PATCH, and it raced the slot
 * the member's next keystroke (or a Done tick) opened under the new id: two
 * PATCHes naming the same revision, and the loser a 409 that decision 9.2 turns
 * into a "Recovered" duplicate the member never edited twice. Through the
 * engine it is one request per row at a time, like every other keystroke.
 */
function queueNoteBody(rootState, noteId, body, meta) {
  autosave.input(
    AUTOSAVE_TYPE.NOTE,
    noteId,
    { body },
    { member: rootState.profile?.id ?? null, meta },
  );
}

function annotationAuth(rootState) {
  return { token: rootState.user.token, memberId: rootState.profile?.id ?? null };
}

// The per-row record the recordUnreadableAnnotations mutation takes: which rows
// of one fetch this device could not open, and which it could, so a row that
// opens on a later load drops out. `idOf` names the row for a type whose rows
// are not keyed by `id` (a blank answer is keyed by its block).
function unreadableEntries(rows, idOf = row => row.id) {
  if (!Array.isArray(rows)) return [];
  return rows.map(row => ({ id: idOf(row), unreadable: row.unreadable || null }));
}

// Record one fetch's rows of one type. A fetch is the whole of what the screen
// shows for that type, so it replaces the type's record — a row that opened on
// this load, or a lesson the reader has left, drops out — except a page appended
// to a list already on screen, which adds to it.
function recordUnreadable(commit, type, rows, { idOf, append = false } = {}) {
  commit('recordUnreadableAnnotations', { type, entries: unreadableEntries(rows, idOf), append });
}

// A rich-text run, as it arrives inside a paragraph, a leader's note, an
// accordion, a quote or a blank's prompt: text carrying marks, a nested list, or
// an input where a fill-in-the-blank goes. Anything with an `items` array is a
// list, an explicit `type: 'blank'` is an input, and everything else is text.
function normalizeRichTextItem(seg) {
  if (seg?.type === 'blank') {
    // An input inside a run. `id` is optional: a blank block's inputs are ordered
    // by position and read their answer out of the block's own pipe-joined string,
    // where a paragraph's inline blank keys off its id. Both shapes reach the
    // tokeniser, which decides by which block it is processing.
    return { type: 'blank', id: seg.id, length: Number(seg.length) || 0 };
  }
  if (Array.isArray(seg?.items)) {
    return {
      type: 'list',
      ordered: !!seg.ordered,
      // Recurses, so marks inside a bullet arrive the same way marks in prose do —
      // and so does a list inside a list item, to any depth: this function is what
      // normalizeRichText maps over, so a nested list comes back through here.
      // Both halves of that now exist — buildProcessedContent numbers the words
      // inside a nested list and ListRun draws it — so the recursion is load
      // bearing rather than a shape nothing consumes.
      items: seg.items.map(item => ({
        inline: normalizeRichText(
          typeof item === 'string' ? item : (item?.inline ?? item?.richText ?? item?.text),
        ),
      })),
    };
  }
  return {
    type: 'text',
    text: typeof seg === 'string' ? seg : seg?.text || '',
    marks: Array.isArray(seg?.marks) ? seg.marks : [],
  };
}

// Normalise a block's rich text into the inline-segment shape the renderer
// consumes. The API sends structured rich text as an array of { text, marks? }
// segments, optionally with { ordered, items } entries for a nested list —
// delivered either directly (b.inline / richText = [...]) or wrapped under an
// `inline` key (richText = { inline: [...] }). Older/string payloads fall back to
// the Markdown-stripping path so legacy lessons still render cleanly. We accept
// all of these defensively because the exact envelope has shifted across backend
// revisions.
function normalizeRichText(value) {
  const segments = Array.isArray(value)
    ? value
    : Array.isArray(value?.inline)
      ? value.inline
      : null;

  if (segments) {
    const mapped = segments
      .map(normalizeRichTextItem)
      // An empty text run is dropped; an empty list is too, since it would render
      // as a stray marker-less ul. A blank always survives — it is an input, not
      // copy, and dropping one would renumber every answer after it.
      .filter(seg => {
        if (seg.type === 'blank') return true;
        if (seg.type === 'list') return seg.items.length > 0;
        return seg.text.length > 0;
      });
    return mapped.length ? mapped : [{ type: 'text', text: '', marks: [] }];
  }

  if (typeof value === 'string') {
    return [{ type: 'text', text: stripInlineMarkup(value), marks: [] }];
  }
  return [{ type: 'text', text: '', marks: [] }];
}

function extractInlineText(inline) {
  if (typeof inline === 'string') return stripInlineMarkup(inline);
  if (!Array.isArray(inline)) return '';

  return inline
    .map(segment => {
      if (typeof segment === 'string') return stripInlineMarkup(segment);
      if (typeof segment?.text === 'string') return stripInlineMarkup(segment.text);
      if (Array.isArray(segment?.content)) return extractInlineText(segment.content);
      // A bulleted or numbered list, which any rich body may now hold. Its lines
      // are joined with newlines, because that is what they read as: without this
      // a list contributed nothing and a body made only of bullets flattened to an
      // empty string — an empty copy action, and an empty mirror for any older
      // build reading the plain field.
      if (Array.isArray(segment?.items)) {
        return segment.items.map(item => extractInlineText(item?.inline)).join('\n');
      }
      return '';
    })
    .join('')
    .trim();
}

// Inputs in a rich-text run, including those inside a nested list's bullets.
// Walked in the same document order the page tokeniser assigns `blankIndex` in, so
// the two cannot disagree about how many answers a block has.
function countBlanksInRun(inline) {
  return (inline || []).reduce((total, item) => {
    if (item.type === 'blank') return total + 1;
    if (item.type === 'list') {
      return total + (item.items || []).reduce((n, li) => n + countBlanksInRun(li.inline), 0);
    }
    return total;
  }, 0);
}

/**
 * A BLANK's rich-text body, or null when it has none worth using.
 *
 * `normalizeRichText` always returns at least one run — an empty text run when it
 * was given nothing — so its result can never be tested for emptiness by length.
 * A body counts as real if any item is an input, a list, or text with characters
 * in it; anything else means the block still wants its legacy `prompt`.
 */
function richBody(source) {
  if (!source) return null;
  const inline = normalizeRichText(source);
  const usable = inline.some(item => (item.type === 'text' ? item.text.length > 0 : true));
  return usable ? inline : null;
}

function richBlankBody(b) {
  return richBody(b.inline ?? b.richText);
}

/**
 * A field's rich body, or null when it has none worth preferring.
 *
 * The authoring shape is a rich array in `<field>Inline` beside a plain-text
 * mirror in `<field>`, which is what the admin tool writes (richField.js). The
 * mirror carries no markdown markers and exists so a build that knows nothing
 * about `<field>Inline` keeps rendering readable text — so the rich key is
 * PREFERRED, never required, and an absent or empty one falls back silently.
 */
function richFieldBody(b, field) {
  return richBody(b[`${field}Inline`]);
}

/**
 * A blank's answer key as a STRING, whatever shape it arrives in.
 *
 * The markup is deliberately kept. An answer key may be styled — `**faith**` — and
 * three readers each take what they need from it:
 *
 *   - the matcher strips the markup before comparing, so formatting never decides
 *     right or wrong (see normalizeBlankAnswer);
 *   - the settled field draws it as runs, so an answer authored bold reads back
 *     bold once it is accepted (see parseAnswerMarkup);
 *   - the page's word stream takes the plain reading, so a highlight quoting the
 *     sentence never contains a marker.
 *
 * What must NOT happen is a key arriving as an object: `String()` on one yields
 * "[object Object]", which nothing a member types can ever match. Hence the
 * flattening below.
 *
 * `|` is load-bearing and survives: it separates one blank's answer from the next,
 * positionally, and that ordering is what stored answers are keyed by.
 */
function answerKeyText(value) {
  if (typeof value === 'string') return value;
  // A runs-shaped key has nowhere to keep its marks once flattened into the
  // pipe-joined string, so it collapses to its words. Authors who want a styled
  // answer write the markup in the string instead.
  if (Array.isArray(value)) return extractInlineText(value);
  if (typeof value?.text === 'string') return value.text;
  if (Array.isArray(value?.inline)) return extractInlineText(value.inline);
  return '';
}

function extractVerseText(block) {
  return (
    stripInlineMarkup(block.verseText) ||
    extractInlineText(block.inline) ||
    stripInlineMarkup(block.text)
  );
}

// Field shape for a resource card (BANNER / VIDEO). Factored out so the public
// blocks and their leader-only twins (LEADER_BANNER / LEADER_VIDEO) map their
// fields from one source of truth — a resource card renders identically whoever
// it is authored for; only the accordion chrome around it differs.
function bannerFields(b) {
  return {
    badge: b.badge || '',
    // Each of these three is a plain-text mirror beside an optional rich body in
    // `<field>Inline`. ResourceCard prefers the rich one and falls back to the
    // string, so a card authored before the rich fields existed — or by a build
    // that never writes them — renders exactly as it did.
    title: b.title || '',
    titleInline: richFieldBody(b, 'title'),
    subtitle: b.subtitle || '',
    subtitleInline: richFieldBody(b, 'subtitle'),
    description: b.description || '',
    descriptionInline: richFieldBody(b, 'description'),
    // `thumbnail` is a string path (renamed from `qrImage`); tolerate the
    // legacy field and an object shape just in case.
    imageUrl:
      (typeof b.thumbnail === 'string' ? b.thumbnail : b.thumbnail?.url) ||
      b.qrImage ||
      b.imageUrl ||
      '',
    duration: b.duration || '',
    // External-resource banners carry the link as `externalUrl`; tap-to-watch
    // cards rely on this to open. Tolerate `href` too. For an INTERNAL_PAGE
    // banner this same field carries the bare in-app router path.
    url: b.url || b.externalUrl || b.href || '',
    // How the tap opens `url`: EXTERNAL_URL (OS hand-off) / IN_APP_BROWSER /
    // INTERNAL_PAGE. Left raw here and normalised at the point of use; absent on
    // older banners, which resolveBannerLink() defaults to EXTERNAL_URL.
    linkType: b.linkType || '',
    ctaText: b.ctaText || '',
    dataEventTracking: b.dataEventTracking || '',
  };
}

function videoFields(b) {
  // Sniff the URL this block actually resolves to, not just `videoUrl`. The
  // seeds write `videoUrl`; the internal-tools builder writes `url`, and only
  // `videoUrl` was ever tested — so every builder-authored YouTube video fell
  // through to `source: 'native'` and was handed to the HTML5 player, which
  // cannot play a youtube.com/watch page. An explicit `source` still wins.
  const url = b.videoUrl || b.url || '';
  return {
    source: b.source || (isYoutubeUrl(url) ? 'youtube' : 'native'),
    url,
    thumbnail: b.thumbnail || '',
    // Tolerate both `label` and `badge` so backend naming can vary (same
    // defensive style as the BANNER case above).
    label: b.label || b.badge || '',
    title: b.title || '',
    subtitle: b.subtitle || '',
    description: b.description || '',
    duration: b.duration || '',
  };
}

// Title/subtitle/stories shape for a Life Stories block. Shared by LIFE_STORIES
// and its optional (grey) twin LIFE_STORIES_OPTIONAL — same data, different
// audience cue — so both read from one source of truth. Story ids are normalised
// to strings and back-filled by index so the story route always has a stable key.
function lifeStoriesFields(b) {
  return {
    eyebrow: (b.eyebrow || '').trim(),
    title: b.title || '',
    titleInline: richFieldBody(b, 'title'),
    subtitle: b.subtitle || '',
    subtitleInline: richFieldBody(b, 'subtitle'),
    // Same allowlist as BLANK: the builder's H1/H2/H3 selector writes `level`
    // (and optional `bold`) onto the section title. Absent means the card's own
    // 16px semibold, not an invented heading tier.
    ...(Number.isFinite(Number(b.level)) ? { level: Number(b.level) } : null),
    ...(typeof b.bold === 'boolean' ? { bold: b.bold } : null),
    stories: (Array.isArray(b.stories) ? b.stories : []).map((s, i) => ({
      ...s,
      id: String(s?.id ?? `${b._uid}-${i}`),
      category: s?.category || 'Other',
      kind: s?.kind === 'video' ? 'video' : 'text',
      // Excerpt and full body are the same rich-field pair every other card
      // uses (`<field>Inline` + plain mirror). Mapped here so a nested flatten
      // or an allowlist cannot drop the marks the story page paints from.
      excerptInline: richFieldBody(s, 'excerpt'),
      bodyInline: richFieldBody(s, 'body'),
    })),
  };
}

// Trailing inline icon (the Team-of-3 mark), for the block types that can carry
// one. Only passed on when the seed holds an absolute URL — an unresolved
// design-relative path (`assets/team-of-3-icon.png`) would render as a broken
// image against the app's own origin.
//
// Shared by the paragraph and heading branches rather than written out in each:
// the mark belongs at the end of the phrase "Team of 3", and that phrase is a
// heading as often as it is a sentence (ONE-8284). Two copies of the URL guard
// is two places for one of them to be forgotten.
function inlineIconFields(b) {
  return {
    iconSrc: /^https?:\/\//.test(b.iconSrc || '') ? b.iconSrc : '',
    iconAlt: b.iconAlt || '',
  };
}

const NESTED_API_TYPES = new Set([
  'PARAGRAPH',
  'HEADING',
  'LIST',
  'QUOTE',
  'VERSE',
  'BLANK',
  'IMAGE',
  'BANNER',
  'VIDEO',
  'LIFE_STORIES',
  'LIFE_STORIES_OPTIONAL',
]);

function flattenStoredChild(child) {
  if (!child || typeof child !== 'object') return null;
  if (child.content && typeof child.content === 'object' && child.type) {
    return {
      _uid: child.id || child._uid,
      type: child.type,
      ...child.content,
    };
  }
  return { _uid: child.id || child._uid, ...child };
}

function nestedApiBlocks(blocks) {
  if (!Array.isArray(blocks) || !blocks.length) return undefined;
  const nested = blocks
    .map(flattenStoredChild)
    .filter(child => child && NESTED_API_TYPES.has(child.type))
    .map(normalizeApiBlock);
  return nested.length ? nested : undefined;
}

// ── API → frontend lesson normaliser ──────────────────────────────────────
// Translates backend block shapes (UPPERCASE types, DB field names) into the
// kebab-case shape LessonContentRenderer consumes. Unmapped types pass through
// unchanged and render as empty wrappers until renderers are added.
function normalizeApiBlock(b) {
  // `anchor` is the URL fragment a printed booklet QR addresses this section by
  // (`#additional-testimonies`). It sits in `base` rather than in the HEADING
  // branch because a QR may address ANY block — a life-stories card on a page
  // with no heading above it has nothing else to aim at. pageNumberForAnchor and
  // LessonContentRenderer's wrapper id are both type-agnostic, so the branch was
  // the only thing making anchors heading-only, and it did it silently: this
  // normaliser is an allowlist, so an anchor on a BANNER reached the client from
  // the API and was dropped here, one layer before anything could use it.
  // Spread conditionally rather than defaulted to '': almost no block is
  // anchored, and a key meaning "no anchor" sitting on all ~1,600 of them is the
  // thing `setOptionalField` exists to avoid in the admin tool. Readers all test
  // it for truthiness (sectionAnchorId, pageNumberForAnchor), so absent and ''
  // behave identically — absent is just cheaper and diffs cleaner.
  const base = { id: b._uid, _uid: b._uid, ...(b.anchor ? { anchor: b.anchor } : {}) };
  switch (b.type) {
    case 'PARAGRAPH':
      return {
        ...base,
        type: 'paragraph',
        inline: normalizeRichText(b.inline ?? b.richText),
        ...inlineIconFields(b),
      };
    case 'HEADING':
      // `level` drives which h-tag renders (2 = section, 3 = sub-section). The
      // design source authors it explicitly; older seeds omit it and fall back to 2.
      //
      return {
        ...base,
        type: 'heading',
        content: b.text || '',
        // A heading takes marks but not lists — see fieldRuns. The API field is
        // `text`, so its rich twin is `textInline`; `contentInline` is tolerated
        // because that is the name this block carries once normalised.
        inline: richBody(b.textInline ?? b.contentInline),
        level: b.level || 2,
        // A heading can carry the trailing mark too (ONE-8284). The Team-of-3 icon
        // belongs at the end of the phrase "Team of 3", which in both intros is the
        // heading — it had been hung on the paragraph underneath instead, because
        // this branch dropped the field and the renderer had nowhere to draw it.
        ...inlineIconFields(b),
      };
    // `inline` is the rich body and `content` the plain mirror of it. The mirror is
    // what the copy action pastes and what an older build renders, so it is kept
    // whichever shape arrives.
    //
    // The rich body must never be mirrored into `verseText`: that field wins
    // extractVerseText's `||` chain, so a plain copy written there would beat
    // `inline` and take the marks down with it.
    case 'VERSE':
      return {
        ...base,
        type: 'verse',
        reference: b.reference,
        content: extractVerseText(b),
        inline: richBody(b.inline ?? b.richText),
        version: b.version,
        externalUrl: b.externalUrl,
      };
    case 'SCRIPTURE_MEMORY_VERSE':
      return {
        ...base,
        type: 'scripture-memory-verse',
        reference: b.reference,
        content: extractVerseText(b),
        inline: richBody(b.inline ?? b.richText),
        version: b.version,
        externalUrl: b.externalUrl,
      };
    case 'IMAGE':
      // `diagram` names a vector the app can draw itself when no asset is hosted
      // yet. `images` is a set shown side by side at their authored relative widths
      // (the Leader's/Student's Guide covers, where the size difference is the point).
      return {
        ...base,
        type: 'image',
        src: b.imageUrl,
        alt: b.alt || '',
        diagram: b.diagram || '',
        // `downloadable` is the author's switch for the save-to-Photos button, set
        // per image in the admin tool; `downloadUrl` an optional other file for it
        // to save (else it saves the picture itself). Absent on every image
        // authored before the switch existed, which reads as false — inert.
        // Resolved in pages/courses/lesson-image-downloads.js.
        downloadable: Boolean(b.downloadable),
        downloadUrl: b.downloadUrl || '',
        images: Array.isArray(b.images)
          ? b.images.map(img => ({
              src: img.imageUrl || '',
              alt: img.alt || '',
              caption: img.caption || '',
              captionInline: richFieldBody(img, 'caption'),
              width: Number(img.width) || 0,
            }))
          : [],
      };
    case 'BLANK': {
      const richBody = richBlankBody(b);

      // Two authoring shapes, and the tokeniser picks by which one is present.
      //
      // `prompt` is the original: a plain string with `____` where each input goes.
      // It carries no marks, so a bolded clause or a bullet inside an exercise was
      // impossible — which is why 74 of them draw their own "•" glyph.
      //
      // `inline` is the rich-text shape: the same runs a paragraph carries, with
      // `{ type: 'blank' }` items where the inputs go. Inputs are still ordered by
      // position, which is the order the stored pipe-joined answer string is in, so
      // a prompt converted from a string to runs keeps every answer on the input it
      // was typed into. `prompt` is left in place when both are present — nothing
      // reads it then, but it is the readable form of the same sentence.
      return {
        ...base,
        type: 'blank',
        prompt: b.prompt || '',
        // Only when the rich body actually carries something. An EMPTY `inline`
        // array is a real transitional payload, and it used to be enough to switch
        // the tokeniser onto the rich path — which then never split the legacy
        // prompt, so the whole question and its inputs rendered as nothing at all.
        // Presence of the key is not the test; usable content is.
        ...(richBody ? { inline: richBody } : null),
        // Markup preserved — see answerKeyText. The matcher strips it before
        // comparing; the settled field draws it.
        correctAnswer: answerKeyText(b.correctAnswer),
        // The builder's H1/H2/H3 style selector writes `content.level` on any
        // prose block, including a blank. This normaliser is an allowlist, so
        // the field has to be named or an H3 blank silently renders as body
        // copy. Absent means Normal — do not invent a default the way HEADING
        // does, or every legacy blank would jump to the section tier.
        ...(Number.isFinite(Number(b.level)) ? { level: Number(b.level) } : null),
      };
    }

    case 'OPEN_RESPONSE':
      // The writing area the printed booklets put under a heading for the
      // disciple to fill in while the leader teaches. Unlike BLANK there is no
      // answer to check — it stores whatever they write. `lines` is how much
      // room the printed page gives it; OpenResponseBlock treats >= 9 as the
      // tall window, so a bad value cannot produce a field taller than that.
      return {
        ...base,
        type: 'open-response',
        placeholder: b.placeholder || '',
        lines: Number(b.lines) > 0 ? Number(b.lines) : 6,
      };
    case 'BANNER':
      return { ...base, type: 'banner', ...bannerFields(b) };
    // Leader-only resource cards. The card itself is a normal banner/video —
    // it renders through the same ResourceCard — so the block nests a ready-made
    // `resource` the renderer hands straight over; the `leader-*` type only tells
    // the renderer to wrap it in the "Leader's Resource" accordion. Server-side
    // audience gating (?view=leader) keeps these out of the student payload.
    case 'LEADER_BANNER':
      return {
        ...base,
        type: 'leader-banner',
        accordionLabel: (b.accordionLabel || '').trim(),
        resource: { type: 'banner', ...bannerFields(b) },
      };
    case 'LEADER_VIDEO':
      return {
        ...base,
        type: 'leader-video',
        accordionLabel: (b.accordionLabel || '').trim(),
        resource: { type: 'video', ...videoFields(b) },
      };
    case 'LEADER_NOTE': {
      const nested = nestedApiBlocks(b.blocks);
      return {
        ...base,
        type: 'leader-note',
        title: (b.title || '').trim(),
        inline: normalizeRichText(b.inline ?? b.richText),
        ...(nested ? { blocks: nested } : {}),
      };
    }
    case 'LIST':
      // Bullet or numbered list. One block type with an `ordered` flag rather
      // than two enum values: the renderer branches once, and switching a list
      // from bullets to numbers is then a content edit that leaves the block's id
      // — and so every highlight anchored to it — alone.
      //
      // Each item is the same inline-run shape a paragraph carries, so marks
      // inside a bullet work without any further plumbing. A string item is
      // tolerated because the seed's own authoring shape allows a bare line.
      return {
        ...base,
        type: 'list',
        ordered: !!b.ordered,
        items: (Array.isArray(b.items) ? b.items : []).map(item => ({
          inline: normalizeRichText(
            typeof item === 'string' ? item : (item?.inline ?? item?.richText ?? item?.text),
          ),
        })),
      };
    case 'ACCORDION': {
      // Single, self-contained collapsible (title + inline body) reusable
      // anywhere in lesson content. Visible to all audiences. The absence of
      // `items` tells the renderer to use the single-accordion path; legacy
      // multi-item accordion data (block.items) keeps rendering as before.
      // Optional `blocks` nest headings, lists, verses, images and cards inside.
      const nested = nestedApiBlocks(b.blocks);
      return {
        ...base,
        type: 'accordion',
        title: b.title || '',
        titleInline: richFieldBody(b, 'title'),
        inline: normalizeRichText(b.inline ?? b.richText),
        ...(nested ? { blocks: nested } : {}),
      };
    }
    case 'QUOTE':
      // Blockquote with a left rule (rendered italic + muted). The quoted line
      // carries an `italic` mark; the attribution run is plain. Same inline
      // shape as PARAGRAPH / LEADER_NOTE.
      return {
        ...base,
        type: 'quote',
        inline: normalizeRichText(b.inline ?? b.richText),
      };
    case 'VIDEO':
      return { ...base, type: 'video', ...videoFields(b) };
    case 'AUDIO': {
      // Same asymmetry the VIDEO case carried: the sniff read `audioUrl` while
      // the URL it emitted fell back to `url`, so a Spotify link authored in the
      // builder was played by the native <audio> element instead of the embed.
      const url = b.audioUrl || b.url || '';
      return {
        ...base,
        type: 'audio',
        // `spotify` -> inline Spotify embed; anything else -> native HTML5 audio.
        source: b.source || (/open\.spotify\.com/.test(url) ? 'spotify' : 'native'),
        url,
        thumbnail: b.thumbnail || '',
        // Tolerate both `label` and `badge` so backend naming can vary (same
        // defensive style as the VIDEO / BANNER cases above).
        label: b.label || b.badge || '',
        title: b.title || '',
        subtitle: b.subtitle || '',
        description: b.description || '',
        duration: b.duration || '',
      };
    }
    case 'LIFE_STORIES':
      // Leader-only resource: testimony stories grouped by demographic. Stories are
      // either text ({ excerpt, body } — opens the in-app story page) or video
      // ({ kind: 'video' } — plays inline). Ids are normalised to strings and
      // back-filled by index so the story route always has a stable key to look up.
      return { ...base, type: 'life-stories', ...lifeStoriesFields(b) };
    // Same resource as LIFE_STORIES, flagged optional: the renderer draws it on a
    // grey ground with a "(Optional)" eyebrow instead of the gold treatment. The
    // fields and story shapes are identical — only the audience cue differs.
    case 'LIFE_STORIES_OPTIONAL':
      return { ...base, type: 'life-stories-optional', ...lifeStoriesFields(b) };
    default:
      return { ...base, ...b };
  }
}

// The looping-video asset families that drive a card background. One definition,
// shared by the animation and the poster resolvers below so they can never drift.
const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

// Resolve the card background animation from the lesson's (animated) thumbnail.
// Only a looping video asset drives the card background; a static-image or empty
// thumbnail yields no animation, so the card shows its solid colour instead.
function resolveBackgroundAnimation(apiLesson) {
  const thumbnail = apiLesson.content?.thumbnail;
  return thumbnail && VIDEO_EXT.test(thumbnail) ? thumbnail : null;
}

// First-frame still for the card background, served next to the video with the
// same basename (…/NSP2L10.mp4 → …/NSP2L10.webp). The carousel shows it as a
// static <img> on side cards and as the <video> poster on the focused card.
// Null when there is no video background.
function resolveBackgroundPoster(apiLesson) {
  const animation = resolveBackgroundAnimation(apiLesson);
  return animation ? animation.replace(VIDEO_EXT, '.webp$2') : null;
}

function normalizeApiLesson(apiLesson) {
  const pages = (apiLesson.content?.pages || []).map((p, i) => ({
    id: p._uid,
    pageIndex: i,
    blocks: (p.blocks || []).map(normalizeApiBlock),
  }));
  // The Part 2 leader Cover Letter: the brief BEFORE the lesson, served outside
  // `pages` and outside totalPages (LessonsService.findOne). Null on every Part 1
  // lesson and in every student view — the API decides that, not the client.
  const coverLetter = apiLesson.coverLetter?.blocks?.length
    ? { blocks: apiLesson.coverLetter.blocks.map(normalizeApiBlock) }
    : null;
  return {
    slug: apiLesson.slug,
    id: apiLesson.slug,
    // Reader/carousel rows carry `content.title`; the lightweight /lessons/all
    // rows carry the CMS `title` column (used for guides + the no-lesson bucket).
    title: apiLesson.content?.title || apiLesson.title || apiLesson.name,
    lessonType: apiLesson.content?.lessonType,
    thumbnail: apiLesson.content?.thumbnail,
    // Card background animation, sourced from the lesson's (animated) thumbnail.
    // See resolveBackgroundAnimation — non-video/empty thumbnails render solid.
    backgroundAnimation: resolveBackgroundAnimation(apiLesson),
    // Its first-frame still (…/x.mp4 → …/x.webp). See resolveBackgroundPoster.
    backgroundPoster: resolveBackgroundPoster(apiLesson),
    programSlug: apiLesson.programSlug || 'next-steps-p1',
    program: apiLesson.program || null, // { slug, name }
    part: apiLesson.part || null, // intra-program section index
    // What the row represents (backend `LessonKind`):
    //   LESSON       — a real numbered lesson; counts toward progress, on the carousel.
    //   GUIDE/LEADER_GUIDE — an intro guide; shown on the carousel but not counted
    //                  (backend serves whichever matches the member's leader access).
    //   OTHER        — the `no-lesson` bucket; never on the carousel, note-tagging only.
    // Defaults to LESSON for any row the backend leaves unset.
    kind: apiLesson.kind || 'LESSON',
    // COURSE position (1..10), server-resolved. Next Steps is one ten-lesson course
    // split across two program rows, so this is NOT the lesson's position within its
    // program — see the lessonNumber getter. Null on guides, and absent on an API
    // that predates the field, which the getter falls back for.
    lessonNumber: Number.isFinite(apiLesson.lessonNumber) ? apiLesson.lessonNumber : null,
    totalPages: pages.length,
    // Set by fetchLesson, not here: the API answers with a BOOLEAN about the anchor
    // the request asked for, and a bare boolean on the lesson could be read against
    // a different anchor later. fetchLesson resolves it to the slug it actually
    // asked about, so anchorTarget can check the two match. Null on an API that
    // predates the field, which is the old behaviour exactly.
    leaderOnlyAnchor: null,
    // The anchor the last fetch of this lesson asked the server about, or null.
    // Set by fetchLesson beside leaderOnlyAnchor; declared here so a lesson from
    // any other path (the carousel, /lessons/all) carries the key explicitly
    // rather than reading as undefined.
    requestedAnchor: null,
    coverLetter,
    pages,
  };
}

// ── Word-index conversion helpers ──────────────────────────────────────────
// The offsets come from the page tokeniser (lesson-content-words.js), which is
// also what lays the words out on screen. Counting them a second time here is how
// the two drifted: see buildBlockOffsets for what that cost.

// Convert global page word indices → the block-scoped pair a highlight is stored
// as: the start block plus, when the span reaches further, the block it ends in.
// `endWordIndex` is relative to `endBlockId` whenever that is set, and
// `endBlockId` is null for a span contained in one block — the same contract the
// API's Highlight model documents.
//
// This used to clip the end to the START block's last word, which is why the drag
// knobs were clamped to one block: anything wider was silently truncated on save.
function globalToBlock(globalStart, globalEnd, blocks) {
  const offsets = buildBlockOffsets(blocks);
  // Blocks the tokeniser reached. A wordless block (image, banner, divider) owns
  // no index, so no span can start or end in one.
  const owning = (blocks || []).filter(block => (offsets[block.id]?.wordCount || 0) > 0);
  const blockAt = index =>
    owning.find(block => {
      const { offset, wordCount } = offsets[block.id];
      return index >= offset && index < offset + wordCount;
    }) || null;

  const startBlock = blockAt(globalStart);
  if (!startBlock) return null;

  const end = Math.max(globalEnd, globalStart);
  // An end past the last word on the page means the content shrank under a saved
  // highlight. Anchor it to the last block that has words rather than dropping the
  // highlight, which would lose the reader's notes with it.
  const endBlock = blockAt(end) || owning[owning.length - 1];
  const endInfo = offsets[endBlock.id];

  return {
    blockId: startBlock.id,
    endBlockId: endBlock.id === startBlock.id ? null : endBlock.id,
    startWordIndex: globalStart - offsets[startBlock.id].offset,
    endWordIndex: Math.min(end, endInfo.offset + endInfo.wordCount - 1) - endInfo.offset,
  };
}

// ── Re-anchoring a highlight onto re-authored content ───────────────────────
//
// `selectedText` is the authoritative anchor; the block id and word indices are
// hints that go stale the moment a lesson is edited or re-seeded (a reseed re-mints
// every block id). `contextBefore` / `contextAfter` exist to disambiguate a quote
// that appears more than once on a page — the schema has described them as exactly
// that since the columns were added, but nothing has ever written or read them, so
// the resilience they promise has not existed.
//
// Words either side of the span that get stored as context. Five is what the schema
// comment describes and is enough to separate repeats of a short quote.
const CONTEXT_WORDS = 5;

// Words are compared on their visible text, case- and punctuation-insensitively: a
// re-author that changes "Father." to "Father," must not lose the highlight, and the
// quote was built by joining these same word texts.
//
// Some tokens carry no letters or digits at all and normalise to nothing: a lone
// dash or bullet the tokeniser split off, and — reachable without any editing —
// the `___` an UNANSWERED fill-in-the-blank contributes. Those are dropped from
// both sides of a comparison, never from one: the quote already loses them (it is
// built by filtering the normalised words), so a page that keeps them shifts every
// slot after the gap and the sequence stops lining up. A highlight drawn across a
// blank — which Next Steps is full of — could never be re-anchored.
function normaliseWord(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

const wordSequence = text =>
  String(text || '')
    .split(/\s+/)
    .map(normaliseWord)
    .filter(Boolean);

// Every word on a page that can take part in a comparison, in document order, and
// where the page's blanks sit. Built through the same tokeniser that lays the page
// out, so each word keeps the globalIndex a highlight was saved against — the
// filtering changes what is compared, not what a match resolves to. Built at most
// once per page per load: the cache is keyed by page id, so a story's pseudo-page,
// minted afresh for every highlight on that story, still hits.
//
// A blank is tokenised with no answer, so its word is the placeholder. The set
// records where they are because a saved quote carries whatever the member typed
// there and cannot be compared across one (see spanReadsAsQuote).
function pageWords(page, wordCache) {
  const key = page?.id ?? page;
  if (!wordCache.has(key)) {
    const all = collectWords(buildProcessedContent(page?.blocks || [], new Map()));
    wordCache.set(key, {
      words: all.filter(word => normaliseWord(word.text)),
      blanks: new Set(
        all.filter(word => word.text === BLANK_PLACEHOLDER).map(word => word.globalIndex),
      ),
    });
  }
  return wordCache.get(key);
}

// The stored quote's neighbours, for a span that is being written.
function contextForSpan(blocks, globalStart, globalEnd) {
  const words = collectWords(buildProcessedContent(blocks || [], new Map()));
  const textBetween = (from, to) =>
    words
      .filter(word => word.globalIndex >= from && word.globalIndex <= to)
      .map(word => word.text)
      .join(' ')
      .trim();
  return {
    contextBefore: textBetween(globalStart - CONTEXT_WORDS, globalStart - 1) || undefined,
    contextAfter: textBetween(globalEnd + 1, globalEnd + CONTEXT_WORDS) || undefined,
  };
}

// Every place the quote still occurs, across the lesson's pages.
function findQuote(pages, quote, wordCache) {
  const needle = wordSequence(quote);
  if (!needle.length) return [];
  const hits = [];
  pages.forEach(page => {
    const { words } = pageWords(page, wordCache);
    for (let i = 0; i + needle.length <= words.length; i += 1) {
      const matches = needle.every(
        (token, offset) => normaliseWord(words[i + offset].text) === token,
      );
      if (matches) {
        hits.push({
          page,
          start: words[i].globalIndex,
          end: words[i + needle.length - 1].globalIndex,
        });
      }
    }
  });
  return hits;
}

// How much of the stored context still surrounds a candidate. Used only to choose
// BETWEEN candidates — never to accept one, since a quote that occurs once is
// already unambiguous whether or not its neighbours moved.
function contextScore(candidate, hl, wordCache) {
  const { words } = pageWords(candidate.page, wordCache);
  const at = index => words.find(word => word.globalIndex === index);
  const run = (from, to) => {
    const out = [];
    for (let i = from; i <= to; i += 1) {
      const word = at(i);
      if (word) out.push(normaliseWord(word.text));
    }
    return out;
  };
  const tail = (list, n) => list.slice(Math.max(0, list.length - n));
  const overlap = (a, b) => {
    let count = 0;
    for (let i = 0; i < Math.min(a.length, b.length); i += 1) if (a[i] === b[i]) count += 1;
    return count;
  };
  const before = wordSequence(hl.contextBefore);
  const after = wordSequence(hl.contextAfter);
  return (
    overlap(
      tail(run(candidate.start - CONTEXT_WORDS, candidate.start - 1), before.length),
      before,
    ) + overlap(run(candidate.end + 1, candidate.end + after.length), after)
  );
}

// Where a highlight's quote sits now, or null when that cannot be answered safely.
//
// An ambiguous quote is deliberately LEFT unanchored rather than guessed at: moving
// a highlight to the wrong sentence and then healing that guess into the row is
// worse than reporting that it could not be placed.
function reanchorHighlight(pages, hl, wordCache) {
  const hits = findQuote(pages, hl.selectedText, wordCache);
  if (hits.length === 1) return hits[0];
  if (hits.length < 2) return null;
  const scored = hits
    .map(hit => ({ hit, score: contextScore(hit, hl, wordCache) }))
    .sort((a, b) => b.score - a.score);
  if (scored[0].score === 0) return null;
  if (scored[0].score === scored[1].score) return null;
  return scored[0].hit;
}

// Whether a span decoded from the stored hints still reads as the stored quote.
//
// Hints that resolve can be as stale as hints that do not: an edit that keeps a
// block's id but adds or removes a word ahead of the span leaves blockId valid and
// every index pointing one word off, and the highlight lands on the wrong words
// with no error anywhere. Compared the way findQuote compares — on normalised
// words, with tokens that normalise to nothing dropped from both sides — so
// punctuation does not fail a span that is in place.
//
// Two spans are taken at their word without a comparison. A row with no quote has
// nothing to check against. And a span that crosses a blank cannot be checked: the
// page tokenises the blank as its placeholder, but the quote was built from the
// rendered page and carries whatever the member had typed there, so the two never
// read the same even when the highlight sits exactly where it was drawn.
function spanReadsAsQuote(page, span, quote, wordCache) {
  const wanted = wordSequence(quote);
  if (!wanted.length) return true;
  const { words, blanks } = pageWords(page, wordCache);
  const inSpan = index => index >= span.start && index <= span.end;
  if ([...blanks].some(inSpan)) return true;
  const covered = words
    .filter(word => inSpan(word.globalIndex))
    .map(word => normaliseWord(word.text));
  return covered.length === wanted.length && covered.every((token, i) => token === wanted[i]);
}

// The page a highlight belongs to.
//
// `pageIndex` is written once, at create, and no update path can correct it — it is
// not on UpdateHighlightDto. It is also VIEW-relative (the frontend indexes the
// pages the server returned for the active view), so anything that re-paginates a
// lesson — a content resync, splitting a section onto its own screen — leaves every
// existing highlight pointing at the wrong page, and `pages[pageIndex].blocks` then
// has no such block, so the highlight silently disappears.
//
// `blockId` does not have that problem: a block belongs to exactly one page, so it
// already identifies the page uniquely. Trust the index only when it agrees, and
// otherwise find the block. Same answer in the common case, no lost highlights when
// the pagination moves under them.
function pageForBlock(pages, pageIndex, blockId) {
  const hasBlock = page => (page?.blocks || []).some(block => blockTreeHasId(block, blockId));
  const indexed = pages[pageIndex];
  if (hasBlock(indexed)) return indexed;
  return pages.find(hasBlock) || null;
}

// True when this block, a nested child, or an inline blank owns `id`. Nested
// blanks live in a note/accordion's `blocks` list, not as their own lesson_block
// row, so a walk that only checks the page's top level would send their answers
// to pageId '_' and they would never come back on reload.
function blockTreeHasId(block, id) {
  if (!block || id === null || id === undefined) return false;
  if (block.id === id || block._uid === id) return true;
  if (Array.isArray(block.inline) && block.inline.some(item => item?.id === id)) return true;
  // A Life Story anchor names the story, not a lesson_block row — the story is a
  // member of the life-stories block's `stories[]`. Without this arm a story
  // highlight resolves to no page and disappears; see story-blocks.js for why the
  // anchor is minted client-side.
  if (isStoryAnchor(id) && isLifeStoriesBlock(block)) {
    const storyId = storyIdFromAnchor(id);
    if ((block.stories || []).some(story => String(story.id) === String(storyId))) return true;
  }
  return (block.blocks || []).some(child => blockTreeHasId(child, id));
}

// ── Anchor scopes ──────────────────────────────────────────────────────────
//
// Lesson prose is numbered across the page it renders on; a Life Story is
// numbered within itself, on the route it is read on. Both are saved against the
// lesson, so every anchor resolves to a page — the difference is only which block
// list the offsets are measured against, and which `state.highlights` bucket the
// result is filed under.

// A pseudo-page holding one story's blocks. `findQuote` and `pageWords` take
// pages, so wrapping the story lets the quote-based heal path work on a story
// exactly as it does on lesson content, with no second implementation.
function storyPseudoPage(scopeKey, story) {
  return { id: scopeKey, blocks: storyBlocks(story) };
}

// The scope of a mark on lesson prose: numbered across the page it renders on,
// searched across every page, filed under the page id.
function lessonScope(pages, page) {
  return { page, scopeKey: page.id, blocks: page.blocks, quotePages: pages, wordsPage: page };
}

// The page index a placed highlight now belongs to. A resolved scope names the
// LESSON page it sits on — a story's scope names the page carrying the story,
// which is what scopeForNewHighlight stores too — and a row whose scope did not
// resolve keeps the index it came with.
function pageIndexOf(pages, scope, row) {
  const index = (pages || []).findIndex(page => page.id === scope?.page?.id);
  return index >= 0 ? index : (row?.pageIndex ?? 0);
}

// The scope a STORED highlight decodes in, resolved from the anchor the API
// returned. Null when nothing in the lesson answers to that anchor any more.
function scopeForStoredHighlight(pages, hl) {
  if (isStoryAnchor(hl.blockId)) {
    const found = findStoryInPages(pages, storyIdFromAnchor(hl.blockId));
    if (!found) return null;
    const scopeKey = storyScopeKey(found.page?.id, found.story.id);
    const storyPage = storyPseudoPage(scopeKey, found.story);
    return {
      page: found.page,
      scopeKey,
      blocks: storyBlocks(found.story),
      quotePages: [storyPage],
      // The page whose word list a decoded span is read against: the story's own
      // pseudo-page, numbered the way the story's offsets are.
      wordsPage: storyPage,
    };
  }
  const page = pageForBlock(pages, hl.pageIndex, hl.blockId);
  if (!page) return null;
  return lessonScope(pages, page);
}

// Where a stored highlight goes on this lesson, and how it got there.
//
// The hints — block id and word indices — are tried first, and they can be stale
// two ways: they resolve to nothing (the block is gone), or they resolve to the
// wrong words (a word added or removed ahead of the span in a block that kept its
// id puts every index one off, and nothing errors). Either way the quote decides,
// which is what the schema calls the authoritative anchor.
//
// A story searches its OWN pseudo-page and a lesson mark searches the lesson's
// pages: a story's quote does not appear in the page the reader sees, so searching
// the lesson for it would find nothing and searching the story for a lesson quote
// would be equally blind. When the anchor resolved to nothing at all there is no
// story to search, so the lesson pages are the only thing left to try.
//
// Returns the scope and the global span to file under (either null when it cannot
// be placed); whether a quote search moved it (`healed`: the caller writes the
// corrected hints back so the next load is an exact hit); and whether it is shown
// where the hints point without the quote agreeing (`unverified`: the passage was
// rewritten and the quote is nowhere on the lesson). That last one is kept rather
// than hidden because hiding it would hide the notes hanging off it, and the saved
// place is the best information there is.
function placeStoredHighlight(pages, hl, wordCache) {
  let scope = scopeForStoredHighlight(pages, hl);
  const hinted = scope
    ? blockToGlobal(
        hl.blockId,
        hl.startWordIndex,
        hl.endWordIndex,
        scope.blocks,
        // Absent on every highlight saved before spans could cross a block, and
        // on every highlight that still doesn't — both mean "ends in blockId".
        hl.endBlockId || null,
      )
    : null;
  if (hinted && spanReadsAsQuote(scope.wordsPage, hinted, hl.selectedText, wordCache)) {
    return { scope, global: hinted, healed: false, unverified: false };
  }

  const found = reanchorHighlight(scope?.quotePages || pages, hl, wordCache);
  if (!found) return { scope, global: hinted, healed: false, unverified: Boolean(hinted) };
  // A lesson mark FOLLOWS its heal: the quote can turn up on a different page than
  // the stale anchor named, and the bucket has to move with it. A story keeps the
  // scope it already resolved — `found.page` there is the story's pseudo-page,
  // which is not a lesson page and must never be filed as one. A story anchor that
  // resolved to nothing has no story to keep, so it takes the lesson page its
  // quote was found on.
  if (!scope || !isStoryAnchor(hl.blockId)) scope = lessonScope(pages, found.page);
  return { scope, global: { start: found.start, end: found.end }, healed: true, unverified: false };
}

// The scope a highlight being WRITTEN belongs to. A story-scoped caller passes
// `storyId`; everything else keeps passing a 1-based `pageNumber`, unchanged.
function scopeForNewHighlight(state, highlight) {
  const pages = state.currentLesson?.pages || [];
  if (highlight.storyId) {
    const found = findStoryInPages(pages, highlight.storyId);
    if (!found) return null;
    return {
      page: found.page,
      // The page the story's block sits on — which is what makes this a highlight
      // on the LESSON rather than on a document of its own.
      pageIndex: found.pageIndex,
      scopeKey: storyScopeKey(found.page?.id, found.story.id),
      blocks: storyBlocks(found.story),
    };
  }
  const pageIndex = highlight.pageNumber ? highlight.pageNumber - 1 : 0;
  const page = pages[pageIndex];
  return { page, pageIndex, scopeKey: page?.id, blocks: page?.blocks || [] };
}

// Page a fill-in or open-response answer belongs to. Open-response is a whole
// block (`b.id`); inline blanks live inside a paragraph/blank run (`b.inline`);
// nested blanks live in a container's `blocks`. `inline` is only walked when it
// is an array — a string/object body would throw and abort the upsert before the
// PUT, which is how an open-response could look saved locally and then vanish
// after an app delete.
function pageIdForAnswerBlock(lesson, blockId) {
  const page = lesson?.pages?.find(p =>
    (p.blocks || []).some(block => blockTreeHasId(block, blockId)),
  );
  return page?.id || '_';
}

function blankAnswerProgramSlug(state) {
  return state.currentLesson?.programSlug || state.currentProgramSlug || 'next-steps-p1';
}

/**
 * The autosave queue key for a blank on the lesson in view — the same composite
 * `upsertBlankAnswer` queues under, so a component can ask the status module
 * about its own block. One derivation, or the fetch guard and the "not saved"
 * line would each have their own idea of which row they were talking about.
 */
function blankAutosaveKeyFor(state, blockId) {
  return blankKey(
    annotationsTransport.blankAnswerItemId({
      programSlug: blankAnswerProgramSlug(state),
      lessonSlug: state.currentLesson?.id ?? state.lessonSlug,
      blockId,
    }),
  );
}

// Convert block-scoped indices → global page word indices. `endBlockId` is null
// for the single-block case, which is every highlight saved before the column
// existed — so a null resolves the end against the start block, exactly as before.
function blockToGlobal(blockId, blockStart, blockEnd, blocks, endBlockId = null) {
  const offsets = buildBlockOffsets(blocks);
  const startInfo = offsets[blockId];
  if (!startInfo) return null;
  // A named end block that is no longer in the content (the page was re-authored)
  // falls back to the start block, so the highlight narrows rather than vanishing.
  const endInfo = (endBlockId ? offsets[endBlockId] : null) || startInfo;
  const start = startInfo.offset + blockStart;
  return { start, end: Math.max(start, endInfo.offset + blockEnd) };
}

// ── All-page icon cache invalidation ───────────────────────────────────────
// The All page's icon feed is filtered per-member on the server, and course
// entitlements are part of that filter: a program the member owns shows its
// entry button even when their pastoral status alone would hide it. The feed is
// cached for the session, so the moment program visibility changes — a book code
// redeemed, a voucher used, a purchase confirmed, a class QR scanned — that
// cache is stale and missing the button just earned.

// Which programs the member can currently see, as a stable string. null when no
// summary has loaded yet — deliberately never equal to a loaded one, because the
// first load of a session can itself be the one that follows a grant (redeeming
// a voucher never opens a Courses page, so `access` is still null at that point).
// One `/courses/access` request at a time, and an answer that is good for a
// moment after it lands (#1812). Two things this de-duplicates on a lesson open:
//
//  - Callers that OVERLAP: `checkLeaderAccess` fires alongside the lesson gate and
//    fetches the summary itself when the store holds none (a cold start straight
//    into a lesson). The second joins the first's request instead of making its
//    own.
//  - Callers in SEQUENCE: the pastoral guard resolves in `beforeEnter`, before the
//    page exists, and the gate asks again ~100 ms later for an answer the store
//    already holds. A caller that passes `maxAgeMs` accepts an answer that landed
//    within that window; nothing can have changed in it.
//
// Fail-closed is intact: a failed fetch clears the stamp, so a failure is never
// fresh. The default (no `maxAgeMs`) always fetches, so every existing caller —
// the guard, purchase confirmation, voucher redemption, unlock — is unchanged.
// A monotonic clock, so a device clock jump cannot make a stale answer fresh.
// The window itself is ACCESS_FRESH_MS in @/constants/courses.
let accessInFlight = null;
const monotonicNow = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

function visibleProgramSignature(access) {
  if (!access) return null;
  return (access.programs || [])
    .filter(p => p?.visible)
    .map(p => p.slug)
    .sort()
    .join(',');
}

// Both feeds are filtered per member on the server and both can carry a course
// entry point, so a change in program visibility stales both at once. The All
// page re-fetches on its next view; the homepage's featured cards are only
// fetched at boot, so they carry a flag its view checks instead.
function invalidateEntitlementFeedsIfChanged(commit, previousSignature, access) {
  if (visibleProgramSignature(access) === previousSignature) return;
  commit('allpage/invalidateAllPageIcons', null, { root: true });
  commit('homepage/markFeaturedCardsStale', null, { root: true });
}

// ── Initial state ──────────────────────────────────────────────────────────
// Everything a member's session puts in this module, fresh. A factory rather
// than a constant: `reset` hands these out on logout (#1506), and the mutations
// write into `userAnswers` and the other maps in place, so one shared object
// would carry the member in between back to the one after. The audience toggle
// IS here: logout's sweep removes `next-steps-view`, so the store follows it
// back to `student`. What is NOT here is the one thing that outlives a member on
// purpose: the theme (`isDark`), which the sweep leaves alone too.
const initialState = () => ({
  programs: [], // [{ slug, name, sortOrder }]
  // Standalone LEADER resources per program — { [programSlug]: [{ slug, kind, title }] }.
  // Summaries only; a resource's content is fetched when its screen opens, because
  // the home screen needs a title and a chevron and nothing else.
  //
  // The API returns [] for a member without leader-guide access, so an empty list
  // here means "nothing to show" whether the member is not a leader or the program
  // simply has no resources — the screen does not need to tell those apart.
  programResources: {},
  // The resource currently open, with its content. null until one is fetched.
  currentResource: null,
  // Per-member access summary from GET /api/courses/access — the server is the
  // source of truth for program visibility, lesson locks, purchase CTAs, and
  // feature enablement. null until fetched. See getters below.
  access: null,
  // When `access` landed, on the monotonic clock; null while unloaded or after a
  // failed fetch. Read only by fetchCourseAccess's `maxAgeMs` (see ACCESS_FRESH_MS).
  accessFetchedAt: null,
  currentProgramSlug: null,
  view: 'student', // audience view: 'student' | 'leader' (leaders only) — persists across pages
  // The view the LOADED lesson was actually fetched with. Usually `view`, but not
  // always: the Leader's Guide lesson is leader-only whatever the toggle says
  // (see `effectiveView` in the reader). Every in-lesson highlight/note write
  // stamps THIS, so a mark made in the Leader's Guide is not filed under the
  // student view and then never shown again.
  currentLessonView: 'student',
  lessons: [], // carousel lessons from GET /lessons (excludes the no-lesson bucket)
  lessonOptions: [], // note-tagging list from GET /lessons/all (includes no-lesson)
  currentLesson: null,
  totalPages: 0,
  // Last-viewed page per lesson (keyed by lesson slug), so returning to a lesson
  // from a child screen (Notes Hub, a Life Story) reopens on the page the reader
  // left. Kept in the store rather than the URL on purpose: the old ONE-7904 fix
  // wrote the page to the URL via $router.replace on every page turn, which in
  // Ionic Vue is a 'root' navigation that flattened the router-outlet view stack
  // — stranding the reader on a frozen Next Steps selector after back. See
  // _lessonId.vue rememberCurrentPage. Not persisted; a fresh app launch starts clean.
  // { [`${lessonSlug}:${view}`]: pageNumber }. Keyed by VIEW as well as slug: a
  // lesson holds the Student's and Leader's Guides as two different documents of
  // different lengths, so "where I left off" is a different place in each. One
  // key per slug sent a leader opening the Leader's Guide to whatever page they
  // had reached as a student.
  //
  // COVER_LETTER_PAGE (0) is a position like any other: it means the reader was
  // left standing on the Part 2 brief. This is the whole record of where they
  // were — there is no second flag saying whether the brief has been shown. There
  // used to be (`coverLettersSeen`), and the two disagreed: the flag was set the
  // moment the brief appeared, so a leader who opened a lesson, read the brief and
  // left came back to page 1 — a page they had never seen. One rule, one place.
  lessonPages: {},
  highlights: {}, // { [pageId]: highlight[] }
  progress: {}, // { lessonId: { lastPage, completedAt } } — merged across programs
  // Which slugs the last fetch of each program contributed to `progress`. Only
  // bookkeeping for setProgramProgress, so a refetch can retire that program's
  // own stale entries without touching another program's. Nothing reads it.
  progressByProgram: {}, // { [programSlug]: { [lessonId]: { completedAt } } }
  userAnswers: {}, // { lessonId: { pageId: { blankId: answer } } }
  blankAnswers: {}, // { [blockId]: answer } — from API
  lessonSlug: null,
  isLeader: false,
  loading: false,
  error: null,
  /**
   * The notes a surface currently has an EDITOR open on — the Hub's expanded
   * cards and the highlight drawer's notes.
   *
   * Here rather than in the components that know it because `resume` is handled
   * once, in App.vue, and it has to be able to ask "what is the member looking
   * at?" without reaching into a page's data. Ids, not rows: this says which
   * rows are on screen, never what they hold.
   */
  openNoteIds: [],
  // NotesHub state (API source-of-truth; localStorage is a read-cache only).
  // `allNotes` is the flat cross-lesson note list from GET /api/notes — each
  // note carries lessonSlug, programSlug, highlightId (null for standalone),
  // tags/tagObjects.
  allNotes: [], // Note[]
  notesTotal: 0, // total count from the paginated /api/notes response
  // Bumped by every /api/notes request so a response can tell whether a later
  // one has overtaken it. The component cannot make this call: by the time it
  // sees the response the rows are already committed.
  notesEpoch: 0,
  // `allHighlights` is the flat cross-lesson highlight list from GET /api/highlights
  // — each carries lessonSlug + selectedText + color, grouped by lessonSlug for the
  // hub (no page/block resolution needed, unlike the in-lesson page-keyed `highlights`).
  allHighlights: [], // { id, lessonSlug, programSlug, color, text }[]
  tags: [], // Tag[] — every tag the member owns, part- and guide-wide (GET /api/tags)
  // Annotations this device could not read, `${type}:${id}` -> reason (see
  // UNREADABLE_REASON). An encrypted row lands here when this build cannot decode
  // its envelope version, when the device holds no master key, or when the
  // envelope failed to open. The Hub and the reader read the getters over it to
  // decide between a per-item placeholder, the "update the app" line and the
  // blocking "approve this device" state. Rewritten per fetch, per type.
  unreadableAnnotations: {},
  // True while the signed-in member's annotations must not be cached in the
  // clear (this device holds their key, or they are enrolled). Kept by the
  // plaintext-cache guard; every localStorage content writer checks it.
  encryptedMember: false,
  // Set once this member confirms a connect group in this session. Lives in the
  // store, not on a page: the block is shown on both the program landing and the
  // lesson reader, and answering it on one must settle it for the other.
  cgHardBlockConfirmed: false,
  // A card whose redemption the server refused until a CG exists. Also in the
  // store rather than on a page, because the surface that receives the refusal
  // is often not the one that shows the block — a camera scan is answered
  // wherever the app happens to be, and then navigates to the chooser.
  cgPendingRedeemCode: null,
  // The leader-unlock twin of the code above: a scanned leader QR whose
  // redemption stopped at the CG check (locally, or the server's
  // MEMBER_HAS_NO_CG). Held so linking a CG resumes THAT unlock instead of
  // sending the member back to find the leader's screen (Codex, #1522).
  cgPendingUnlockToken: null,
  // Leader's dashboard. Server-authoritative — no mock fallback: the
  // /courses/leader/* endpoints are live. Everything here is derived from CG
  // and zone membership, so the whole module is read-plus-mark: there is no
  // enrolment write path.
  leaderContext: null,
  students: [],
  // Zone leaderboard — the same derivation one level up. `students` is the I/GI
  // of the CG this leader leads; `zones` is every CG in their zone(s).
  zones: [],
  cgDetail: null,
});

const state = () => ({
  ...initialState(),
  // Restore the audience toggle chosen in a previous session (default student).
  view: readView(),
  // Light/dark for the whole Next Steps section. Lives here rather than on the
  // landing page so every surface (reader, notes hub) sees the same value —
  // as local page state it could never propagate past the page that owned it.
  isDark: readTheme(),
  // Hydrate from the read-cache so the hub paints instantly; fetchAll* then
  // replaces these with fresh API data.
  allNotes: reviveAllNotes(readJson(ALL_NOTES_STORAGE_KEY(readView()), [])),
  // Optimistic temp id -> server id, for edits that still name the temp row.
  noteIdAliases: {},
  allHighlights: readJson(ALL_HIGHLIGHTS_STORAGE_KEY(readView()), []),
  tags: readJson(TAGS_STORAGE_KEY, []),
});

const mutations = {
  setCgHardBlockConfirmed(state, confirmed) {
    state.cgHardBlockConfirmed = !!confirmed;
  },
  setCgPendingRedeemCode(state, code) {
    state.cgPendingRedeemCode = code || null;
  },
  setCgPendingUnlockToken(state, token) {
    state.cgPendingUnlockToken = token || null;
  },
  setLeaderContext(state, context) {
    state.leaderContext = context || null;
  },
  setStudents(state, students) {
    state.students = Array.isArray(students) ? students : [];
  },
  setZones(state, zones) {
    state.zones = Array.isArray(zones) ? zones : [];
  },
  setCgDetail(state, detail) {
    state.cgDetail = detail || null;
  },
  // Reconcile one student in place — optimistic update and the server response
  // both flow through here. No-op if the id isn't on the roster.
  setStudentProgress(state, student) {
    state.students = state.students.map(s => (s.id === student.id ? { ...s, ...student } : s));
  },
  set(state, { key, value }) {
    state[key] = value;
  },
  setLessons(state, lessons) {
    state.lessons = lessons || [];
  },
  setLessonOptions(state, lessons) {
    state.lessonOptions = lessons || [];
  },
  setPrograms(state, programs) {
    state.programs = programs || [];
  },
  setProgramResources(state, { programSlug, resources }) {
    // Replaced wholesale per program rather than merged: the list IS the answer for
    // that program, and a merge would keep showing a resource that was unpublished.
    state.programResources = { ...state.programResources, [programSlug]: resources || [] };
  },
  setCurrentResource(state, resource) {
    state.currentResource = resource || null;
  },
  setCurrentProgramSlug(state, slug) {
    state.currentProgramSlug = slug;
  },
  setView(state, view) {
    const next = view === 'leader' ? 'leader' : 'student';
    if (next === state.view) return;
    state.view = next;
    // Persist so the choice sticks across refresh / app relaunch.
    writeJson(VIEW_STORAGE_KEY, state.view);
    // Highlights and notes belong to ONE view, so swap the lists over with the
    // toggle instead of leaving the previous guide's marks on screen until the
    // refetch lands. Each view keeps its own read-cache, so this paints the new
    // view's last-known data (or nothing) rather than clearing to empty.
    state.allNotes = reviveAllNotes(readJson(ALL_NOTES_STORAGE_KEY(state.view), []));
    state.allHighlights = readJson(ALL_HIGHLIGHTS_STORAGE_KEY(state.view), []);
    state.notesTotal = state.allNotes.length;
    // The page-keyed map is dropped outright: its keys are page ids, and the two
    // views have none in common, so nothing here belongs to the view being
    // entered. The reader refetches on load.
    state.highlights = {};
  },
  setDark(state, isDark) {
    state.isDark = !!isDark;
    writeTheme(state.isDark);
  },
  // Remember which page of a lesson the reader is on, so a return to the lesson
  // reopens there. Replaces the old URL ?page= sync (see the lessonPages note in
  // initialState). A plain store write — deliberately touches neither the router
  // nor the Ionic view stack.
  //
  // `page` may be COVER_LETTER_PAGE (0), which is why the guard tests the TYPE
  // rather than truthiness: `if (!page)` dropped the one position the reader could
  // not otherwise get back to.
  setLessonPage(state, { slug, view, page }) {
    if (!slug || !Number.isInteger(page) || page < COVER_LETTER_PAGE) return;
    state.lessonPages = { ...state.lessonPages, [lessonPageKey(slug, view)]: page };
  },
  // Forget where the reader was, so the next open starts the lesson over — at the
  // Cover Letter where there is one, otherwise page 1.
  //
  // "Where I left off" only means something while a lesson is unfinished. Once it
  // has been completed, reopening on the last page shows the reader the end of
  // something they have already finished — the fix asked for in ONE-8254. Forgetting
  // the position is now the whole of it: nothing remembered means the reading pass
  // has not started, which is exactly what puts the brief back (ONE-8294).
  //
  // Clears the key for ONE view, not the slug: the two guides are different
  // documents (see the `lessonPages` note in initialState), so finishing the
  // Leader's Guide must not throw away a student's place in the same lesson.
  clearLessonPage(state, { slug, view }) {
    if (!slug) return;
    const key = lessonPageKey(slug, view);
    if (!(key in state.lessonPages)) return;
    const next = { ...state.lessonPages };
    delete next[key];
    state.lessonPages = next;
  },
  setCurrentLesson(state, lesson) {
    state.currentLesson = lesson;
    state.totalPages = lesson?.totalPages || 0;
  },
  setCurrentLessonView(state, view) {
    state.currentLessonView = view === 'leader' ? 'leader' : 'student';
  },
  setLessonSlug(state, slug) {
    state.lessonSlug = slug;
  },
  setPageHighlights(state, { pageId, highlights }) {
    // Item 1f, for the notes nested inside a highlight: re-entering a lesson
    // refetches its highlights, and a drawer note being typed must not be
    // replaced by the server's older copy of itself.
    // Same two sources as `preservePending`: the live queue, and the outbox.
    const current = (state.highlights[pageId] || []).flatMap(hl => hl.notes || []);
    const kept = (highlights || []).map(hl =>
      Array.isArray(hl.notes)
        ? { ...hl, notes: preservePending(hl.notes, current, n => (n?.id ? noteKey(n.id) : null)) }
        : hl,
    );
    state.highlights = { ...state.highlights, [pageId]: kept };
  },
  addHighlight(state, highlight) {
    const pageId = highlight.pageId;
    const existing = state.highlights[pageId] || [];
    state.highlights = { ...state.highlights, [pageId]: [...existing, highlight] };
  },
  updateHighlight(state, highlight) {
    const pageId = highlight.pageId;
    const existing = state.highlights[pageId] || [];
    const index = existing.findIndex(h => h.id === highlight.id);
    if (index !== -1) {
      const updated = [...existing];
      updated.splice(index, 1, highlight);
      state.highlights = { ...state.highlights, [pageId]: updated };
    }
  },
  removeHighlight(state, { highlightId, pageId }) {
    const existing = state.highlights[pageId] || [];
    state.highlights = {
      ...state.highlights,
      [pageId]: existing.filter(h => h.id !== highlightId),
    };
  },
  /**
   * Patch a note wherever it lives, without being told where that is.
   *
   * A note is in two places at once — the Hub's flat `allNotes`, and nested
   * inside its highlight in `highlights` — and the outbox overlay knows only the
   * note's id: it runs before any fetch, so there is nothing yet to look the
   * highlight up from. Everything else that patches a note has a `highlightId`
   * in hand and should keep using the narrower mutations.
   */
  /**
   * A surface opened or closed an editor on a note. Idempotent both ways: the
   * Hub and the drawer can both have the same note open, and a page that
   * unmounts without closing its editor must not leave the id here for ever —
   * which is why the drawer clears its whole set rather than one id at a time.
   */
  setNoteEditorOpen(state, { noteId, open }) {
    if (!noteId) return;
    const without = state.openNoteIds.filter(id => id !== noteId);
    state.openNoteIds = open ? [...without, noteId] : without;
  },

  /** Every editor on this screen closed at once — a drawer dismissed, a page left. */
  clearOpenNoteEditors(state, noteIds = null) {
    state.openNoteIds = noteIds ? state.openNoteIds.filter(id => !noteIds.includes(id)) : [];
  },

  patchNoteEverywhere(state, { noteId, patch }) {
    state.allNotes = state.allNotes.map(n => (n.id === noteId ? { ...n, ...patch } : n));
    const updated = {};
    let touched = false;
    for (const [pageId, highlights] of Object.entries(state.highlights)) {
      updated[pageId] = highlights.map(h => {
        if (!Array.isArray(h.notes) || !h.notes.some(n => n?.id === noteId)) return h;
        touched = true;
        return withNotes({
          ...h,
          notes: h.notes.map(n => (n?.id === noteId ? { ...n, ...patch } : n)),
        });
      });
    }
    if (touched) state.highlights = updated;
  },

  // ── Per-highlight note mutations (1..many notes per highlight) ────────────
  // Replace the full notes[] array on a highlight.
  setHighlightNotes(state, { highlightId, notes }) {
    const updated = {};
    for (const [pageId, highlights] of Object.entries(state.highlights)) {
      updated[pageId] = highlights.map(h =>
        h.id === highlightId ? withNotes({ ...h, notes: notes || [] }) : h,
      );
    }
    state.highlights = updated;
  },
  addHighlightNote(state, { highlightId, note }) {
    const updated = {};
    for (const [pageId, highlights] of Object.entries(state.highlights)) {
      updated[pageId] = highlights.map(h =>
        h.id === highlightId ? withNotes({ ...h, notes: [...(h.notes || []), note] }) : h,
      );
    }
    state.highlights = updated;
  },
  updateHighlightNote(state, { highlightId, noteId, patch }) {
    const updated = {};
    for (const [pageId, highlights] of Object.entries(state.highlights)) {
      updated[pageId] = highlights.map(h => {
        if (h.id !== highlightId) return h;
        const notes = (h.notes || []).map(n => (n.id === noteId ? { ...n, ...patch } : n));
        return withNotes({ ...h, notes });
      });
    }
    state.highlights = updated;
  },
  replaceHighlightNoteId(state, { highlightId, tempId, note }) {
    if (note?.id && note.id !== tempId) {
      state.noteIdAliases = { ...(state.noteIdAliases || {}), [tempId]: note.id };
      // Same carry as `replaceNoteInAll`: an open editor follows the re-key.
      state.openNoteIds = state.openNoteIds.map(id => (id === tempId ? note.id : id));
    }
    const updated = {};
    for (const [pageId, highlights] of Object.entries(state.highlights)) {
      updated[pageId] = highlights.map(h => {
        if (h.id !== highlightId) return h;
        const notes = (h.notes || []).map(n => (n.id === tempId ? { ...n, ...note } : n));
        return withNotes({ ...h, notes });
      });
    }
    state.highlights = updated;
  },
  removeHighlightNote(state, { highlightId, noteId }) {
    const updated = {};
    for (const [pageId, highlights] of Object.entries(state.highlights)) {
      updated[pageId] = highlights.map(h => {
        if (h.id !== highlightId) return h;
        const notes = (h.notes || []).filter(n => n.id !== noteId);
        return withNotes({ ...h, notes });
      });
    }
    state.highlights = updated;
  },
  // `answers` is the list the transport hands back, already reduced to rows that
  // name a blank.
  setBlankAnswers(state, answers) {
    const map = {};
    (answers || []).forEach(a => {
      // An answer this device could not open is not an empty answer; it is
      // left out, and recorded separately, so nothing types over it as blank.
      if (a.unreadable) return;
      map[a.blockId] = a.answer ?? '';
    });
    // Item 1f. A lesson's answers arrive after the page paints, so a member who
    // starts filling a blank straight away would otherwise watch the server's
    // older answer land on top of what they are typing. Keyed by the composite
    // the upsert uses, which is what the queue is keyed by too.
    const programSlug = blankAnswerProgramSlug(state);
    const lessonSlug = state.currentLesson?.id ?? state.lessonSlug;
    Object.keys(state.blankAnswers).forEach(blockId => {
      if (autosave.hasPending(blankAutosaveKeyFor(state, blockId))) {
        map[blockId] = state.blankAnswers[blockId];
      }
    });
    // And the outbox's unsent answers for this lesson, which have no local row
    // to keep (the engine has no slot for a body an earlier session queued).
    pendingOverlay.keys().forEach(key => {
      const prefix = `${AUTOSAVE_TYPE.BLANK}:`;
      if (!key.startsWith(prefix)) return;
      const scope = blankScopeOf(key.slice(prefix.length));
      if (!scope || scope.programSlug !== programSlug || scope.lessonSlug !== lessonSlug) return;
      const overlay = pendingOverlay.patchFor(key);
      if (overlay && overlay.answer !== undefined) map[scope.blockId] = overlay.answer;
    });
    state.blankAnswers = map;
  },
  setBlankAnswer(state, { blockId, answer }) {
    state.blankAnswers = { ...state.blankAnswers, [blockId]: answer };
  },
  // Undo an optimistic blank answer the sealing hook refused: put back what was
  // there and take out what was not. An absent answer must not come back as an
  // explicit `undefined`, which getPageAnswers would let shadow the server's
  // copy in blankAnswers.
  restoreBlankAnswer(state, { lessonId, pageId, blockId, blank, user }) {
    const answers = { ...state.blankAnswers };
    if (blank === undefined) delete answers[blockId];
    else answers[blockId] = blank;
    state.blankAnswers = answers;
    const pageAnswers = state.userAnswers[lessonId]?.[pageId];
    if (!pageAnswers) return;
    if (user === undefined) delete pageAnswers[blockId];
    else pageAnswers[blockId] = user;
    // The in-memory rollback is for everyone; the cache write is a plaintext
    // writer like setUserAnswer's, and stays off for an enrolled member
    // (decision 44). It would otherwise serialise the whole answer map.
    if (state.encryptedMember) return;
    try {
      localStorage.setItem('next-steps-answers', JSON.stringify(state.userAnswers));
    } catch (e) {
      console.error('Failed to save answers to localStorage:', e);
    }
  },
  // `entries` is unreadableEntries() over one fetch's rows: every row it returned,
  // readable or not, so the record for that type reflects the latest load. See
  // recordUnreadable for when `append` is set.
  /**
   * Every unreadable mark, gone. For the moment a key ARRIVES on this device
   * (recovery, approval, a kept device re-keyed): the NO_KEY marks recorded
   * before it are about a key this device did not have, and `annotationsLocked`
   * reads them as "nothing here opens" — which sent the Hub to the approval
   * screen, which sent the approved member straight back, for ever (DEV-13,
   * 2026-09-11). The next fetch of each list records the truth again.
   */
  clearUnreadableAnnotations(state) {
    state.unreadableAnnotations = {};
  },
  recordUnreadableAnnotations(state, { type, entries, append = false }) {
    const prefix = `${type}:`;
    const kept = Object.entries(state.unreadableAnnotations).filter(
      ([key]) => append || !key.startsWith(prefix),
    );
    const next = Object.fromEntries(kept);
    entries.forEach(({ id, unreadable }) => {
      const key = `${type}:${id}`;
      if (unreadable) next[key] = unreadable;
      else delete next[key];
    });
    state.unreadableAnnotations = next;
  },
  setProgress(state, progress) {
    state.progress = progress;
    state.progressByProgram = {};
  },
  // Fold one program's completions into the shared map. `progress` is keyed by
  // lesson slug, and the chooser needs every part's rows at once, so a fetch must
  // not replace the whole map the way setProgress does — Part 1 would read 0%
  // purely because Part 2 loaded last. A refetch of the *same* program still has
  // to retire its own stale rows (an un-completed lesson stops coming back from
  // the server), which is why the previous slice is dropped before the fresh one
  // lands. Rows that no fetch has claimed — setLessonProgress writes from
  // updateProgress — are left alone.
  setProgramProgress(state, { program, progress }) {
    const merged = { ...state.progress };
    Object.keys(state.progressByProgram[program] || {}).forEach(slug => {
      delete merged[slug];
    });
    state.progress = { ...merged, ...progress };
    state.progressByProgram = { ...state.progressByProgram, [program]: progress };
  },
  setLessonProgress(state, { lessonId, program, progress }) {
    state.progress = { ...state.progress, [lessonId]: progress };
    // Into the program's own slice too, when the caller names it, so the landing
    // bars and the chooser see the completion without a refetch. A later fetch of
    // that program replaces the slice from the server, as it always did.
    if (program) {
      const slice = { ...(state.progressByProgram[program] || {}), [lessonId]: progress };
      state.progressByProgram = { ...state.progressByProgram, [program]: slice };
    }
  },
  setLeaderStatus(state, isLeader) {
    state.isLeader = isLeader;
  },
  setCourseAccess(state, access) {
    state.access = access || null;
    state.accessFetchedAt = state.access ? monotonicNow() : null;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  // Logout commits this (Logout.js). Fresh objects every time — see initialState.
  // The plaintext-cache flag survives it: logout resets this module before it
  // wipes the key, and the guard re-fires only when the member's enrolment
  // signals change, which they have not yet. A cleared flag here would put every
  // writer back to work for the outgoing member with the key still in hand.
  reset(state) {
    Object.assign(state, initialState(), { encryptedMember: state.encryptedMember });
  },
  // Flipped by the plaintext-cache guard as the member's enrolment becomes
  // known. Turning it on also removes what the caches held from before, since
  // a member who enrolled today wrote plaintext into them yesterday.
  setEncryptedMember(state, encrypted) {
    state.encryptedMember = !!encrypted;
    if (state.encryptedMember) sweepPlaintextCaches();
  },
  setUserAnswer(state, { lessonId, pageId, blankId, answer }) {
    if (!state.userAnswers[lessonId]) state.userAnswers[lessonId] = {};
    if (!state.userAnswers[lessonId][pageId]) state.userAnswers[lessonId][pageId] = {};
    state.userAnswers[lessonId][pageId][blankId] = answer;
    if (state.encryptedMember) return;
    try {
      localStorage.setItem('next-steps-answers', JSON.stringify(state.userAnswers));
    } catch (e) {
      console.error('Failed to save answers to localStorage:', e);
    }
  },
  loadUserAnswers(state, answers) {
    state.userAnswers = answers || {};
  },
  // ── NotesHub (cross-lesson) note mutations ────────────────────────────────
  // `allNotes` is the flat note list backing the hub. The API is the source of
  // truth; we refresh the read-cache on every mutation so an offline reload
  // paints last-known data (never a write-fallback for a failed mutation).
  setAllNotes(state, notes) {
    const fetched = preservePending(notes || [], state.allNotes, row =>
      row?.id ? noteKey(row.id) : null,
    );
    // A new Hub note the server has not been told of yet — its first keystroke
    // is still to come, its create is in the air, or its create failed for want
    // of a network — is a row no fetch can return, so it would vanish on the
    // first Hub re-entry with its text. It stays until the create lands, the
    // member deletes it, or `discardEmptyNoteDrafts` finds it empty and
    // unowned.
    const fetchedIds = new Set(fetched.map(n => n?.id));
    const drafts = (state.allNotes || []).filter(n => n?.pendingCreate && !fetchedIds.has(n.id));
    state.allNotes = drafts.length ? [...fetched, ...drafts] : fetched;
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  // Append the next page of notes (infinite scroll), de-duping by id so an
  // optimistic note already in state is never doubled by the server page.
  appendAllNotes(state, notes) {
    state.allNotes = appendNotesById(
      state.allNotes,
      preservePending(notes, state.allNotes, row => (row?.id ? noteKey(row.id) : null)),
    );
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  setNotesTotal(state, total) {
    state.notesTotal = total || 0;
  },
  bumpNotesEpoch(state) {
    state.notesEpoch += 1;
  },
  // Flat cross-lesson highlight list backing the hub's Highlights tab.
  setAllHighlights(state, highlights) {
    state.allHighlights = highlights || [];
    persistJson(state, ALL_HIGHLIGHTS_STORAGE_KEY(state.view), state.allHighlights);
  },
  removeHighlightFromAll(state, highlightId) {
    state.allHighlights = state.allHighlights.filter(h => h.id !== highlightId);
    persistJson(state, ALL_HIGHLIGHTS_STORAGE_KEY(state.view), state.allHighlights);
  },
  // Every note that hung off a highlight, dropped with it.
  //
  // The server deletes them (the confirmation promises it, and hogc-one-api #947
  // makes it true), but the hub keeps TWO lists: `allHighlights` behind the
  // Highlights tab and `allNotes` behind the Notes tab. Deleting a highlight
  // updated the first and not the second, so its notes stayed on screen with
  // nothing to belong to until the next refetch — visibly orphaned, and worse,
  // tappable. Before #947 leaving them was CORRECT, because the server detached
  // them rather than deleting them; the client was never brought along.
  //
  // The count moves with the rows because the hub prints it as the tab label.
  removeNotesForHighlight(state, highlightId) {
    const before = state.allNotes.length;
    state.allNotes = state.allNotes.filter(note => note.highlightId !== highlightId);
    const removed = before - state.allNotes.length;
    if (removed > 0) state.notesTotal = Math.max(0, state.notesTotal - removed);
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  addNoteToAll(state, note) {
    state.allNotes = [...state.allNotes, note];
    state.notesTotal += 1;
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  replaceNoteInAll(state, { noteId, note }) {
    if (note?.id && note.id !== noteId) {
      state.noteIdAliases = { ...(state.noteIdAliases || {}), [noteId]: note.id };
      // An editor open on the temp row is open on the server's row now. The Hub
      // keeps a note being edited on screen by this list whatever its filters
      // say; a pin left on the old id would let a search drop the row the
      // moment its create landed, and close the editor mid-sentence.
      state.openNoteIds = state.openNoteIds.map(id => (id === noteId ? note.id : id));
    }
    state.allNotes = state.allNotes.map(n => (n.id === noteId ? { ...n, ...note } : n));
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  updateNoteInAll(state, { noteId, patch }) {
    state.allNotes = state.allNotes.map(n => (n.id === noteId ? { ...n, ...patch } : n));
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  removeNoteFromAll(state, noteId) {
    const before = state.allNotes.length;
    state.allNotes = state.allNotes.filter(n => n.id !== noteId);
    if (state.allNotes.length < before) state.notesTotal = Math.max(0, state.notesTotal - 1);
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  // ── Tag list mutations (the member's tags, across every part and guide) ───
  setTags(state, tags) {
    state.tags = tags || [];
    persistJson(state, TAGS_STORAGE_KEY, state.tags);
  },
  addTag(state, tag) {
    state.tags = [...state.tags, tag];
    persistJson(state, TAGS_STORAGE_KEY, state.tags);
  },
  renameTag(state, { tagId, newName }) {
    state.tags = state.tags.map(t => (t.id === tagId ? { ...t, name: newName } : t));
    persistJson(state, TAGS_STORAGE_KEY, state.tags);
  },
  deleteTag(state, tagId) {
    state.tags = state.tags.filter(t => t.id !== tagId);
    // Strip the deleted tag from any cached note's tag arrays so the hub stops
    // rendering it immediately (the API has already removed the association).
    state.allNotes = mapKeepingIdentity(state.allNotes, n => stripNoteTag(n, tagId));
    // The reader keeps its own copy of a highlight's notes, and an open drawer
    // reads from it — so a delete made from the drawer's own tag screen left a
    // nameless pill sitting on the note until the next fetch.
    state.highlights = mapHighlightNotes(state.highlights, note => stripNoteTag(note, tagId));
    persistJson(state, TAGS_STORAGE_KEY, state.tags);
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
  // Undo of the above. The server answers the restore with the notes whose links
  // it brought back, because the device cannot know them: a link on a note in
  // another part or guide was never in any cache here.
  restoreTag(state, { tag, noteIds }) {
    if (!tag?.id) return;
    if (!state.tags.some(t => t.id === tag.id)) state.tags = [...state.tags, tag];
    const restored = new Set(noteIds || []);
    const reattach = note =>
      restored.has(note.id) && !(note.tags || []).includes(tag.id)
        ? {
            ...note,
            tags: [...(note.tags || []), tag.id],
            tagObjects: [...(note.tagObjects || []), tag],
          }
        : note;
    state.allNotes = mapKeepingIdentity(state.allNotes, reattach);
    state.highlights = mapHighlightNotes(state.highlights, reattach);
    persistJson(state, TAGS_STORAGE_KEY, state.tags);
    persistJson(state, ALL_NOTES_STORAGE_KEY(state.view), state.allNotes);
  },
};

// Roster paging, matched to the API (ROSTER_PAGE_MAX in leader-roster.service.ts).
// The page cap is a guard against a server that ignores `limit`: 50 pages of 200
// is 10,000 members, far beyond any CG, so hitting it means something is wrong
// rather than that the roster is that long.
const ROSTER_PAGE_SIZE = 200;
const ROSTER_MAX_PAGES = 50;

// Zone-board paging. The API clamps `limit` to BOARD_PAGE_MAX (50, in
// leader-roster.service.ts) and defaults to 25 when the caller says nothing —
// which is what this used to send, and why a leader past 25 CGs was cut.
//
// ⚠ 50 is the API's cap AND the test this loop stops on: a page shorter than it
// means the end. Lower the cap server-side without lowering this and the FIRST
// page comes back short, the loop stops, and the board truncates again in
// silence — the same coupling ROSTER_PAGE_SIZE carries above. Change them
// together.
const ZONE_PAGE_SIZE = 50;
// 40 pages of 50 is 2,000 CGs, far past any leader's scope; reaching it means
// something is wrong rather than that the board is that long.
const ZONE_MAX_PAGES = 40;

/**
 * Whether a note has an edit this device has not sent: a live slot the engine
 * holds, or an outbox entry from an earlier session, which has no slot at all.
 * Item 1f's test, shared by every refetch that could otherwise paint the
 * server's row over the member's words.
 */
function unsentNote(noteId) {
  const key = noteKey(noteId);
  return autosave.hasPending(key) || pendingOverlay.has(key);
}

// Bodies of the two guarded writes; the actions of the same name are the guards.
async function runSaveHighlight({ commit, state, rootState }, highlight) {
  commit('setLoading', true);
  try {
    const auth = annotationAuth(rootState);
    // Which block list this highlight's offsets are measured against. Lesson
    // prose measures against the page it renders on; a Life Story measures
    // against itself. Both are saved with the lesson and a real page index —
    // see scopeForNewHighlight.
    const scope = scopeForNewHighlight(state, highlight);
    const pageIndex = scope?.pageIndex ?? 0;
    const anchorBlocks = scope?.blocks || null;
    const blockInfo = anchorBlocks
      ? globalToBlock(highlight.startWordIndex, highlight.endWordIndex, anchorBlocks)
      : null;
    // Lazy, not hoisted: contextForSpan re-tokenises the scope's blocks, and a
    // colour-only PATCH re-states the context it already has rather than
    // recomputing it. Calling it up front would put that walk on the most
    // common write there is.
    const spanContext = () =>
      anchorBlocks
        ? contextForSpan(anchorBlocks, highlight.startWordIndex, highlight.endWordIndex)
        : {};

    if (highlight.id) {
      // Update existing highlight (colour / span only — notes are managed
      // through the dedicated addNote/editNote/deleteNote actions).
      //
      // A PATCH carries the WHOLE content set of spec section 6.4, not the
      // half the caller changed: for an enrolled member the payload column is
      // replaced outright, so a colour-only body would destroy the quote, its
      // context and the page index, and the quote is the only thing that can
      // re-find the mark on a re-authored lesson. Callers hand back a colour
      // and a span, so the rest comes off the stored row.
      const stored = findStoredHighlight(state, highlight.id);
      // The quote: the caller's if it has a new one (a widened span covers
      // different words), else the row's own. Null when neither has one, in
      // which case the field is omitted exactly as before — the column is NOT
      // nullable — and an enrolled member's write is refused at the seam
      // rather than stored short of its anchor.
      const quote = highlight.text || stored?.text || null;
      // The neighbours move with the quote, so they are recomputed for the
      // span being written whenever the scope resolved, and re-stated from the
      // row when it did not. `?? null` rather than omitted: a span dragged to
      // the top of a page genuinely has no words before it, and "there is
      // nothing here" has to be storable.
      const context = anchorBlocks
        ? spanContext()
        : { contextBefore: stored?.contextBefore, contextAfter: stored?.contextAfter };
      const data = await annotationsTransport.updateHighlight(auth, highlight.id, {
        color: hexToEnum(highlight.color),
        // Which page the mark is on. Absent from every highlight PATCH before
        // this, so an enrolled member's recolour blanked it.
        pageIndex: scope ? pageIndex : (stored?.pageIndex ?? 0),
        // The START anchor travels with startWordIndex, which is measured
        // FROM it. A merge can widen a span backwards past the start of its
        // block, which moves the anchor to an earlier one; sending the new
        // offset while the row keeps the old blockId stores a range that
        // decodes somewhere the highlight was never drawn.
        ...(blockInfo?.blockId ? { blockId: blockInfo.blockId } : {}),
        startWordIndex: blockInfo?.startWordIndex,
        endWordIndex: blockInfo?.endWordIndex,
        // Sent even when null, which is how a span dragged back inside one
        // block clears its second anchor. Omitting it would leave the end
        // pointing at a block the highlight no longer reaches. Only skipped
        // when the page is unknown, where there are no indices to send either.
        ...(blockInfo ? { endBlockId: blockInfo.endBlockId } : {}),
        ...(quote ? { selectedText: quote } : {}),
        contextBefore: context.contextBefore ?? null,
        contextAfter: context.contextAfter ?? null,
      });
      const updated = withNotes({
        ...highlight,
        color: enumToHex(data.color),
        // What was just written, kept on the row: `updateHighlight` replaces
        // the store entry outright rather than merging, and callers hand back
        // only a colour and a span — so without these the NEXT recolour would
        // have nothing to re-state and would be refused at the seam.
        pageIndex: scope ? pageIndex : (stored?.pageIndex ?? 0),
        text: quote ?? '',
        contextBefore: context.contextBefore ?? null,
        contextAfter: context.contextAfter ?? null,
        notes: highlight.notes || [],
      });
      commit('updateHighlight', updated);
      return updated;
    } else {
      // Create new highlight (no embedded notes — highlights are created
      // empty; notes are attached afterwards via addNote).
      const data = await annotationsTransport.createHighlight(auth, {
        programSlug: state.currentProgramSlug || 'next-steps-p1',
        lessonSlug: state.lessonSlug,
        pageIndex,
        blockId: blockInfo?.blockId || (anchorBlocks?.[0]?.id ?? ''),
        // Omitted rather than sent as null for a single-block highlight: the
        // create DTO treats absence as "ends in blockId", and the column
        // defaults to null anyway.
        ...(blockInfo?.endBlockId ? { endBlockId: blockInfo.endBlockId } : {}),
        startWordIndex: blockInfo?.startWordIndex ?? highlight.startWordIndex,
        endWordIndex: blockInfo?.endWordIndex ?? highlight.endWordIndex,
        selectedText: highlight.text,
        // The quote's neighbours, so a later edit to the lesson can tell THIS
        // occurrence of it from another one on the same page. Without them a
        // repeated quote is ambiguous and the highlight stays unanchored.
        ...spanContext(),
        color: hexToEnum(highlight.color),
        // Which guide this mark belongs to — the view the OPEN lesson was
        // fetched with, not the toggle (see currentLessonView). The server
        // re-checks it against the member's leader access and downgrades if
        // they lack it.
        view: state.currentLessonView,
      });
      const newContext = spanContext();
      const created = withNotes({
        id: data.id,
        // The bucket, not necessarily the page: a story's marks live under their
        // own scope key so their story-local indices cannot collide with the
        // page-global ones filed under the page itself.
        pageId: scope?.scopeKey,
        blockId: data.blockId,
        startWordIndex: highlight.startWordIndex,
        endWordIndex: highlight.endWordIndex,
        color: enumToHex(data.color),
        text: data.selectedText,
        // As on a fetched row: what a later PATCH re-states rather than blanks.
        pageIndex,
        contextBefore: newContext.contextBefore ?? null,
        contextAfter: newContext.contextAfter ?? null,
        notes: [],
      });
      commit('addHighlight', created);
      return created;
    }
  } catch (error) {
    // API is the source of truth — a failed write must NOT silently persist
    // to localStorage (that path diverges and never resyncs). Surface the
    // error to the caller so it can roll back + toast.
    //
    // #1584: a recolour re-states the whole content set from the cached row,
    // so a stale one would undo a span or context another device fixed. The
    // API refuses it; this drops the revision it refused and tells the
    // member. Never replayed — see `onRevisionConflict`.
    if (isStaleRevisionError(error)) {
      onRevisionConflict('highlight', highlight.id);
      commit('setError', REVISION_CONFLICT_MESSAGE);
      throw error;
    }
    commit(
      'setError',
      error.response?.data?.message || error.message || 'Failed to save highlight',
    );
    throw error;
  } finally {
    commit('setLoading', false);
  }
}

async function runUpdateProgress(
  { commit, state, rootState },
  { lessonId, isCompleted = true, program },
) {
  try {
    const token = rootState.user.token;
    const programSlug = program || state.currentProgramSlug || 'next-steps-p1';
    const headers = { Authorization: `Bearer ${token}` };
    const url = `${process.env.VUE_APP_API_URL}/lessons/${lessonId}/complete`;
    if (isCompleted) {
      await axios.put(url, { program: programSlug }, { headers });
    } else {
      await axios.delete(url, { params: { program: programSlug }, headers });
    }
    commit('setLessonProgress', {
      lessonId,
      program: programSlug,
      progress: isCompleted ? { completedAt: true } : {},
    });
    return { lessonId, isCompleted };
  } catch (error) {
    console.error('Failed to update progress:', error);
    throw error;
  }
}

const actions = {
  // ── Leader's dashboard ──────────────────────────────────────────────────
  // Marking discipleship progress. Distinct from course ACCESS, which is
  // verified by the member's own CG on ACTS and never granted here.

  // Whether this member may disciple at all: base pastoral status >= R AND an
  // active pastoral/others leadership role at PCGL/POGL or above. Deliberately
  /**
   * What to do with a scanned resource link, for THIS member.
   *
   * Authenticated on purpose: the public `GET /deeplink/:key` withholds a gated
   * resource's target, because a printed QR is photographable and that endpoint is
   * effectively open. This one answers per member — see the API's
   * DeepLinkService.resolveForMember for the decision order.
   *
   * Returns null on any failure rather than throwing. The caller's fallback is the
   * app's normal deep-link behaviour (home), and a scanned code that cannot be
   * resolved is indistinguishable to the member from one that does not exist.
   */
  // Retried on a transient failure, for the same reason the anonymous lookup is: a
  // scanned QR asks this at a cold start, and the gateway answers ANY null by sending
  // the member to /home. A 403 or 404 is a real answer and returns immediately; a
  // timeout or a 5xx is asked again. See utils/transient-retry.js.
  //
  // A 401 is deliberately NOT retried here — retrying without a token just spends the
  // budget on the same refusal. The gateway waits for the token before it calls.
  async resolveResourceLink(_ctx, key) {
    const token = _ctx.rootState.user.token;
    try {
      return await withLookupRetry(
        async () => {
          const { data } = await axios.get(
            `${process.env.VUE_APP_API_URL}/deeplink/${encodeURIComponent(key)}/resolve`,
            { headers: { Authorization: `Bearer ${token}` }, timeout: LOOKUP_TIMEOUT_MS },
          );
          return data;
        },
        {
          onRetry: (error, attempt, delay) =>
            console.warn(
              `[ResourceLink] Resolve for ${key} failed (attempt ${attempt + 1}), retrying in ${delay}ms:`,
              error?.message,
            ),
        },
      );
    } catch (error) {
      console.warn(`[ResourceLink] Failed to resolve ${key}:`, error?.message);
      return null;
    }
  },

  // NOT courses/leader/access, which answers leader-GUIDE entitlement instead.
  async fetchLeaderContext({ commit, rootState }) {
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/courses/leader/context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      commit('setLeaderContext', data);
      return data;
    } catch (error) {
      // Still fail closed — but say WHICH kind of no this is. A 401/403/404 is
      // the server's answer: not eligible, or the flag is off. Anything else
      // (offline, 5xx, timeout) is no answer at all, and treating the two alike
      // bounced eligible leaders to Home on one bad request with nothing to
      // read. The route guard branches on `unavailable` to tell them apart.
      const status = error?.response?.status;
      const definitive = status === 401 || status === 403 || status === 404;
      const context = { canDisciple: false, unavailable: !definitive };
      commit('setLeaderContext', context);
      return context;
    }
  },

  // The members of the CG this leader leads, derived server-side from
  // member_cg_zone — not an enrolment list. Nothing the app does changes who is
  // on it; a member joins when their pastoral status reaches I (or they hold a
  // Next Steps entitlement).
  //
  // PAGED, and every page is fetched. The API pages this roster (default 50,
  // max 200) so that one request never derives progress for a whole large CG
  // at once — that is what keeps it affordable when thousands of leaders open
  // the dashboard together. But the screen renders the whole roster, so a
  // single unpaged call silently showed only the first page: a leader of a
  // 60-person CG saw 50 names and no hint that ten were missing. Walk the pages
  // at the API's maximum until a short page says the roster is exhausted. The
  // server orders by name then member id, so the concatenation is the roster in
  // reading order.
  //
  // `cgId` NAMES WHICH CG, and is omitted rather than defaulted when the caller
  // has none: the server resolves an absent one to `scope.cgId`, the CG the
  // dashboard opens on, so every existing call site keeps its behaviour exactly.
  // Sending a CG the caller may not mark is refused with 403 — `markableCgs` on
  // the context is what a picker should offer, never a free-text id.
  async fetchStudents({ commit, rootState }, { cgId } = {}) {
    try {
      const token = rootState.user.token;
      const students = [];
      for (let page = 0; page < ROSTER_MAX_PAGES; page += 1) {
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/courses/leader/students`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            limit: ROSTER_PAGE_SIZE,
            offset: page * ROSTER_PAGE_SIZE,
            ...(typeof cgId === 'number' ? { cgId } : {}),
          },
        });
        students.push(...data);
        if (data.length < ROSTER_PAGE_SIZE) break;
      }
      commit('setStudents', students);
      return students;
    } catch (error) {
      // Fail closed. An empty roster is a legitimate answer, so it is never
      // treated as "backend not ready" — that conflation is what made the old
      // mock fallback show nine fake students to real leaders. A failure on a
      // later page discards the earlier ones too: half a roster presented as
      // whole is the silent-omission bug this paging exists to prevent.
      commit('setStudents', []);
      throw error;
    }
  },

  // Every CG in this leader's zone(s), rolled up. Read-only: there is no write
  // path that takes a cgId. Rows carry both the all-time and this-month figures
  // so the leaderboard's range toggle never refetches.
  //
  // PAGED, and every page is fetched — the same bug as the roster above, with a
  // sharper edge. The API RANKS the whole zone by yearMarks, a rolling twelve
  // months, and returns one page of that ranking; ZoneLeaderboard then re-sorts
  // what it received by totalMarks or monthMarks. So the unpaged call did not
  // merely cut the tail, it cut it by a metric the board never shows and
  // presented the survivors as an all-time ranking: a CG strong all-time but
  // quiet this past year was simply absent, with nothing on screen saying so.
  //
  // Worse for a multi-zone leader. The page is sliced across ALL their zones at
  // once and only then grouped per zone, and the API drops a zone with nothing
  // left on the page — so an entire zone chip could vanish from the tab bar.
  //
  // Fetching every page is also what makes the local re-sort CORRECT: with the
  // complete set in hand, ordering it by any figure on the row is valid, which
  // is exactly what the range toggle assumes.
  async fetchZones({ commit, rootState }) {
    try {
      const token = rootState.user.token;
      // Merged by zone id rather than concatenated: successive pages carry the
      // SAME zones holding different slices of their CGs, and a zone with
      // nothing on page 1 appears for the first time on a later page.
      const byZone = new Map();
      let offset = 0;

      for (let page = 0; page < ZONE_MAX_PAGES; page += 1) {
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/courses/leader/zones`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: ZONE_PAGE_SIZE, offset },
        });
        const zones = Array.isArray(data) ? data : [];

        // Counted in CGs, not zones: the API pages its ranked list of CGs and
        // groups the slice afterwards, so the zones on a page are a by-product
        // of it. Counting zones would call a full page of one zone's CGs a page
        // of one and stop immediately.
        let cgsOnPage = 0;
        for (const zone of zones) {
          if (!zone || zone.id === null || zone.id === undefined) continue;
          const cgs = Array.isArray(zone.cgs) ? zone.cgs : [];
          cgsOnPage += cgs.length;
          const merged = byZone.get(zone.id);
          if (merged) merged.cgs.push(...cgs);
          else byZone.set(zone.id, { ...zone, cgs: [...cgs] });
        }

        if (cgsOnPage < ZONE_PAGE_SIZE) break;
        offset += cgsOnPage;
      }

      const zones = [...byZone.values()];
      commit('setZones', zones);
      return zones;
    } catch (error) {
      // Same rule as the roster: fail closed and let the page say the load
      // failed, rather than passing an empty zone list off as "no zones". A
      // failure on a later page discards the earlier ones too — half a
      // leaderboard shown as whole is the silent omission this paging exists to
      // prevent.
      commit('setZones', []);
      throw error;
    }
  },

  // One CG's members, read-only. The server refuses any CG outside the caller's
  // zones, so a 403 here means the leaderboard and the detail view disagree.
  async fetchCgDetail({ commit, rootState }, cgId) {
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/courses/leader/cgs/${cgId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      commit('setCgDetail', data);
      return data;
    } catch (error) {
      commit('setCgDetail', null);
      throw error;
    }
  },

  // Mark or unmark one lesson. Optimistic so the chip flips instantly; the
  // server response replaces the guess, and a failure rolls it back.
  async setLessonMark({ commit, state, rootState }, { studentId, n, completed }) {
    const before = state.students.find(s => s.id === studentId);
    if (before) {
      // Every read here is guarded. A roster row that arrives without
      // `doneLessons` used to throw out of this block — which sits OUTSIDE the
      // try below, so the leader got "Couldn't save" for a request that was
      // never sent.
      const done = before.doneLessons || [];
      const doneLessons = completed
        ? [...new Set([...done, n])].sort((a, b) => a - b)
        : done.filter(v => v !== n);
      // `awaitingLessons` is deliberately NOT touched. It records that the
      // member reached the end of the lesson themselves — their fact, which a
      // leader confirming or unconfirming cannot change. The old code stripped
      // the ordinal on confirm and never put it back, so unconfirming left the
      // chip EMPTY instead of returning it to awaiting (reconciliation doc §3
      // requires awaiting, never empty). Restoring it on unmark would have been
      // the opposite lie — it would invent a self-completion for a lesson a
      // leader had confirmed ahead of the member. Leaving the list alone is the
      // only version that needs no guess: `lessonState` already ranks confirmed
      // above awaiting, and `readyToConfirm` filters out anything credited.
      commit('setStudentProgress', { id: studentId, doneLessons });
    }

    try {
      const token = rootState.user.token;
      const { data } = await axios.patch(
        `${process.env.VUE_APP_API_URL}/courses/leader/students/${studentId}/lessons/${n}`,
        { completed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      commit('setStudentProgress', data);
      return data;
    } catch (error) {
      if (before) commit('setStudentProgress', before);
      throw error;
    }
  },

  loadUserAnswersFromStorage({ commit }) {
    try {
      const stored = localStorage.getItem('next-steps-answers');
      if (stored) {
        const answers = JSON.parse(stored);
        commit('loadUserAnswers', answers);
        return answers;
      }
    } catch (e) {
      console.error('Failed to load answers from localStorage:', e);
    }
    return {};
  },

  async fetchPrograms({ commit, rootState }) {
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/programs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      commit('setPrograms', data);
      return data;
    } catch (error) {
      commit('setPrograms', []);
      return [];
    }
  },

  /**
   * A program's standalone leader resources — "Leader's Resource: Stories of HOGC".
   *
   * Summaries only. Non-fatal on failure, like fetchPrograms: these draw an extra
   * card under the lesson carousel, so a failed request should cost the card, not
   * the Next Steps home screen.
   *
   * The API returns [] for anyone without leader-guide access, so there is no
   * leader check here. Doing it in one place matters: a second, client-side check
   * is a second thing that can disagree with the server, and the direction it fails
   * in is showing a leader-only card to a member.
   */
  async fetchProgramResources({ commit, rootState }, { programSlug }) {
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/programs/${programSlug}/resources`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      commit('setProgramResources', { programSlug, resources: data });
      return data;
    } catch (error) {
      commit('setProgramResources', { programSlug, resources: [] });
      return [];
    }
  },

  /**
   * One resource with its content, for the resource screen.
   *
   * Throws rather than swallowing: this IS the screen, so a failure has to reach a
   * caller that can show "not found" instead of an empty page that looks loaded.
   * A non-leader gets a 404 here (not a 403) — see the API service for why.
   */
  async fetchProgramResource({ commit, rootState }, { programSlug, slug }) {
    commit('setCurrentResource', null);
    const token = rootState.user.token;
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_URL}/programs/${programSlug}/resources/${slug}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    commit('setCurrentResource', data);
    return data;
  },

  async fetchLessons({ commit, rootState }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/lessons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      commit('setLessons', data.map(normalizeApiLesson));
      return data;
    } catch (error) {
      commit(
        'setError',
        error.response?.data?.message || error.message || 'Failed to fetch lessons',
      );
      commit('setLessons', []);
    } finally {
      commit('setLoading', false);
    }
  },

  // Lightweight lesson list for the Notes Hub tagging dropdown. Unlike /lessons
  // (the carousel), this INCLUDES the no-lesson (kind OTHER) bucket, and returns
  // both guides to leaders (so the hub can swap GUIDE/LEADER_GUIDE by the active
  // student/leader view). Rows carry no `content`, so it is used only for
  // labels/tagging, not the reader/carousel.
  async fetchLessonOptions({ commit, rootState }) {
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/lessons/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      commit('setLessonOptions', data.map(normalizeApiLesson));
      return data;
    } catch (error) {
      // Non-fatal: the dropdown just falls back to whatever is already loaded.
      commit('setLessonOptions', []);
      return [];
    }
  },

  async fetchLesson({ commit, rootState }, { programSlug, slug, view, anchor }) {
    commit('setLoading', true);
    commit('setError', null);
    commit('setLessonSlug', slug);
    commit('setCurrentProgramSlug', programSlug);
    commit('setCurrentLessonView', view);
    try {
      const token = rootState.user.token;
      // `view=leader` returns LEADER-audience blocks too; default (student) omits them.
      //
      // `anchor` is the `#section` a scanned QR named. It filters nothing — it only
      // asks the server to say whether a section we did NOT receive is leader-only,
      // which is the one thing the client cannot work out for itself (see
      // anchorLeaderOnly). Sent only when there is one, so an ordinary lesson open
      // is byte-for-byte the request it always was.
      const params = {};
      if (view === 'leader') params.view = 'leader';
      if (anchor) params.anchor = anchor;
      const { data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/programs/${programSlug}/lessons/${slug}`,
        { headers: { Authorization: `Bearer ${token}` }, params },
      );
      const lesson = normalizeApiLesson(data);
      // Name the anchor the server's answer is ABOUT. See leaderOnlyAnchor.
      lesson.leaderOnlyAnchor = anchor && data?.anchorLeaderOnly === true ? anchor : null;
      // The anchor we ASKED about, whatever the answer. Distinct from the line
      // above, which records only a yes: the reader needs to know the question has
      // been put, or an anchor that exists in neither guide is asked about again on
      // every return to the lesson.
      lesson.requestedAnchor = anchor || null;
      commit('setCurrentLesson', lesson);
      return lesson;
    } catch (error) {
      commit(
        'setError',
        error.response?.data?.message || error.message || 'Failed to fetch lesson',
      );
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // `view` is the reader's EFFECTIVE view, which is not always the toggle: the
  // Leader's Guide lesson is leader-only whatever the toggle says, so the reader
  // passes what it fetched the content with. Reading the wrong one paints the
  // other guide's marks — or, since the two views' block ids are disjoint, none
  // at all.
  async fetchLessonHighlights({ commit, state, rootState }, { slug, view }) {
    const audienceView = view === 'leader' ? 'leader' : 'student';
    try {
      const auth = annotationAuth(rootState);
      const data = await annotationsTransport.fetchLessonHighlights(auth, {
        slug,
        view: audienceView,
      });
      // Rows this device could not open carry no position and nothing to show
      // inline. Recorded so the reader can say so, and skipped below before the
      // anchor search would count them as content that moved.
      recordUnreadable(commit, 'highlight', data);
      recordUnreadable(
        commit,
        'note',
        (data || []).flatMap(hl => hl.notes || []),
      );
      // Group by page, converting block-scoped indices to global page indices
      const pages = state.currentLesson?.pages || [];
      const byPage = {};
      // Highlights the page/block anchors could not resolve. Counted rather than
      // dropped in silence: both guards below used to `return` with no trace, so a
      // highlight that stopped resolving was indistinguishable from one that was
      // never made — the reader sees their mark gone and nothing anywhere says why.
      let unanchored = 0;
      // Highlights whose hints decode to words that are not their quote, and whose
      // quote is nowhere else on the lesson. Shown where the hints point, and
      // counted, for the same reason as above.
      let unverified = 0;
      // Highlights whose block anchors no longer resolve but whose QUOTE was found
      // elsewhere. Healed back into the row after the list is built, so the next
      // load is an exact hit rather than another search.
      const healed = [];
      // Page word lists are expensive to build and shared across every re-anchor
      // attempt on this load, so they are built at most once each.
      const wordCache = new Map();
      data.forEach(hl => {
        if (hl.unreadable) return;
        // Where this mark goes — which block list its anchor is measured against
        // and which bucket the decoded range belongs in — and whether the hints
        // needed correcting to get there. See placeStoredHighlight.
        const placed = placeStoredHighlight(pages, hl, wordCache);
        const { scope, global } = placed;
        // The whole API row travels with the heal, not just its id: the
        // write-back has to re-state the content it is NOT correcting (see
        // below), and this is the only place that still holds it.
        if (placed.healed) healed.push({ row: hl, scope, global });
        if (placed.unverified) unverified += 1;
        if (!scope || !global) {
          unanchored += 1;
          return;
        }
        if (!byPage[scope.scopeKey]) byPage[scope.scopeKey] = [];
        // Highlights embed a notes[] array (1..many); map each note to the
        // drawer's shape.
        const notes = (hl.notes || []).map(mapApiNote).filter(Boolean);
        byPage[scope.scopeKey].push(
          withNotes({
            id: hl.id,
            // The bucket this mark was filed under, which is the page id for
            // lesson prose and a story scope key for a story. Round-tripped as
            // `pageId` because that is the key every highlight mutation reads.
            pageId: scope.scopeKey,
            blockId: hl.blockId,
            startWordIndex: global.start,
            endWordIndex: global.end,
            color: enumToHex(hl.color),
            text: hl.selectedText,
            // Carried on the mapped row so a later PATCH can re-state the
            // whole content set of spec section 6.4. An envelope REPLACES the
            // row's payload column, and the reader's callers only ever hand
            // back a colour and a span, so without these a recolour would
            // destroy the page index and the quote's context.
            pageIndex: hl.pageIndex ?? 0,
            contextBefore: hl.contextBefore ?? null,
            contextAfter: hl.contextAfter ?? null,
            notes,
          }),
        );
      });
      // Best effort, and deliberately after the reader has its highlights: a failed
      // heal costs another search on the next load and nothing else, so it must not
      // hold up painting or surface as an error.
      if (healed.length) {
        healed.forEach(({ row, scope, global }) => {
          const blockInfo = globalToBlock(global.start, global.end, scope.blocks);
          if (!blockInfo) return;
          annotationsTransport
            .updateHighlight(auth, row.id, {
              // The corrected hints, which is what the heal is for.
              blockId: blockInfo.blockId,
              endBlockId: blockInfo.endBlockId,
              startWordIndex: blockInfo.startWordIndex,
              endWordIndex: blockInfo.endWordIndex,
              // The page the quote was actually found on, which a heal can move.
              pageIndex: pageIndexOf(pages, scope, row),
              // ...and the rest of the row, unchanged. Once positions live in an
              // envelope a PATCH REPLACES the payload column, so a four-field
              // write-back would destroy the quote, its context and the colour —
              // and the quote is the only thing that can ever re-find this mark
              // again (spec section 6.4, E2EE launch gate #1554). Re-stating
              // what the row already holds costs a plaintext member nothing.
              selectedText: row.selectedText,
              contextBefore: row.contextBefore ?? null,
              contextAfter: row.contextAfter ?? null,
              color: row.color,
            })
            .catch(error => {
              // Non-blocking: the highlight is already on screen. #1584 gives
              // this one a reason to be loud in the log though — a stale
              // revision here means another device re-anchored the same mark
              // first, so THIS heal is the one that is out of date and must not
              // be retried over it. Dropping the revision leaves the next read
              // to record the winner's.
              if (isStaleRevisionError(error)) {
                onRevisionConflict('highlight', row.id);
                console.info(
                  `[Highlights] re-anchor of ${row.id} lost to another device; keeping theirs.`,
                );
              }
            });
        });
        console.info(
          `[Highlights] re-anchored ${healed.length} highlight(s) on ${slug} by their quoted text; the blocks they were saved against are gone.`,
        );
      }
      // Rows this device could not open never entered the anchor search, so
      // neither count is measured against them.
      const anchorable = data.filter(hl => !hl.unreadable).length;
      if (unanchored) {
        console.warn(
          `[Highlights] ${unanchored} of ${anchorable} highlight(s) on ${slug} (${audienceView} view) could not be anchored to the content and are not shown. The blocks they point at are gone — usually a lesson re-authored or re-seeded since they were made.`,
        );
      }
      if (unverified) {
        console.warn(
          `[Highlights] ${unverified} of ${anchorable} highlight(s) on ${slug} (${audienceView} view) no longer read as their quoted text and the quote was not found elsewhere in the lesson; shown where their saved position points.`,
        );
      }
      Object.entries(byPage).forEach(([pageId, highlights]) => {
        commit('setPageHighlights', { pageId, highlights });
      });
      // Refresh the read-cache with the latest API data (never a write path),
      // unless the member's marks must not sit on the device in the clear.
      if (!state.encryptedMember) saveHighlightsToStorage(audienceView, byPage);
      return byPage;
    } catch (error) {
      // Hydrate from the read-cache so the UI can paint last-known data while
      // the API is unreachable. This is a read-only cache — never written to
      // as a fallback for failed writes.
      const stored = loadHighlightsFromStorage(audienceView);
      Object.entries(stored).forEach(([pageId, highlights]) => {
        commit('setPageHighlights', { pageId, highlights });
      });
      return stored;
    }
  },

  // One write per highlight at a time (#1814). Guard the WRITE, never the button:
  // a second tap on the same selection while its POST is out joins that request
  // instead of creating a twin, and a second recolour of the same mark while its
  // PATCH is out joins it instead of earning a stale-revision 409 the member never
  // caused. Different selections still save independently. The body is
  // `runSaveHighlight` below the helpers.
  saveHighlight(ctx, highlight) {
    const key = highlight.id
      ? `id:${highlight.id}`
      : `span:${highlight.pageId}|${highlight.startWordIndex}|${highlight.endWordIndex}|${ctx.state.currentLessonView}`;
    // What makes two writes to the same key "the same write". A create is: a
    // twin of a span is never wanted, whatever colour the second tap carried. A
    // recolour is not: yellow → green → pink in quick succession are three
    // different writes, and joining pink onto green's PATCH would leave the
    // server on green while the screen shows pink. So an identical recolour
    // joins; a different one waits for the one in flight to land, then sends —
    // which also stops the two from racing to a stale-revision 409.
    const signature = highlight.id
      ? `${highlight.color}|${highlight.startWordIndex}|${highlight.endWordIndex}`
      : 'create';
    const running = highlightWritesInFlight.get(key);
    if (running && running.signature === signature) return running.promise;
    const promise = (running ? running.promise.catch(() => {}) : Promise.resolve())
      .then(() => runSaveHighlight(ctx, highlight))
      .finally(() => {
        if (highlightWritesInFlight.get(key)?.promise === promise) {
          highlightWritesInFlight.delete(key);
        }
      });
    highlightWritesInFlight.set(key, { signature, promise });
    return promise;
  },

  // Legacy single-note path retained for older callers. API source-of-truth; no
  // localStorage write-fallback. The multi-note in-lesson drawer uses
  // addNote/editNote/deleteNote instead.
  async saveHighlightNote({ commit, state, rootState }, { highlightId, note, lessonSlug, noteId }) {
    const hasNoteContent = !isEmptyNoteBody(note);
    if (!hasNoteContent && !noteId) return;
    try {
      const auth = annotationAuth(rootState);

      let resultNoteId = noteId || null;
      let resultBody = null;
      let resultText = '';

      if (!hasNoteContent && noteId) {
        // Delete existing note when cleared
        await annotationsTransport.deleteNote(auth, noteId);
        resultNoteId = null;
      } else if (hasNoteContent && noteId) {
        // Update existing note
        resultBody = buildNoteBody(note);
        resultText = extractNotePlainText(resultBody);
        await annotationsTransport.updateNote(auth, noteId, { body: resultBody });
      } else if (hasNoteContent && !noteId) {
        // Create new note
        resultBody = buildNoteBody(note);
        resultText = extractNotePlainText(resultBody);
        const data = await annotationsTransport.createNote(auth, {
          programSlug: state.currentProgramSlug || 'next-steps-p1',
          lessonSlug,
          highlightId,
          body: resultBody,
          view: state.currentLessonView,
        });
        resultNoteId = data.id;
      }

      // Keep the highlight's notes[] aligned.
      if (resultNoteId) {
        commit('setHighlightNotes', {
          highlightId,
          notes: [
            { id: resultNoteId, body: resultBody, text: resultText, tags: [], tagObjects: [] },
          ],
        });
      } else {
        commit('setHighlightNotes', { highlightId, notes: [] });
      }
    } catch (error) {
      // A sealing refusal was never sent and will not be retried: the caller
      // has to hear about it. A failed request stays non-blocking, as before.
      if (isSealedWriteError(error)) throw error;
      /* non-blocking */
    }
  },

  // ── Multi-note in-lesson drawer actions (optimistic + API source-of-truth) ─
  // add a note to a highlight: insert a temp note → POST → swap in the server
  // id; remove temp + rethrow on error so the caller can toast.
  async addNote({ commit, state, rootState }, { highlightId, lessonSlug, content }) {
    const tempId = newTempNoteId();
    const body = buildNoteBody(content);
    const text = extractNotePlainText(body);
    commit('addHighlightNote', {
      highlightId,
      note: { id: tempId, body, text, tags: [], tagObjects: [] },
    });
    try {
      const data = await annotationsTransport.createNote(annotationAuth(rootState), {
        programSlug: state.currentProgramSlug || 'next-steps-p1',
        lessonSlug: lessonSlug || state.lessonSlug,
        highlightId,
        body,
        // Ignored by the server for an anchored note (it takes the
        // highlight's view), sent so every note write says the same thing.
        view: state.currentLessonView,
      });
      const mapped = mapApiNote({ ...data, body: data.body || body }) || {
        id: data.id,
        body,
        text,
        tags: [],
        tagObjects: [],
      };
      // Whatever the member typed while the create was in flight landed on the
      // temp row (editNote keeps those edits local). Read it before the swap.
      const typed = findHighlightNote(state, highlightId, tempId)?.body;
      const caughtUp = typed !== undefined && !sameNoteBody(typed, body);
      commit('replaceHighlightNoteId', {
        highlightId,
        tempId,
        note: caughtUp ? { ...mapped, body: typed, text: extractNotePlainText(typed) } : mapped,
      });
      // Queued, not PATCHed directly — see `queueNoteBody`. A failure is the
      // engine's to retry and report; the create itself stands either way.
      if (caughtUp) queueNoteBody(rootState, mapped.id, typed, { highlightId });
      return mapped;
    } catch (error) {
      commit('removeHighlightNote', { highlightId, noteId: tempId });
      commit('setError', error.response?.data?.message || error.message || 'Failed to add note');
      throw error;
    }
  },

  // edit a note's body: snapshot → apply locally → PATCH; revert on error.
  editNote({ commit, state, rootState }, { highlightId, noteId: rawNoteId, content }) {
    const noteId = resolveNoteId(state, rawNoteId);
    const body = buildNoteBody(content);
    const text = extractNotePlainText(body);
    commit('updateHighlightNote', { highlightId, noteId, patch: { body, text } });
    // A placeholder id (drawer pending slide, in-flight temp row, or no id at
    // all) has no server row to PATCH; the edit stays local and addNote carries
    // it across once the create lands.
    if (!isPersistedNoteId(noteId)) return;
    // Rule 1: the commit above has already happened, and only the request is
    // deferred. Rule 2: no rollback on failure — with edits coalesced there is
    // no single "prior" body to restore (a burst of keystrokes has many), and
    // throwing away what the member typed to agree with a server that will be
    // caught up in seconds is the wrong trade. `courses-autosave` reports the
    // failure instead; see `onAutosaveFailure`.
    queueNoteBody(rootState, noteId, body, { highlightId });
  },

  // delete a note: remove locally → DELETE; re-insert on error.
  async deleteNote({ commit, state, rootState }, { highlightId, noteId: rawNoteId }) {
    const noteId = resolveNoteId(state, rawNoteId);
    // Nothing queued for a row about to stop existing.
    autosave.cancel(noteKey(noteId));
    const prior = findHighlightNote(state, highlightId, noteId);
    commit('removeHighlightNote', { highlightId, noteId });
    // Nothing on the server to delete for a placeholder; the local removal is it.
    if (!isPersistedNoteId(noteId)) return;
    try {
      await annotationsTransport.deleteNote(annotationAuth(rootState), noteId);
    } catch (error) {
      if (prior) commit('addHighlightNote', { highlightId, note: prior });
      commit('setError', error.response?.data?.message || error.message || 'Failed to delete note');
      throw error;
    }
  },

  // add a tag to a note: apply locally → POST; revert on error.
  async addNoteTag({ commit, state, rootState }, { highlightId, noteId, tag }) {
    const prior = findHighlightNote(state, highlightId, noteId);
    if (!prior) return;
    const tagObjects = [...(prior.tagObjects || []).filter(t => t.id !== tag.id), tag];
    commit('updateHighlightNote', {
      highlightId,
      noteId,
      patch: { tags: tagObjects.map(t => t.id), tagObjects },
    });
    try {
      await annotationsTransport.attachNoteTag(annotationAuth(rootState), noteId, tag.id);
    } catch (error) {
      commit('updateHighlightNote', {
        highlightId,
        noteId,
        patch: { tags: prior.tags || [], tagObjects: prior.tagObjects || [] },
      });
      commit('setError', error.response?.data?.message || error.message || 'Failed to add tag');
      throw error;
    }
  },

  // remove a tag from a note: apply locally → DELETE; revert on error.
  async removeNoteTag({ commit, state, rootState }, { highlightId, noteId, tagId }) {
    const prior = findHighlightNote(state, highlightId, noteId);
    if (!prior) return;
    const tagObjects = (prior.tagObjects || []).filter(t => t.id !== tagId);
    commit('updateHighlightNote', {
      highlightId,
      noteId,
      patch: { tags: tagObjects.map(t => t.id), tagObjects },
    });
    try {
      await annotationsTransport.detachNoteTag(annotationAuth(rootState), noteId, tagId);
    } catch (error) {
      commit('updateHighlightNote', {
        highlightId,
        noteId,
        patch: { tags: prior.tags || [], tagObjects: prior.tagObjects || [] },
      });
      commit('setError', error.response?.data?.message || error.message || 'Failed to remove tag');
      throw error;
    }
  },

  // ── Tag list actions (the member's tags, across every part and guide) ─────
  //
  // Unscoped on purpose. A tag is "this name, for this member" — not "…in this
  // part" — so every caller gets the same one list and nothing has to decide
  // which part it is asking on behalf of (review 2026-09-10, ONE-8528). The
  // lesson reader already fetched it this way; the hub and the story page used
  // to disagree with it, which is why a member saw a different tag list
  // depending on where they opened the picker.
  async fetchTags({ commit, state, rootState }) {
    try {
      const data = await annotationsTransport.fetchTags(annotationAuth(rootState), {});
      commit('setTags', data || []);
      recordUnreadable(commit, 'tag', data);
      return data;
    } catch (error) {
      // Keep the read-cached tag list already loaded in state.
      return state.tags;
    }
  },

  // create a personal tag via the API, then add to the local list.
  async createTag({ commit, dispatch, rootState }, { name, color, program }) {
    try {
      // `programSlug` is still sent, and the API ignores it: it records where the
      // tag was created and nothing reads it. It stays until no bundle in the
      // fleet predates the API change — an older API REQUIRES the field and
      // answers 400 without it, which would break tag creation everywhere.
      const data = await annotationsTransport.createTag(annotationAuth(rootState), {
        name,
        color,
        programSlug: program,
      });
      commit('addTag', { id: data.id, name: data.name, color: data.color });
      return data;
    } catch (error) {
      // A duplicate name is now usually the SERVER's to catch, not the device's:
      // with one member-wide list the colliding tag is often in a part this
      // screen never loaded, or one this device cannot decrypt. Refetch so it is
      // there to select, and reject with the name so every creation surface can
      // say the same sentence instead of "Could not create tag".
      if (isTagNameConflict(error)) {
        dispatch('fetchTags').catch(() => {});
        throw new TagNameConflictError(name, error);
      }
      commit('setError', error.response?.data?.message || error.message || 'Failed to create tag');
      throw error;
    }
  },

  // rename a personal tag via the API, then update the local list.
  async updateTag({ commit, state, rootState }, { tagId, name, color }) {
    try {
      // Both callers — the Hub's rename and the reader's tag manager — pass a
      // new name and nothing else, and a tag envelope REPLACES the row's
      // payload column, so the colour has to travel with the rename or it is
      // destroyed (spec section 6.4). The store holds every tag, so the
      // unchanged half is read back from there rather than asked of the caller.
      const stored = (state.tags || []).find(tag => tag.id === tagId) || null;
      // #1588: state the absent colour as `null` rather than letting it go out
      // as `undefined`. The tag manager creates a tag from a name alone, so the
      // cached row carries `color: undefined` — and the sealing hook drops
      // `undefined` BEFORE it counts the content fields, so every rename of
      // such a tag was refused as PARTIAL_PATCH and no rename ever succeeded.
      // `null` is kept by that same split, and it is the truth: the row has no
      // colour.
      //
      // Fixed on the update path, not at creation. A default colour at creation
      // would invent one the member never picked, and it would not help a tag
      // an older build already wrote colourless — the migration (#1545)
      // re-writes rows as they stand, so it does not heal them either. This
      // path has to cope with a colourless tag for ever, so this is where it
      // copes.
      //
      // With NO cached row, the colour stays `undefined` on purpose: nothing
      // here knows whether the server holds one, and sealing a guessed `null`
      // would destroy it. The hook refusing that rename is the safe answer.
      const storedColor = stored ? (stored.color ?? null) : undefined;
      const data = await annotationsTransport.updateTag(annotationAuth(rootState), tagId, {
        name: name ?? stored?.name,
        color: color ?? storedColor,
      });
      if (name !== undefined) commit('renameTag', { tagId, newName: data.name ?? name });
      return data;
    } catch (error) {
      // #1584. Another device replaced this tag between the read this rename
      // re-stated from and the write. NOT replayed: the envelope carries the
      // whole content set, so sending it again with a fresh revision would
      // destroy whatever that device wrote. Forget the number, say so, and
      // leave the member to reopen the tag.
      if (isStaleRevisionError(error)) {
        onRevisionConflict('tag', tagId);
        commit('setError', REVISION_CONFLICT_MESSAGE);
        throw error;
      }
      commit('setError', error.response?.data?.message || error.message || 'Failed to update tag');
      throw error;
    }
  },

  // delete a personal tag via the API, then strip it from the local list +
  // every note-tag association (the deleteTag mutation handles the latter).
  async deleteTag({ commit, rootState }, tagId) {
    try {
      await annotationsTransport.deleteTag(annotationAuth(rootState), tagId);
      commit('deleteTag', tagId);
    } catch (error) {
      commit('setError', error.response?.data?.message || error.message || 'Failed to delete tag');
      throw error;
    }
  },

  // How many notes carry each of these tags, and where — by part and by guide.
  //
  // Not stored: the tag list is read on every screen open, these counts only
  // when a delete confirmation opens or a filter comes back empty. Returns an
  // empty map on failure, which every caller treats as "say nothing extra"
  // rather than as an error: a count is an explanation, never a gate.
  async fetchTagUsage({ rootState }, tagIds) {
    try {
      return await annotationsTransport.fetchTagUsage(annotationAuth(rootState), tagIds);
    } catch {
      return {};
    }
  },

  // Undo of deleteTag, inside the server's restore window. Throws on 410/409 so
  // the toast can say the undo has expired rather than silently doing nothing.
  async restoreTag({ commit, rootState }, tagId) {
    const restored = await annotationsTransport.restoreTag(annotationAuth(rootState), tagId);
    commit('restoreTag', restored);
    return restored.tag;
  },

  // ── Notes Hub cross-lesson loaders (API source-of-truth) ──────────────────
  // Load every highlight (each embeds its notes[]) for the hub's Highlights
  // tab, grouped into the page-keyed `highlights` map the rest of the store
  // uses. Block→global word-index conversion needs the lessons loaded first;
  // highlights for unknown lessons are skipped (the hub only shows loaded
  // lessons anyway). Hydrates the read-cache on failure.
  // Cross-lesson highlights for the hub. The hub does NOT load lesson pages, so we
  // store the flat API rows (grouped by lessonSlug via the allHighlightsByLesson
  // getter) and display selectedText directly — no page/block-offset resolution
  // (that's only needed by the in-lesson reader's fetchLessonHighlights). localStorage
  // is a read-cache only.
  async fetchAllHighlights({ commit, state, rootState }) {
    try {
      const data = await annotationsTransport.fetchAllHighlights(annotationAuth(rootState), {
        view: state.view,
      });
      const highlights = (data || []).map(hl => ({
        id: hl.id,
        lessonSlug: hl.lessonSlug,
        // Carried so the hub can scope highlights to one Next Steps part client-side
        // (unlike notes, GET /highlights is unpaginated, so no server filter is needed).
        programSlug: hl.programSlug,
        pageIndex: hl.pageIndex,
        blockId: hl.blockId,
        color: enumToHex(hl.color),
        text: hl.selectedText,
        // A row this device could not open has no quote; the hub shows why instead.
        ...(hl.unreadable ? { unreadable: hl.unreadable } : {}),
      }));
      recordUnreadable(commit, 'highlight', data);
      commit('setAllHighlights', highlights);
      return highlights;
    } catch (error) {
      // Read-cache hydrate (setAllHighlights already restored from cache on init).
      return state.allHighlights;
    }
  },

  // Load the flat cross-lesson note list (standalone + highlight-attached) for
  // the hub's Notes tab. Paginated `{ data, total }`; `lessonSlug`/`tagIds`
  // narrow server-side. Search runs on the device (notes-hub/index.vue,
  // filteredNotes), so no term is ever sent. Replaces the read-cache with
  // fresh API data; hydrates from cache on failure, flagged `failed: true`.
  // Pagination: pass `page` (1-based) with `append: true` to add the next page
  // to the list (infinite scroll); page 1 (or no `append`) replaces the list.
  // `notesTotal` is the server-side count the hub uses to know when to stop.
  // NOTE: tag filtering is done CLIENT-SIDE (see filteredNotes / OR semantics),
  // so `tagIds` is not passed by the hub today; it's wired to the API's
  // GET /notes?tagIds= contract for when server-side filtering is turned on.
  async fetchAllNotes({ commit, state, rootState }, params = {}) {
    // Claim this request's place in line before it goes out. A part switch
    // refetches page 1 while a page of the part just left may still be open;
    // that response would otherwise append its rows to the new part's list and
    // replace its `total`, leaving both inputs to `hasMoreNotes` wrong.
    commit('bumpNotesEpoch');
    const epoch = state.notesEpoch;
    try {
      // The hub shows one view at a time; `total` comes back scoped to it too,
      // so the tab count cannot claim notes the list will not show.
      const query = { view: state.view };
      if (params.lessonSlug) query.lessonSlug = params.lessonSlug;
      if (params.tagIds && params.tagIds.length) query.tagIds = params.tagIds;
      if (params.page) query.page = params.page;
      // Scope the hub to one Next Steps part; the server scopes `total` with it.
      if (params.program) query.program = params.program;
      const data = await annotationsTransport.fetchAllNotes(annotationAuth(rootState), query);
      // Two shapes, one list. A plaintext member gets `{ data, total }` paged by
      // `page`; an ENROLLED member gets `{ items, nextCursor, total }` paged by
      // an opaque cursor (encryption API contract, section 3).
      //
      // The Hub no longer comes here when enrolled — it drains the corpus
      // instead (#1548 part 2) — but `adoptWrappedKey` still does: a device that
      // has just been approved or has just entered its recovery code refills the
      // Hub through this action while enrolled, and reading only `data` would
      // leave it empty at exactly the moment the member got their notes back.
      // `adopt-unlocks-the-hub.spec.js` is what pins that.
      //
      // So this stays until that path drains too. `nextCursor` is still not
      // threaded here: one page of 200 is what the adopt flow needs to paint,
      // and the drain that follows on the next Hub open completes the corpus.
      const rows = data?.data || data?.items || [];
      const list = rows.map(mapApiNote).filter(Boolean);
      // Overtaken while in flight: the list on screen belongs to a newer
      // request, so commit nothing and say so. Callers treat it as nothing
      // landing rather than as a page to retry — there is nothing wrong with
      // the page, it is just no longer wanted.
      if (state.notesEpoch !== epoch) {
        return { data: state.allNotes, total: state.notesTotal, stale: true };
      }
      commit(params.append ? 'appendAllNotes' : 'setAllNotes', list);
      commit('setNotesTotal', data?.total ?? list.length);
      recordUnreadable(commit, 'note', list, { append: params.append });
      return { data: list, total: data?.total ?? list.length };
    } catch (error) {
      // Keep the read-cached notes already in state so the hub paints
      // last-known data while the API is unreachable. The flag is what lets
      // the hub tell a failed page from an empty one when it drains.
      return { data: state.allNotes, total: state.notesTotal, failed: true };
    }
  },

  /**
   * Paint the hub from the member's own IndexedDB corpus (#1548 part 2).
   *
   * The enrolled replacement for `fetchAllNotes`'s page walk: rows are held on
   * the device as the envelopes the server sent, and opened here, once, into
   * live Vuex state. Nothing decrypted is written back to disk.
   *
   * `notesEpoch` is shared with `fetchAllNotes` on purpose — a part switch must
   * be able to supersede a corpus paint and a page fetch alike.
   *
   * A row that will not open is kept, not dropped: `recordUnreadable` is what
   * puts the "update the app" and "approve this device" states on screen, and a
   * corpus full of rows this build cannot read is exactly when a member needs
   * to be told so.
   */
  async paintNotesFromCorpus({ commit, state, rootState }, { scope, total } = {}) {
    commit('bumpNotesEpoch');
    const epoch = state.notesEpoch;
    const memberId = rootState['courses-keys']?.memberId ?? null;
    if (memberId === null || !scope) return { data: state.allNotes, total: state.notesTotal };
    try {
      const auth = annotationAuth(rootState);
      const stored = await notesCorpus.listNotes(memberId, scope);
      const opened = await Promise.all(
        stored.map(row => annotationsTransport.openNoteRow(auth, row)),
      );
      const list = opened.map(mapApiNote).filter(Boolean);
      if (state.notesEpoch !== epoch) {
        return { data: state.allNotes, total: state.notesTotal, stale: true };
      }
      commit('setAllNotes', list);
      // The scope's server-side count when the drain has one; the rows in hand
      // otherwise, so the tab count never claims notes the list will not show.
      commit('setNotesTotal', typeof total === 'number' ? total : list.length);
      recordUnreadable(commit, 'note', list, { append: false });
      return { data: list, total: state.notesTotal };
    } catch (error) {
      // No IndexedDB, or a read that failed: the hub keeps whatever it holds and
      // the caller decides whether to fall back to the page walk.
      return { data: state.allNotes, total: state.notesTotal, failed: true };
    }
  },

  /**
   * Walk the cursor to the end of a scope, writing envelopes into the corpus
   * (#1548 part 2). The module owns the cursor, the atomicity of a page and its
   * cursor advance, and the resume; this action owns the transport calls, so the
   * Hub needs neither the auth nor the seam.
   *
   * `reconcileFirst` is a later open: drop what the member deleted on another
   * device, refetch what changed there, and only then drain — reconcile skips
   * ids it has never seen until the scope is complete, so the order matters.
   * A failed reconcile is not fatal: an unreconciled corpus is stale, not wrong,
   * and the drain that follows reports the truth through `complete`.
   */
  async drainNotesCorpus(
    { state, rootState },
    { scope, program, reconcileFirst = false, onProgress } = {},
  ) {
    const memberId = rootState['courses-keys']?.memberId ?? null;
    if (memberId === null || !scope) return { ok: false, reason: 'storage' };
    const auth = annotationAuth(rootState);
    const view = state.view;

    if (reconcileFirst) {
      try {
        const manifest = await annotationsTransport.fetchNotesManifest(auth, { program });
        await notesCorpus.reconcile(memberId, manifest, {
          scope,
          fetchById: id => annotationsTransport.fetchNoteByIdRaw(auth, id),
        });
      } catch (error) {
        // Left to the drain to notice and to `complete` to report.
      }
    }

    return notesCorpus.drain(memberId, scope, {
      fetchPage: ({ cursor }) =>
        annotationsTransport.fetchNotesPageRaw(auth, { cursor, view, program }),
      onProgress,
    });
  },

  /**
   * Keep the corpus level with a write the member just made (#1548 part-2 step 5).
   *
   * Deliberately a REFETCH rather than a put of what the write returned: the
   * transport opens a response on its way through, so the row the store holds is
   * plaintext, and the corpus may only ever hold envelopes. `fetchNoteByIdRaw`
   * skips the response hook, so what lands on disk is exactly what the server
   * holds. One extra GET per write, on a path that already did one.
   *
   * Failure is deliberately silent: the write itself succeeded, the row is in
   * `allNotes`, and the next reconcile picks it up. A toast here would be about
   * a cache, not about the member's note.
   */
  async syncNoteToCorpus({ rootState }, { id, program, row: written } = {}) {
    const memberId = rootState['courses-keys']?.memberId ?? null;
    if (!id || memberId === null || !rootState['courses-keys']?.mk) return { ok: false };
    try {
      // `written` is the sealed row a write handed back through its `onRawRow`
      // hook, and it is the SAME row a fetch would return — the API answers a
      // POST and an encrypted PATCH from `findOne`, which is what
      // `GET /notes/:id` is. So re-fetching was a second request for something
      // already in hand, on every keystroke of a Notes Hub edit (item 1a of the
      // write-volume plan).
      //
      // It has to be the RAW row, not the write's return value: that one has
      // been through the response hook and is plaintext (see `updateNote`). The
      // fetch stays for callers with no row of their own — reconciliation, and
      // any write whose response was not captured.
      const row =
        written ?? (await annotationsTransport.fetchNoteByIdRaw(annotationAuth(rootState), id));
      if (!row) {
        await notesCorpus.deleteNotes(memberId, [id]);
        return { ok: true, dropped: true };
      }
      await notesCorpus.putNote(memberId, row, noteScopeOf(row, { program }));
      return { ok: true };
    } catch (error) {
      return { ok: false };
    }
  },

  /** The member deleted a note; forget it here too rather than at next reconcile. */
  async forgetNoteInCorpus({ rootState }, { id } = {}) {
    const memberId = rootState['courses-keys']?.memberId ?? null;
    if (!id || memberId === null || !rootState['courses-keys']?.mk) return { ok: false };
    try {
      await notesCorpus.deleteNotes(memberId, [id]);
      return { ok: true };
    } catch (error) {
      return { ok: false };
    }
  },

  // ── Notes Hub note CRUD (optimistic + API source-of-truth) ────────────────
  /**
   * Add a hub note. A note always belongs to a lesson, so it is POSTed with the
   * lessonSlug the member picked; `highlightId` is null for a general
   * (standalone) note and set for another note on an existing highlight.
   *
   * The row is in `allNotes` the moment this is dispatched, under `tempId`
   * (the caller's, or a fresh one) and flagged `pendingCreate` until the server
   * has it — the same model as the drawer's placeholder slide, so a card can
   * open its editor on the row at once and every keystroke is an ordinary
   * `editStandaloneNote` (plan 9.6, revised 2026-09-16).
   *
   * THE POST WAITS FOR CONTENT. With content it goes now; without, the row is a
   * store-only draft and its first non-empty keystroke sends it — no empty note
   * ever reaches the server from here, and a + tapped and abandoned leaves
   * nothing behind once its editor closes (`settleNoteEditorClosed`). What a
   * failed create means is `createNoteRow`'s; the dialog's create surfaces it
   * at once, a card's keeps the row for the retry on close.
   */
  async addStandaloneNote(
    ctx,
    {
      tempId = newTempNoteId(),
      lessonSlug,
      content,
      tagIds,
      highlightId = null,
      program,
      retainOnFailure = false,
    },
  ) {
    const { commit, state } = ctx;
    const body = buildNoteBody(content);
    const text = extractNotePlainText(body);
    commit('addNoteToAll', {
      id: tempId,
      body,
      text,
      content: text,
      lessonSlug,
      highlightId,
      // Recorded on the row so the deferred create can send the right part
      // without its caller; also what the Hub's part filter reads.
      programSlug: program || state.currentProgramSlug || 'next-steps-p1',
      tags: tagIds && tagIds.length ? tagIds : [],
      tagObjects: [],
      createdAt: new Date(),
      pendingCreate: true,
    });
    if (isEmptyNoteBody(body)) return state.allNotes.find(n => n.id === tempId) ?? null;
    return createNoteRow(ctx, tempId, { retainOnFailure });
  },

  /**
   * The member has closed the editor on a note — Done, a tap outside, paging
   * away, or leaving the Hub. For a row the server already has this is nothing:
   * the card flushes the queue itself. For a row it has NOT — a new note whose
   * create is still to come — this is the moment it is decided:
   *
   *   - empty, and no request out for it: removed. A + tapped and abandoned.
   *   - with content, and its create failed: tried again, with the row as it is
   *     now. Nothing retries a create on a timer (plan 9.6), so this is the one
   *     retry point, and the honest shape of "creates are not queued".
   *   - with a create in the air: left to it — `postStandaloneNote` carries
   *     what was typed since, or takes the note down if the row has gone.
   */
  settleNoteEditorClosed(ctx, { noteId: rawNoteId }) {
    const { commit, state } = ctx;
    const noteId = resolveNoteId(state, rawNoteId);
    const row = state.allNotes.find(n => n.id === noteId);
    if (!row?.pendingCreate || noteCreatesInFlight.has(noteId)) return Promise.resolve(null);
    if (isEmptyNoteBody(row.body)) {
      commit('removeNoteFromAll', noteId);
      commit('courses-autosave/clearFailed', noteKey(noteId), { root: true });
      return Promise.resolve(null);
    }
    return createNoteRow(ctx, noteId, { retainOnFailure: true });
  },

  /**
   * Drop every empty new-note row nobody is editing. What a kill or a crash
   * leaves behind: a + was tapped, nothing typed, and the editor never closed
   * — so `settleNoteEditorClosed` never ran, and `setAllNotes` keeps
   * `pendingCreate` rows across the refetch on purpose. The Hub calls this on
   * open, before it paints. A row with an editor open on it is the member's.
   */
  discardEmptyNoteDrafts({ commit, state }) {
    (state.allNotes || [])
      .filter(
        n =>
          n?.pendingCreate &&
          isEmptyNoteBody(n.body) &&
          !noteCreatesInFlight.has(n.id) &&
          !state.openNoteIds.includes(n.id),
      )
      .forEach(n => {
        commit('removeNoteFromAll', n.id);
        commit('courses-autosave/clearFailed', noteKey(n.id), { root: true });
      });
  },

  // Edit a note's body: snapshot → apply locally → PATCH; revert on error.
  editStandaloneNote(ctx, { noteId: rawNoteId, content }) {
    const { commit, state, rootState } = ctx;
    const noteId = resolveNoteId(state, rawNoteId);
    const body = buildNoteBody(content);
    const text = extractNotePlainText(body);
    commit('updateNoteInAll', { noteId, patch: { body, text, content: text } });
    if (!isPersistedNoteId(noteId)) {
      // A NEW NOTE'S FIRST KEYSTROKE WITH CONTENT IS ITS CREATE (plan 9.6). The
      // row exists only here until then; from this request on it is a server
      // row and every later keystroke is the queued edit below — the ones typed
      // while the POST is out ride in on `postStandaloneNote`'s catch-up. Not
      // after a failure: that retries when the editor closes, or offline would
      // be a failed request per keystroke. Not awaited — the commit above has
      // already happened, and the failure is the row's to show.
      const row = state.allNotes.find(n => n.id === noteId);
      if (row?.pendingCreate && !row.createFailed && !isEmptyNoteBody(body)) {
        createNoteRow(ctx, noteId, { retainOnFailure: true }).catch(() => {});
      }
      return;
    }
    // Same two rules as `editNote`. The Hub had no coalescing at all before
    // this: every keystroke was a PATCH and a corpus GET.
    queueNoteBody(rootState, noteId, body, { standalone: true });
  },

  /**
   * The autosave engine's note sender: the transport half of the two actions
   * above, run once per coalesced burst. Registered in `installAutosave`, not
   * called from a component.
   *
   * Rethrows, because the engine classifies the failure and tells its listeners.
   */
  async writeNoteBody({ dispatch, rootState }, { noteId, sealed }) {
    let writtenRow = null;
    const row = await annotationsTransport.sendSealed(annotationAuth(rootState), {
      type: annotationsTransport.ANNOTATION_TYPE.NOTE,
      itemId: noteId,
      sealed,
      onRawRow: raw => (writtenRow = raw),
    });
    // The corpus keeps the SEALED row (item 1a) — never the opened one above.
    dispatch('syncNoteToCorpus', { id: noteId, row: writtenRow }).catch(() => {});
    return row;
  },

  /**
   * A row the member is typing in was refused because another device wrote
   * first. The member's version wins.
   *
   * THE ACTIVE DEVICE WINS, AND NOTHING IS SHOWN. It is the member's own text on
   * their own devices, and the winner is deterministic — the one they are
   * looking at — so there is nothing here for them to decide, the way a message
   * edited on two clients simply shows its latest version. The counter is how we
   * would learn that this is commoner than it looks.
   *
   * The refetch is for the REVISION, not the body: `onRevisionConflict` has just
   * forgotten the stale number, and without a fresh one the next send is refused
   * before it leaves (`NO_REVISION`). Committing the server's body would be
   * actively wrong — it would put the other device's words into the editor the
   * member is typing in — so both refetches are `recordOnly`.
   *
   * What goes up on the retry is the slot's CURRENT body, not the refused one.
   * The engine parks a newer body while a send is in the air and promotes it on
   * failure, so `retryNow(key)` sends whatever they have typed since.
   */
  async forceThroughConflict({ dispatch }, { type, itemId, meta }) {
    countAutosaveEvent(AUTOSAVE_EVENT.CONFLICT_FORCED);
    onRevisionConflict(type === AUTOSAVE_TYPE.NOTE ? 'note' : 'blank', itemId);
    if (type === AUTOSAVE_TYPE.NOTE) {
      let fresh;
      try {
        fresh = await dispatch('refreshNoteFromServer', { noteId: itemId, recordOnly: true });
      } catch (error) {
        // THE REFETCH FAILED, NOT THE ROW. No network, a 5xx: the row is very
        // likely still there and the words are still in the slot. This used to
        // read as "gone" and mark the note unsavable — a blip during a conflict
        // ended the note's saving for the session. Send it anyway: with the
        // revision forgotten the hook refuses it `NO_REVISION`, which is a
        // counted retry, and `onAutosaveFailure` re-reads the revision on each
        // of those, so the attempt after the network returns carries a fresh
        // number. A row that genuinely cannot be read parks after the cap.
        autosave.retryNow(noteKey(itemId));
        return;
      }
      // Gone from the server entirely (the read answered 404): deleted on the
      // other device rather than edited. Nothing we send revives it, and the
      // engine stops asking.
      if (fresh === null) {
        autosave.markUnsavable(noteKey(itemId));
        return;
      }
      autosave.retryNow(noteKey(itemId));
      return;
    }
    // A blank has no row id to read, so its lesson is refetched instead; the
    // open hook records the revision of every row it passes. A blank the other
    // device CLEARED leaves no row and so no revision, and the resend goes up
    // unconditional — which recreates it, the right answer for a member typing
    // into a blank someone else emptied.
    await dispatch('fetchBlankAnswers', {
      slug: meta?.lessonSlug,
      recordOnly: true,
    }).catch(() => {});
    autosave.retryNow(blankKey(itemId));
  },

  /**
   * A write REPLAYED from the outbox was refused because the row has moved on.
   * The other version wins, and this device takes it.
   *
   * The mirror of `forceThroughConflict`, and the asymmetry is the whole rule:
   * a replay is a body from a session that has ended, with nobody looking at it,
   * against a row someone has since edited on purpose. Forcing it through would
   * undo a deliberate edit with an accidental one. So the entry goes and the
   * server's text is what this device shows — which, unlike the live case, means
   * committing the refetch rather than only recording its revision.
   */
  async takeServerVersion({ commit, dispatch, state }, { type, itemId, scope, origin = 'live' }) {
    countAutosaveEvent(AUTOSAVE_EVENT.CONFLICT_YIELDED);
    onRevisionConflict(type === AUTOSAVE_TYPE.NOTE ? 'note' : 'blank', itemId);
    if (type === AUTOSAVE_TYPE.NOTE) {
      // Item 1f, once more, for a REPLAY's yield: `refreshNoteFromServer`
      // commits `patchNoteEverywhere` unguarded, and the replay may have lost
      // its race to THIS device's own live slot — the member typed into the row
      // while the drain was still replaying it. Their slot has the newer body
      // and its own send settles the row; painting the server's text over the
      // editor here would take their words off the screen until it lands. A
      // LIVE yield is the opposite case: the engine is giving the row up after
      // three refusals and drops the slot right after this, so the server's
      // version is exactly what should be shown.
      if (origin === 'replay' && unsentNote(itemId)) return;
      await dispatch('refreshNoteFromServer', { noteId: itemId }).catch(() => {});
      return;
    }
    if (!scope?.lessonSlug) return;
    // ONLY FOR THE LESSON ON SCREEN. `blankAnswers` and `userAnswers` are the
    // open lesson's maps, and `fetchBlankAnswers` commits into them under the
    // open lesson's pages and program — so a yielded replay for ANOTHER lesson
    // (queued there, drained here) would paint that lesson's answers over the
    // one the member is looking at, and then empty a block of this lesson that
    // happens to share the id. The other lesson has nothing on screen to take
    // the server's version for; its own open fetches the row as it now is.
    const openLesson = state.currentLesson?.id ?? state.lessonSlug;
    if (scope.lessonSlug !== openLesson) return;
    // The same rule as the note above: the member typed into the replayed row
    // while the drain was still on it, and their slot has the newer answer.
    if (origin === 'replay' && autosave.hasPending(blankKey(itemId))) return;
    const rows = await dispatch('fetchBlankAnswers', {
      slug: scope.lessonSlug,
      // The entry's program, not the store's idea of the current one: the
      // envelope is bound to it, and it is what the read path opens the rows
      // under.
      programSlug: scope.programSlug,
    }).catch(() => null);
    // `fetchBlankAnswers` commits every row it finds, but a blank the other
    // device CLEARED has no row to commit — and the overlay put our unsent
    // answer on the screen. Empty it by hand, or the member keeps seeing a
    // version of the answer that no longer exists anywhere.
    const present = (rows || []).some(row => row?.blockId === scope.blockId);
    if (!present) commit('setBlankAnswer', { blockId: scope.blockId, answer: '' });
  },

  /**
   * Re-read the rows the member is looking at, because nothing pushes another
   * device's write to a device that already has the row on screen.
   *
   * Two moments call this, and they are the two where the member re-engages: an
   * editor opening, and the app resuming. Between them a note edited on their
   * tablet reaches this phone before they type on top of it — and if it does
   * not, their next keystroke is refused as stale and `forceThroughConflict`
   * settles it anyway. This only makes the common case quiet.
   *
   * NEVER OVER AN UNSENT EDIT. Item 1f is the rule and this is two more callers
   * of it — a refetch that landed on top of a body still waiting to be sent
   * would take the member's words off the screen and leave the send to put them
   * back, or not. Two things can be unsent: a live slot the engine holds, and an
   * outbox entry from an earlier session, which has no slot at all. Both are
   * skipped. (`setBlankAnswers` already honours both itself, so the blanks
   * fetch needs no guard here; `patchNoteEverywhere` honours neither, which is
   * why the note ids are filtered before the read rather than after it.)
   */
  async refreshAnnotationRows({ dispatch }, { noteIds = [], lessonSlug = null } = {}) {
    await Promise.all([
      ...[...new Set(noteIds)]
        // A new note's temp id is "open" too, and has no server row to re-read.
        .filter(noteId => isPersistedNoteId(noteId) && !unsentNote(noteId))
        .map(noteId => dispatch('refreshNoteFromServer', noteId).catch(() => {})),
      lessonSlug ? dispatch('fetchBlankAnswers', lessonSlug).catch(() => {}) : null,
    ]);
  },

  /**
   * What `resume` asks for: everything the member currently has open, decided
   * here so App.vue dispatches one thing and knows nothing about which pages
   * are mounted.
   */
  refreshOpenAnnotations({ state, dispatch }) {
    const lessonSlug = state.currentLesson?.id ?? state.lessonSlug;
    if (!state.openNoteIds.length && !lessonSlug) return Promise.resolve();
    return dispatch('refreshAnnotationRows', { noteIds: state.openNoteIds, lessonSlug });
  },

  /**
   * Re-read one note and make every copy of it in Vuex, and the corpus, agree
   * with the server. Used after a conflict; not part of the ordinary save path.
   *
   * `recordOnly` reads the row and commits NOTHING. The read is what records the
   * row's revision (the open hook does it for every row it passes), and a caller
   * that only wants the number must not also get the body: `patchNoteEverywhere`
   * would overwrite the text of a note the member has open and is typing in,
   * which is the failure item 1f exists to prevent. The list mutations honour
   * that rule through `preservePending`; this action is not one of them, so it
   * has to be asked.
   *
   * @param {{ noteId: string, recordOnly?: boolean }|string} payload a bare id
   *   is still accepted, since most callers want the committing form
   */
  async refreshNoteFromServer({ commit, dispatch, rootState }, payload) {
    const { noteId, recordOnly = false } =
      typeof payload === 'string' ? { noteId: payload } : payload || {};
    if (!noteId) return null;
    const auth = annotationAuth(rootState);
    const raw = await annotationsTransport.fetchNoteByIdRaw(auth, noteId);
    if (!raw) return null;
    const mapped = mapApiNote(await annotationsTransport.openNoteRow(auth, raw));
    if (!mapped) return null;
    if (recordOnly) return mapped;
    // Asked AGAIN, after the await, not only by the callers before it. The
    // round trip is exactly when a member starts typing in a just-opened editor
    // or a just-resumed page; a keystroke in it opens a slot, and the server's
    // older body landing on `allNotes` would re-sync the editor to it and take
    // their words off the screen — with the next keystroke sending old body
    // plus one character over the top. Item 1f, at the commit rather than the
    // call. The corpus still gets the raw row: it is a copy of the server, and
    // the Hub lays the pending body over it when it paints.
    if (!unsentNote(noteId)) {
      commit('patchNoteEverywhere', {
        noteId,
        patch: { body: mapped.body, text: mapped.text, content: mapped.content },
      });
    }
    dispatch('syncNoteToCorpus', { id: noteId, row: raw }).catch(() => {});
    return mapped;
  },

  /**
   * What a failed autosave means, decided in one place (the engine's error
   * listener, registered in `installAutosave`).
   *
   * Only two failures are final. A 404 says the row is gone, so nothing we send
   * revives it and the engine stops accepting input for it — the drawer's old
   * `unsavableNoteIds`, now for every surface. A `SealedWriteError` says the
   * write was refused before it was sent (encryption spec 6.7), so it will
   * never succeed either; that is Rule 2's one exception, and a blank puts its
   * previous answer back rather than reading as saved for ever and counting as
   * answered on the leader's roster.
   *
   * Everything else keeps the member's text where it is and is reported.
   */
  onAutosaveFailure(
    { commit, dispatch, state },
    { type, itemId, meta, body, error, conflicts, outcome },
  ) {
    // Another device wrote first, and this is the row the member is typing in
    // (the drain reports its replays separately, and handles them itself).
    // Refetch for the fresh revision and send our text again.
    //
    // Unless it has already lost three times: the engine has let the slot go by
    // then, so there is nothing left to force through, and the server's version
    // is what this device takes.
    if (isStaleRevisionError(error)) {
      const outOfForces = conflicts >= DEFAULT_MAX_CONFLICT_FORCES;
      const action = outOfForces ? 'takeServerVersion' : 'forceThroughConflict';
      dispatch(action, {
        type,
        itemId,
        meta,
        scope: { lessonSlug: meta?.lessonSlug, blockId: meta?.blockId },
      }).catch(() => {});
      return;
    }
    if (error?.response?.status === 404) {
      autosave.markUnsavable(type === AUTOSAVE_TYPE.NOTE ? noteKey(itemId) : blankKey(itemId));
    }
    // THE HOOK KNOWS THE ROW BUT NOT ITS REVISION: a conflict forgot the number
    // and the refetch that puts it back failed or has not landed. The engine
    // retries the send on its own (a counted retry, §9); what it cannot do is
    // get the number, so each such failure asks for it again, record-only —
    // committing the body would put the other device's words into the editor.
    // Not awaited: the engine's backoff is the wake, and the next send finds
    // whatever this read recorded.
    if (isSealedWriteError(error, SEALED_WRITE_ERRORS.NO_REVISION)) {
      if (type === AUTOSAVE_TYPE.NOTE) {
        dispatch('refreshNoteFromServer', { noteId: itemId, recordOnly: true }).catch(() => {});
      } else if (meta?.lessonSlug) {
        dispatch('fetchBlankAnswers', { slug: meta.lessonSlug, recordOnly: true }).catch(() => {});
      }
      return;
    }
    // THE SERVER SAYS THIS MEMBER IS ENROLLED AND THIS DEVICE SENT PLAINTEXT. The
    // policy retries it uncounted on the promise that the state ends — "the
    // status read or the key lands" — but nothing on a keyless device's write
    // path re-reads the status once it has been read: the hook answers "not
    // enrolled" from its snapshot for the rest of the session. So a phone whose
    // member enrolled on their tablet retried the same plaintext body every
    // thirty seconds for ever, on the wire each time, and the answer never
    // saved. Re-read here, once per failure (the action single-flights): the
    // next attempt then finds the member enrolled, is refused NO_KEY, and the
    // member is put on the approval path — the intended outcome.
    if (isEncryptedWriteRequiredError(error)) {
      dispatch('courses-devices/refreshStatus', undefined, { root: true }).catch(() => {});
      return;
    }
    if (type === AUTOSAVE_TYPE.BLANK && isSealedWriteError(error)) {
      // A refusal the engine is going to retry — a status that could not be
      // read, or a revision a conflict refetch is about to put back — is not a
      // verdict: the answer stays here, unsent, exactly as one typed offline
      // does. Only a final refusal restores.
      const undecided = isRetryable(outcome);
      const blockId = meta?.blockId;
      // `body` is what was sent (the last keystroke of the burst); `meta` is
      // what the blank held before the burst began. Restoring only while the
      // two still line up is what keeps a refusal from rolling back over a
      // later keystroke that has already moved the answer on.
      if (!undecided && blockId && state.blankAnswers[blockId] === body?.answer) {
        commit('restoreBlankAnswer', {
          lessonId: meta.lessonSlug,
          pageId: meta.pageId,
          blockId,
          ...meta.previous,
        });
      }
      return;
    }
    if (isSealedWriteError(error)) return;
    commit('setError', error?.response?.data?.message || error?.message || 'Failed to save');
  },

  // Move a standalone note to another lesson. The Note model has no PATCHable
  // lessonSlug, so a move is delete-old + create-new (preserving body + tags)
  // and we surface the new note's id. Optimistic: re-key locally → API; revert
  // on error.
  async moveStandaloneNote({ commit, state, rootState }, { noteId, newLessonSlug, program }) {
    const prior = state.allNotes.find(n => n.id === noteId);
    if (!prior) return;
    const priorTagIds = [...(prior.tags || [])];
    commit('updateNoteInAll', { noteId, patch: { lessonSlug: newLessonSlug } });
    try {
      const auth = annotationAuth(rootState);
      const body = buildNoteBody(prior.body || prior.content || prior.text || '');
      const data = await annotationsTransport.createNote(auth, {
        // The destination part (caller-supplied — the hub's current part). A move
        // is delete + recreate, so the new row must carry the right part; the
        // `state.currentProgramSlug` fallback is stale in the hub (see addStandaloneNote).
        programSlug: program || state.currentProgramSlug || 'next-steps-p1',
        lessonSlug: newLessonSlug,
        body,
        tagIds: priorTagIds.length ? priorTagIds : undefined,
        // A move is delete + recreate, so the new row has to be stamped or the
        // note lands in the student view. `state.view` IS the note's own view:
        // the hub only lists the active view's notes, so the note being moved
        // came from this one.
        view: state.view,
      });
      await annotationsTransport.deleteNote(auth, noteId);
      const mapped = mapApiNote({
        ...data,
        body: data.body || body,
        lessonSlug: newLessonSlug,
        highlightId: null,
      }) || {
        ...prior,
        id: data.id,
        lessonSlug: newLessonSlug,
      };
      // The moved note is a new row (new id): drop the old one, add the new.
      commit('removeNoteFromAll', noteId);
      commit('addNoteToAll', mapped);
      return mapped;
    } catch (error) {
      commit('updateNoteInAll', { noteId, patch: { lessonSlug: prior.lessonSlug } });
      commit('setError', error.response?.data?.message || error.message || 'Failed to move note');
      throw error;
    }
  },

  // Delete a note (standalone or highlight-attached): remove locally → DELETE;
  // re-insert on error.
  async deleteStandaloneNote({ commit, dispatch, state, rootState }, rawNoteId) {
    // A card can still name a new note by the temp id it opened it under.
    const noteId = resolveNoteId(state, rawNoteId);
    const prior = state.allNotes.find(n => n.id === noteId);
    autosave.cancel(noteKey(noteId));
    commit('removeNoteFromAll', noteId);
    commit('courses-autosave/clearFailed', noteKey(noteId), { root: true });
    // Nothing on the server to delete for a row it has never been told of — a
    // new note whose create is still to come, or failed. The local removal is
    // the whole delete (`deleteNote` has had this guard since the placeholder
    // ids; this one did not, so the DELETE 404'd and the row was put back,
    // undeletable). One whose POST is in the air is taken down by
    // `postStandaloneNote` when it lands and finds the row gone.
    if (!isPersistedNoteId(noteId)) return;
    try {
      await annotationsTransport.deleteNote(annotationAuth(rootState), noteId);
      dispatch('forgetNoteInCorpus', { id: noteId }).catch(() => {});
    } catch (error) {
      if (prior) commit('addNoteToAll', prior);
      commit('setError', error.response?.data?.message || error.message || 'Failed to delete note');
      throw error;
    }
  },

  // Toggle a tag on a hub note (operates over allNotes): apply locally → POST
  // or DELETE the note-tag association; revert on error.
  async setNoteHubTag({ commit, state, rootState }, { noteId, tag, attach }) {
    const prior = state.allNotes.find(n => n.id === noteId);
    if (!prior) return;
    const priorTags = [...(prior.tags || [])];
    const priorTagObjects = [...(prior.tagObjects || [])];
    const tagObjects = attach
      ? [...priorTagObjects.filter(t => t.id !== tag.id), tag]
      : priorTagObjects.filter(t => t.id !== tag.id);
    commit('updateNoteInAll', {
      noteId,
      patch: { tags: tagObjects.map(t => t.id), tagObjects },
    });
    try {
      const auth = annotationAuth(rootState);
      if (attach) {
        await annotationsTransport.attachNoteTag(auth, noteId, tag.id);
      } else {
        await annotationsTransport.detachNoteTag(auth, noteId, tag.id);
      }
    } catch (error) {
      commit('updateNoteInAll', {
        noteId,
        patch: { tags: priorTags, tagObjects: priorTagObjects },
      });
      commit('setError', error.response?.data?.message || error.message || 'Failed to update tag');
      throw error;
    }
  },

  async deleteHighlight({ commit, state, rootState }, { highlightId, pageId }) {
    // A highlight's notes go with it server-side, so any queued edit to one of
    // them must go too — otherwise it fires after the DELETE and 404s. The notes
    // live in two places: the Hub's flat list, and nested inside the highlight
    // in the reader's page-keyed map, which is the one that holds a DRAWER note
    // and is often the only one populated while the lesson is open.
    [
      ...(state.allNotes || []).filter(note => note.highlightId === highlightId),
      ...(findStoredHighlight(state, highlightId)?.notes || []),
    ].forEach(note => {
      if (note?.id) autosave.cancel(noteKey(note.id));
    });
    commit('setLoading', true);
    try {
      await annotationsTransport.deleteHighlight(annotationAuth(rootState), highlightId);
      // Reader passes pageId (page-keyed state); the hub doesn't (flat list).
      if (pageId) commit('removeHighlight', { highlightId, pageId });
      commit('removeHighlightFromAll', highlightId);
      // The highlight's notes went with it server-side, so they go here too. The
      // reader's copy is nested inside the highlight and left with it above; this
      // is the hub's separate flat list.
      commit('removeNotesForHighlight', highlightId);
    } catch (error) {
      // API is the source of truth — surface the failure rather than persisting
      // a divergent delete to localStorage.
      commit(
        'setError',
        error.response?.data?.message || error.message || 'Failed to delete highlight',
      );
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * @param {{ slug: string, recordOnly?: boolean }|string} payload a bare slug is
   *   the ordinary form. `recordOnly` reads the rows and commits nothing: the
   *   open hook records each row's revision on the way past, which is all
   *   `forceThroughConflict` wants — committing would put the other device's
   *   answers into fields the member is typing in.
   */
  async fetchBlankAnswers({ commit, state, rootState }, payload) {
    const {
      slug,
      recordOnly = false,
      programSlug = null,
    } = typeof payload === 'string' ? { slug: payload } : payload || {};
    if (!slug) return undefined;
    try {
      const answers = await annotationsTransport.fetchBlankAnswers(annotationAuth(rootState), {
        slug,
        // A caller that knows the row's program (a yielded outbox entry names
        // its own) says so; otherwise the lesson on screen decides.
        programSlug: programSlug || blankAnswerProgramSlug(state),
      });
      if (recordOnly) return answers;
      commit('setBlankAnswers', answers);
      recordUnreadable(commit, 'blank', answers, { idOf: a => a.blockId });
      // API is the source of truth (same row fill-in blanks and open-response
      // share). Hydrate the per-lesson local cache so a fresh device / reinstall
      // still paints via getPageAnswers after localStorage is empty.
      answers.forEach(a => {
        if (a.unreadable) return;
        commit('setUserAnswer', {
          lessonId: slug,
          pageId: pageIdForAnswerBlock(state.currentLesson, a.blockId),
          blankId: a.blockId,
          answer: a.answer ?? '',
        });
      });
      return answers;
    } catch (_) {
      // localStorage fallback already loaded via loadUserAnswersFromStorage
    }
  },

  upsertBlankAnswer({ commit, state, rootState }, { lessonSlug, blockId, answer }) {
    const pageId = pageIdForAnswerBlock(state.currentLesson, blockId);
    const programSlug = blankAnswerProgramSlug(state);
    // What goes back if the write is refused outright — see `onAutosaveFailure`.
    // Captured before the commit below, and the engine keeps the meta of the
    // FIRST keystroke of a burst, so this is the answer as the server has it
    // rather than one the member has already typed over.
    const previous = {
      blank: state.blankAnswers[blockId],
      user: state.userAnswers[lessonSlug]?.[pageId]?.[blockId],
    };
    commit('setBlankAnswer', { blockId, answer });
    // Keep userAnswers in sync for pageAnswers computed. Same path for a graded
    // blank and an open-response — both are (member, program, lesson, block).
    commit('setUserAnswer', { lessonId: lessonSlug, pageId, blankId: blockId, answer });
    // The third enrolment trigger, beside the Hub open and a highlight save.
    // AFTER the commits and never awaited, so the field is never waiting on a
    // network round trip; the first save may well go up plaintext, which is
    // correct and handled. A non-empty answer only: focus enrols nobody, and
    // neither does typing and deleting before the first send.
    if (typeof answer === 'string' && answer.trim() !== '') {
      enrolOnFirstBlank({ store: this, memberId: rootState.profile?.id ?? null });
    }
    // Rule 1 and Rule 2, as for the notes above. Blanks were the worst of the
    // four surfaces: one PUT per character, with no debounce at all.
    autosave.input(
      AUTOSAVE_TYPE.BLANK,
      annotationsTransport.blankAnswerItemId({ programSlug, lessonSlug, blockId }),
      { programSlug, lessonSlug, blockId, answer },
      {
        member: rootState.profile?.id ?? null,
        meta: { blockId, lessonSlug, pageId, previous },
      },
    );
  },

  // `program` is optional and defaults to the program in view, which is what every
  // lesson-side caller wants. The part chooser passes it explicitly, once per part,
  // because it renders both bars at the same time and there is no "current" program
  // when it is opened cold.
  async fetchProgress({ commit, state, rootState }, program) {
    commit('setLoading', true);
    commit('setError', null);
    const programSlug = program || state.currentProgramSlug || 'next-steps-p1';
    try {
      const token = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/lessons/progress`, {
        params: { program: programSlug },
        headers: { Authorization: `Bearer ${token}` },
      });
      // Server returns { programSlug, completed, total, completedSlugs }. Progress
      // is keyed by lesson slug (== lesson.id in the store); a truthy completedAt
      // drives the segmented progress bar and the carousel "completed" badges.
      const progressMap = {};
      (data.completedSlugs || []).forEach(slug => {
        progressMap[slug] = { completedAt: true };
      });
      commit('setProgramProgress', { program: programSlug, progress: progressMap });
      return data;
    } catch (error) {
      commit(
        'setError',
        error.response?.data?.message || error.message || 'Failed to fetch progress',
      );
      // Clear this program's rows only. A member with no access to one part must
      // not lose the part they have actually completed to that part's 403.
      commit('setProgramProgress', { program: programSlug, progress: {} });
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // Mark / un-mark a lesson complete. `lessonId` is the lesson slug. Completion is
  // toggled against the per-lesson endpoints under /lessons.
  // Mark / un-mark a lesson complete. `lessonId` is the lesson slug. Completion is
  // toggled against the per-lesson endpoints under /lessons.
  //
  // One write per lesson at a time (#1814). At the launch a member on a slow
  // network tapped Complete four times in a second and the server took one write
  // and 500'd three. The guard is HERE and not on the button: taps are input and
  // may come to mean something on their own (a celebration on repeated taps), so
  // the button keeps emitting and the store makes the write happen once.
  //   - same direction already in flight  → join it
  //   - opposite direction in flight      → let it land, then decide again
  //   - store already holds the state     → nothing to send
  //   - a failed write clears the slot     → the next tap retries, one at a time
  async updateProgress(ctx, payload) {
    const { lessonId, isCompleted = true } = payload;
    // Resolved the same way the write resolves it, so the slot names the same
    // row the request will: one lesson in one program.
    const program = payload.program || ctx.state.currentProgramSlug || 'next-steps-p1';
    const key = `${program}|${lessonId}`;
    const running = progressWritesInFlight.get(key);
    if (running && running.isCompleted === isCompleted) return running.promise;
    if (running) await running.promise.catch(() => {});
    // A completion the store already holds is not re-sent. An UNDO always is:
    // the store may simply not have loaded this lesson's progress (a deep link
    // skips the landing), and "not known complete here" is not "not complete".
    if (isCompleted && ctx.state.progress[lessonId]?.completedAt) {
      return { lessonId, isCompleted, skipped: true };
    }
    const promise = runUpdateProgress(ctx, payload).finally(() =>
      progressWritesInFlight.delete(key),
    );
    progressWritesInFlight.set(key, { isCompleted, promise });
    return promise;
  },

  async checkLeaderAccess({ commit, state, dispatch }) {
    // Local-dev override: force leader access without a real leader account.
    // Enabled via the VUE_APP_MOCK_LEADER_ACCESS build flag or a
    // `mockLeaderAccess` localStorage entry; never active in production.
    const mockLeader =
      process.env.VUE_APP_MOCK_LEADER_ACCESS === 'true' ||
      (typeof window !== 'undefined' && window.localStorage.getItem('mockLeaderAccess') === 'true');
    if (mockLeader) {
      commit('setLeaderStatus', true);
      return true;
    }
    try {
      // Leader-guide access is part of the per-member course access summary
      // (isLeaderGuideUser). Derive it from there instead of a dedicated call —
      // this retires the legacy GET /next-steps/leader/access. Load the summary
      // first if a page reached us without it.
      const access = state.access || (await dispatch('fetchCourseAccess'));
      const isLeader = !!access?.isLeaderGuideUser;
      commit('setLeaderStatus', isLeader);
      return isLeader;
    } catch (error) {
      console.error('Failed to check leader access:', error);
      // Fail closed: leader-only content (e.g. the Leader's Guide) stays gated
      // when the access check is unavailable — the client redirect is the only
      // gate today, so granting on error would leak leader content.
      commit('setLeaderStatus', false);
      return false;
    }
  },

  // Loads the per-member access summary (program visibility, lesson locks,
  // purchase CTAs, feature flags). Fail-closed: on error the access is cleared so
  // the UI shows the locked/hidden state rather than leaking access.
  //
  // `maxAgeMs`: accept the stored summary if it landed within this many ms
  // (ACCESS_FRESH_MS for callers on the same navigation as one that just fetched).
  // Omitted, the action always fetches. Overlapping dispatches share one request
  // either way. See the note above ACCESS_FRESH_MS.
  async fetchCourseAccess({ commit, state, rootState }, { maxAgeMs = 0 } = {}) {
    if (nsMockActive()) {
      // Snapshot before the commit so a change in program visibility can drop the
      // stale per-member feeds (see invalidateEntitlementFeedsIfChanged).
      const previousSignature = visibleProgramSignature(state.access);
      const access = buildNsMockAccess();
      commit('setCourseAccess', access);
      invalidateEntitlementFeedsIfChanged(commit, previousSignature, access);
      return access;
    }
    const isFresh =
      maxAgeMs > 0 &&
      state.access &&
      state.accessFetchedAt !== null &&
      monotonicNow() - state.accessFetchedAt <= maxAgeMs;
    if (isFresh) return state.access;
    if (accessInFlight) return accessInFlight;
    accessInFlight = (async () => {
      // Snapshot at request time, not at commit time, for the same reason as the
      // mock branch — and here too, so a joiner cannot move it.
      const previousSignature = visibleProgramSignature(state.access);
      try {
        const token = rootState.user.token;
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/courses/access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        commit('setCourseAccess', data);
        invalidateEntitlementFeedsIfChanged(commit, previousSignature, data);
        return data;
      } catch (error) {
        console.error('Failed to fetch course access:', error);
        commit('setCourseAccess', null);
        return null;
      } finally {
        accessInFlight = null;
      }
    })();
    return accessInFlight;
  },

  // Class/QR unlock: grants the TRIAL tier for a program. The leader unlock QR used
  // to share this action; it has its own endpoint now (redeemLeaderUnlock above).
  //
  // Refreshes the access summary on success so locks update immediately.
  async classUnlock({ dispatch, rootState }, { programSlug, code } = {}) {
    if (nsMockActive()) {
      grantNsMockTrial(programSlug);
      await dispatch('fetchCourseAccess');
      return {};
    }
    const token = rootState.user.token;
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/${programSlug}/class-unlock`,
      code ? { code } : {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    await dispatch('fetchCourseAccess');
    return data;
  },

  // Redeems a scanned leader unlock QR. The token is the whole identity of the
  // code — the program, the leader and the reason all come from its row — so this
  // sends nothing else and reads the program back off the response.
  //
  // Refreshes the access summary on success so the program page the member lands
  // on is already unlocked rather than briefly showing its paywall.
  async redeemLeaderUnlock({ dispatch, rootState }, token) {
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/unlock/${encodeURIComponent(token)}`,
      {},
      { headers: { Authorization: `Bearer ${rootState.user.token}` } },
    );
    await dispatch('fetchCourseAccess');
    return data;
  },

  // Mints the leader's unlock QR and returns { token, url, programSlug, reason }.
  //
  // The URL is taken from the RESPONSE and never assembled here. The scan path,
  // the short-link host and the query-parameter name are all things the API owns;
  // a second copy in the client goes stale silently — the QR would still render,
  // still scan, and resolve to nothing.
  //
  // Not wrapped in a try/catch: the caller renders a retry, and swallowing the
  // error here would leave it showing a QR-shaped blank.
  async generateLeaderUnlockQr({ rootState }, { programSlug, reason, reasonText } = {}) {
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/${programSlug}/leader/unlock-qr`,
      // reasonText is the reason IN WORDS and is sent for EVERY reason, not just
      // 'other': the consent screen passes the label the leader actually read (or
      // what they typed), and the API stores it on the QR row and writes it into
      // `course_entitlement.notes` at redemption. Only 'other' REQUIRES it; the
      // conditional just keeps an empty string off the wire.
      reasonText ? { reason, reasonText } : { reason },
      { headers: { Authorization: `Bearer ${rootState.user.token}` } },
    );
    return data;
  },

  // Opens a purchase transaction for the FULL tier. Returns
  // { reference, displayReference, status, checkoutUrl } — the sheet presents
  // that hosted checkout rather than opening a second transaction.
  async purchaseProgram({ rootState }, programSlug) {
    if (nsMockActive()) {
      // Stash which program this purchase is for — the mock has no server to
      // key the reference against, so confirmPurchase reads it back here.
      setPendingPurchaseSlug(programSlug);
      return {
        reference: `MOCK_${Date.now()}`,
        displayReference: 'FSNS-MOCK-0001',
        checkoutUrl: 'https://example.invalid/mock-checkout',
        status: 'PENDING',
        // Mirrors the real shape: the open quotes the price, so the sheet in the
        // mock shows an amount for the same reason the live one does.
        amountCents: nsMockPriceCents(),
        currency: 'SGD',
      };
    }
    const token = rootState.user.token;
    // Send an idempotency key so a retried or duplicated create returns the
    // SAME reference instead of opening a second transaction. The generic
    // /payments/transactions contract already takes one; this route did not
    // send it, which is why a double-tap could open two rows. The in-flight
    // guard on the CTA mitigates that; this is the actual fix.
    // NOTE: only effective once the course purchase route honours the key —
    // tracked separately. Sending it is harmless until then.
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/${programSlug}/purchase`,
      { idempotencyKey: newIdempotencyKey() },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return data;
  },

  // Confirms a purchase after payment; grants FULL on the server if PAID, then
  // refreshes access. Safe to call from the client (server re-verifies status).
  async confirmPurchase({ dispatch, rootState }, reference) {
    if (nsMockActive()) {
      const slug = getPendingPurchaseSlug() || NS_MOCK_PROGRAM_SLUGS[0];
      grantNsMockFull(slug, 'PAYMENT');
      clearPendingPurchaseSlug();
      await dispatch('fetchCourseAccess');
      return { granted: true };
    }
    const token = rootState.user.token;
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/purchases/${reference}/confirm`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (data?.granted) await dispatch('fetchCourseAccess');
    return data;
  },

  // ── V/N physical-book unlock lane (confirmed model 2026-07-22) ──────────────
  // A V/N buys the book at the IDR counter and gets a printed unlock card. They
  // scan it with their phone camera (or type the code) → permanent FULL for that
  // program (no ACTS verification, no expiry). Confirming a CG happens after the
  // grant, as a hard block inside Next Steps — not as a gate on redemption.
  // The real (non-mock) endpoints below — GET /courses/cgs, POST /courses/link-cg,
  // POST /courses/redeem-book — SHIPPED on the API in #798 (ONE-7870, 2026-07-22)
  // and are live on the dev API. Note they are on `development` only, so they
  // 404 against production until the next development → main promotion.
  // Each still falls back to Next Steps Mock Mode via nsMockActive() first.

  // Lists the CGs the member can set as their main CG (for the link-CG step).
  async fetchCgs({ rootState }) {
    if (nsMockActive()) {
      return getMockCgs();
    }
    const token = rootState.user.token;
    // Member-scoped CG list lives on the courses controller (GET /api/courses/cgs).
    // NOT /profile/cgs — that path is an existing Netsuite/API-key endpoint that
    // returns all CGs church-wide, so a member-facing handler there is unreachable.
    const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/courses/cgs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  },

  // Sets the member's main CG — the precondition for redeeming a book code.
  // The member TYPES their CG name; the server validates it case-insensitively
  // against the real CG directory and sets/creates their main CG. For V/N this
  // is asked on every book redemption (even if they already have a CG). Throws
  // (400) if the name doesn't match a legit CG.
  async linkMainCg({ rootState }, cgName) {
    if (nsMockActive()) {
      return linkNsMockCg(cgName);
    }
    const token = rootState.user.token;
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/link-cg`,
      { cgName },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return data;
  },

  // Redeems an IDR unlock card (or a legacy demo code in mock mode) → grants FULL
  // for the program the card is for, permanently.
  //
  // Linking a CG is NOT a precondition. The card is spent and the grant written
  // even for a V/N with no CG; Next Steps then hard-blocks them on the way in
  // until they confirm one (see CgHardBlockModal). Holding the burn until the CG
  // is linked would strand a member who closes the app mid-flow with a spent
  // card and no access.
  //
  // The single choke point for normalising the code: the camera lane and the
  // typed lane both funnel through here, so a scanned and a hand-typed card
  // reach the API as the same string. A code we don't recognise as an IDR card
  // is passed through untouched — mock mode still accepts BOOK-/CLASS- demo
  // codes, and the server is the authority on what is valid either way.
  async redeemBookCode({ dispatch, rootState }, { code } = {}) {
    const normalized = normalizeIdrCode(code) || code;

    if (nsMockActive()) {
      const result = redeemNsMockBookCode(normalized);
      if (result?.unlockedProgramSlug) await dispatch('fetchCourseAccess');
      return result;
    }
    // NOTE: today's server still treats a linked CG as a precondition and answers
    // { needsCgLink: true } with no grant. The IDR lane inverts that (grant, then
    // ask for the CG as a hard block). Both shapes are handled: needsCgLink is
    // returned to the caller, which puts the CG block up and retries. When the
    // server switches to grant-first, needsCgLink simply stops coming back.
    const token = rootState.user.token;
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/courses/redeem-book`,
      { code: normalized },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (data?.unlockedProgramSlug) await dispatch('fetchCourseAccess');
    return data;
  },
};

const DEFAULT_FEATURES = {
  blanks: true,
  highlights: false,
  notes: false,
  noteHub: false,
  // Answer-key mode (Regulars). Off by default and against an older API that
  // doesn't send it — blanks then behave as normal empty inputs.
  prefillBlanks: false,
};

const getters = {
  /** A blank's autosave key on the lesson in view, for its "not saved" line. */
  blankAutosaveKey: state => blockId => blankAutosaveKeyFor(state, blockId),
  // Whether anything this device fetched could not be read, and why. Locked means
  // the device holds no key for envelopes it could otherwise open: the Hub and the
  // reader show the "approve this device" placeholder instead of empty content.
  // Needing a newer app means at least one envelope is a version this build
  // cannot decode: the per-item placeholders stand in, under one "update the app"
  // line.
  // Two signals, either enough. The authority is courses-devices, which asks
  // GET /api/me/encryption whether this install is enrolled but still waiting to be
  // approved — true before a single row has been fetched. The row signal catches the
  // rest: something arrived sealed under a key this device does not hold, whether or
  // not the status endpoint has answered yet.
  //
  // The status signal is about the SERVER's row for this install (unregistered
  // or pending) and says nothing about whether the key is here — a device can
  // be both, holding MK from the platform copy or a recovery code with no row
  // registered yet. So a device that holds MK is never locked by that signal:
  // it would be a claim its own notes disprove, and it would cost the member the
  // Hub, which redirects a locked device to the approval screen, which answers
  // "this device already holds the key" and sends them back. The row signal is
  // unconditional: a row sealed under a generation this device does not hold is
  // locked whether or not it holds A key.
  annotationsLocked: (state, getters, rootState, rootGetters) =>
    (rootGetters['courses-devices/needsApproval'] === true &&
      rootGetters['courses-keys/hasMasterKey'] !== true) ||
    Object.values(state.unreadableAnnotations).includes(UNREADABLE_REASON.NO_KEY),
  annotationsNeedNewerApp: state =>
    Object.values(state.unreadableAnnotations).includes(UNREADABLE_REASON.NEEDS_NEWER_APP),
  annotationsFailed: state =>
    Object.values(state.unreadableAnnotations).includes(UNREADABLE_REASON.FAILED),
  // The blank answers on the open lesson this device could not read: block id ->
  // reason, so the reader can show each as unavailable rather than empty.
  unreadableBlanks: state =>
    Object.fromEntries(
      Object.entries(state.unreadableAnnotations)
        .filter(([key]) => key.startsWith('blank:'))
        .map(([key, reason]) => [key.slice('blank:'.length), reason]),
    ),
  // Which access notice, if any, a screen shows for the member's marks: locked
  // first, since nothing on this device opens; else the update line; else a line
  // for rows that did not open; else none.
  annotationsAccessState: (state, getters) => {
    if (getters.annotationsLocked) return ACCESS_NOTICE_STATE.LOCKED;
    if (getters.annotationsNeedNewerApp) return ACCESS_NOTICE_STATE.NEEDS_NEWER_APP;
    if (getters.annotationsFailed) return ACCESS_NOTICE_STATE.FAILED;
    return null;
  },
  isLeader: state => state.isLeader,
  view: state => state.view,
  isDark: state => state.isDark,
  // The remembered page for a (lesson, view) pair, or null if the reader has never
  // been anywhere in it. `?? null`, not `|| null`: COVER_LETTER_PAGE is 0, and a
  // reader standing on the brief is remembered, not forgotten. See setLessonPage.
  lessonPage: state => (slug, view) => state.lessonPages[lessonPageKey(slug, view)] ?? null,
  programs: state => state.programs,
  // A program's leader resources. Empty until fetched, and empty for a member the
  // API does not serve them to — so a caller can render straight off this without
  // a second leader check of its own.
  programResources: state => programSlug => state.programResources[programSlug] || [],
  currentResource: state => state.currentResource,
  // ── Access (server source of truth) ───────────────────────────────────────
  courseAccess: state => state.access,
  isLeaderGuideUser: state => !!state.access?.isLeaderGuideUser,
  programAccess: state => programSlug =>
    state.access?.programs?.find(p => p.slug === programSlug) || null,
  // Program tile visibility on the "all" page. As of 2026-09-04 N/V can SEE both
  // programs (but not buy them — see programPurchaseBlockReason). Defaults to
  // hidden until access is loaded.
  programVisible: (state, get) => programSlug => !!get.programAccess(programSlug)?.visible,
  programTier: (state, get) => programSlug => get.programAccess(programSlug)?.tier || 'PREVIEW',
  // Does this member hold Next Steps at all — any program above the PREVIEW tier.
  //
  // Tier rather than an entitlement row, so the automatic grants count: a Regular
  // or a qualified leader reads everything with no row to their name, and telling
  // them they have no course would be false.
  //
  // Used to decide where the no-access screen sends someone. Someone with the
  // course was refused ONE thing inside something they own, and belongs back in
  // Courses; someone without it has nothing to go back to there.
  //
  // Fails to false while access is unloaded, which sends them Home — the safe
  // direction: Home is reachable by everyone, /courses is not.
  hasCourseAccess: state => (state.access?.programs || []).some(p => p?.tier !== 'PREVIEW'),
  // Can this member get into Next Steps AT ALL right now — any program they may
  // see or already hold. Broader than `hasCourseAccess` (which is entitlement
  // only) because `visible` is the role's answer, and for an N/V that answer is
  // exactly what `courses-nv-visibility` switches.
  //
  // Fails to false while access is unloaded, same direction as its neighbours.
  canEnterCourses: state =>
    (state.access?.programs || []).some(p => p?.visible || p?.tier !== 'PREVIEW'),
  // Has this member ever written a note or a highlight? Straight from the server
  // summary (`hasAnnotations`), which asks the vault for one row per table.
  //
  // Only the Notes Hub gate reads it, and only for a member below the pastoral
  // cutoff: it is what keeps their own work reachable after the launch-weekend
  // visibility switch closes Courses to them again. An older API that does not
  // send the field leaves this false — see the guard for why that is safe.
  hasAnnotations: state => state.access?.hasAnnotations === true,
  // Should the buy BANNER be advertised to this member.
  programCanPurchase: (state, get) => programSlug => !!get.programAccess(programSlug)?.canPurchase,
  // May this member actually complete a sign-up/purchase — the gate the
  // locked-lesson "Sign up" tap reads (ONE-8315).
  //
  // "May we advertise" and "may we sell" are two different questions, but the
  // server answers them with one field: it sends `canPurchase` and nothing else.
  // ONE-8315 deliberately shipped no API change, so today this getter resolves
  // to exactly `canPurchase` — same answer, named after the question the tap is
  // really asking.
  //
  // `canSignUp` is read first purely so a future server that does split the two
  // is honoured without a client change; nothing sends it today. `??` and not
  // `||` so that such a server saying `canSignUp: false` is taken at its word
  // instead of falling through to `canPurchase`.
  //
  // Fails to false while access is unloaded — the safe direction for a getter
  // that decides whether to open a real transaction.
  programCanSignUp: (state, get) => programSlug => {
    const access = get.programAccess(programSlug);
    return !!(access?.canSignUp ?? access?.canPurchase);
  },
  // Why (if at all) this member cannot buy the program — the server's
  // `purchaseBlockReason`: 'ROLE' | 'PREREQUISITE' | 'FULL' | 'OFF_SALE' | null.
  // The page reads 'ROLE' to tell an N/V — who can see the program but not buy
  // it — apart from the other refusals, and shows the "interested to sign up?"
  // modal. null (purchasable) while access is unloaded or the field is absent.
  programPurchaseBlockReason: (state, get) => programSlug =>
    get.programAccess(programSlug)?.purchaseBlockReason ?? null,
  // Per-lesson access summary from the server: { slug, locked, isTrial,
  // isTrial }. Unknown lesson/program → null.
  lessonSummary: (state, get) => (programSlug, lessonSlug) =>
    get.programAccess(programSlug)?.lessons?.find(l => l.slug === lessonSlug) || null,
  // Lesson lock state from the server. Unknown lesson/program → locked (safe default).
  lessonLocked: (state, get) => (programSlug, lessonSlug) => {
    const lesson = get.lessonSummary(programSlug, lessonSlug);
    return lesson ? !!lesson.locked : true;
  },
  // Feature enablement (highlights/notes/note-hub gated to FULL or leader; blanks
  // always on). Defaults to blanks-only until access loads.
  // Merged over the defaults (not a bare fallback) so a server that omits a
  // newer flag — e.g. an older API without prefillBlanks — still yields a
  // complete, explicitly-false feature object.
  courseFeatures: (state, get) => programSlug => ({
    ...DEFAULT_FEATURES,
    ...(get.programAccess(programSlug)?.features || {}),
  }),
  // Account-level note/highlight/note-hub capability. Gates the cross-lesson
  // note-hub entry (the header icon and the /courses/notes page), which spans
  // programs and so cannot ask a single program's `features`.
  //
  // Read off the server's per-program `noteHub` flag rather than re-deriving the
  // rule from tiers. It used to be `isLeaderGuideUser || any FULL program`, which
  // was the rule until Lesson 1 became free in full: an unpaid member is now told
  // `noteHub: true` for Part 1 and may highlight and take notes in it, but the hub
  // that holds those notes stayed hidden from them.
  //
  // Still fails closed — no access loaded means no programs, so this is false.
  canUseNoteFeatures: state => (state.access?.programs || []).some(p => p?.features?.noteHub),
  lessonsByProgram: state => programSlug => {
    return state.lessons.filter(l => l.programSlug === programSlug);
  },
  // Lessons within a program, scoped to an intra-program section (`part`).
  lessonsBySection: state => (programSlug, part) => {
    return state.lessons.filter(l => l.programSlug === programSlug && l.part === part);
  },
  // The lesson's COURSE position — what the reader heading, the carousel card, the
  // sidebar and the notes hub all print as "Lesson N". Single source of truth so
  // they cannot disagree. Returns null for guides and the no-lesson bucket, which
  // are not numbered, and for a lesson the list does not know about.
  //
  // Read from the server, not computed. Next Steps is ONE ten-lesson course split
  // across two program rows, and this getter used to count within a program: Part
  // 2's five lessons numbered 1-5, so Lesson 6 rendered as "Part 2 · Lesson 1".
  // The client cannot fix that itself — the list response carries no sort_order,
  // and both programs tie on (part, sortOrder), which is exactly why the API
  // resolves the ordering in one place (LessonOrdinalService).
  //
  // The fallback is the old within-program index, for an API that predates the
  // field. It is wrong for Part 2 in the same way it always was, which is better
  // than a card with no number on it at all.
  lessonNumber: state => lesson => {
    if (!lesson) return null;
    const row = state.lessons.find(
      l => l.slug === lesson.slug && l.programSlug === lesson.programSlug,
    );
    if (row && Number.isFinite(row.lessonNumber)) return row.lessonNumber;
    if (Number.isFinite(lesson.lessonNumber)) return lesson.lessonNumber;

    const scoped = state.lessons.filter(l => {
      if (l.programSlug !== lesson.programSlug) return false;
      // Only real lessons are numbered; guides and the no-lesson record are not.
      if (l.kind !== 'LESSON') return false;
      return lesson.part ? l.part === lesson.part : true;
    });
    // A guide reaching the fallback must stay unnumbered rather than borrowing the
    // index of whatever lesson happens to sit at its position.
    if (lesson.kind && lesson.kind !== 'LESSON') return null;
    const index = scoped.findIndex(l => l.slug === lesson.slug);
    return index >= 0 ? index + 1 : null;
  },
  currentLessonProgress: state => lessonId => {
    return state.progress[lessonId] || { lastPage: 0, totalPages: 3 };
  },
  highlightsForPage: state => pageId => {
    return state.highlights[pageId] || [];
  },
  getUserAnswer: state => (lessonId, pageId, blankId) => {
    return state.blankAnswers[blankId] || state.userAnswers[lessonId]?.[pageId]?.[blankId] || '';
  },
  getPageAnswers: state => (lessonId, pageId) => {
    const pageLevel = state.userAnswers[lessonId]?.[pageId] || {};
    // Merge API blank answers (keyed by blockId) — page-level localStorage overrides
    return { ...state.blankAnswers, ...pageLevel };
  },
  getLessonAnswerProgress: state => lessonId => {
    const lesson = state.userAnswers[lessonId] || {};
    let filledCount = 0;
    Object.values(lesson).forEach(pageAnswers => {
      if (typeof pageAnswers !== 'object') return;
      Object.values(pageAnswers).forEach(answer => {
        if (answer && answer.trim() !== '') filledCount++;
      });
    });
    // Also count API blank answers
    Object.values(state.blankAnswers).forEach(answer => {
      if (answer && answer.trim() !== '') filledCount++;
    });
    return filledCount;
  },
  // Whether a student has begun answering this lesson — at least one non-empty
  // fill-in-the-blank recorded for it. Keyed by lesson SLUG (the same key
  // upsertBlankAnswer / getPageAnswers use). Reads ONLY the per-lesson userAnswers
  // map, deliberately ignoring the shared blankAnswers (which holds only the
  // currently-open lesson and would otherwise leak into every card's count in the
  // hub). This drives the "In Progress" card state.
  //
  // Device-local by nature: answers that live only server-side — saved on another
  // device and not yet re-typed here — aren't reflected until the student edits a
  // blank on this device. Completion stays server-driven (progress.completedAt), so
  // only the intermediate "In Progress" tag has this limitation.
  lessonHasAnswers: state => lessonSlug => {
    const pages = state.userAnswers[lessonSlug] || {};
    return Object.values(pages).some(pageAnswers => {
      if (!pageAnswers || typeof pageAnswers !== 'object') return false;
      return Object.values(pageAnswers).some(a => a && String(a).trim() !== '');
    });
  },
  // How many fill-in-the-blank slots in the currently loaded lesson are still empty.
  // Walks the same two blank forms the renderer draws — inline blanks inside a
  // paragraph (keyed by the inline item id) and a standalone blank block (one
  // pipe-delimited value keyed by the block id, one slot per '____') — reading the
  // exact answer source the UI reads (getPageAnswers merges API + local answers).
  // Used to warn a student on lesson complete; leaders/answer-key readers are gated
  // out by the caller, so this getter itself is role-agnostic.
  getUnfilledBlanksCount: (state, getters) => lessonId => {
    const isEmpty = value => !value || String(value).trim() === '';
    // A blank whose answer this device could not open is answered, just not
    // readable here; it must not be counted as work left to do.
    const unreadable = getters.unreadableBlanks;
    const pages = state.currentLesson?.pages || [];
    let unfilled = 0;
    pages.forEach(page => {
      const answers = getters.getPageAnswers(lessonId, page.id);
      (page.blocks || []).forEach(block => {
        if (block.type === 'paragraph' && Array.isArray(block.inline)) {
          block.inline.forEach(item => {
            if (item.type === 'blank' && !unreadable[item.id] && isEmpty(answers[item.id])) {
              unfilled++;
            }
          });
          return;
        }
        if (block.type === 'blank') {
          if (unreadable[block.id]) return;
          // Two authoring shapes, and the count has to match whichever one the block
          // carries: rich-text runs count their `blank` items, a legacy prompt counts
          // its `____` markers. Reading only the prompt made a rich-text blank look
          // fully answered — it has no `____` — so the lesson-complete warning would
          // pass over a page of empty inputs.
          const slots = Array.isArray(block.inline)
            ? countBlanksInRun(block.inline)
            : Math.max(0, String(block.prompt || '').split('____').length - 1);
          const parts = String(answers[block.id] || '').split('|');
          for (let i = 0; i < slots; i++) {
            if (isEmpty(parts[i])) unfilled++;
          }
        }
      });
    });
    return unfilled;
  },
  // ── NotesHub getters ───────────────────────────────────────────────────────
  // Maps pageId → lessonId by walking the loaded lessons. Used to group
  // page-keyed highlights by lesson for NotesHub display.
  pageToLessonMap: state => {
    const map = {};
    (state.lessons || []).forEach(lesson => {
      (lesson.pages || []).forEach(page => {
        if (page?.id) map[page.id] = lesson.id;
      });
    });
    return map;
  },
  // { [lessonId]: Highlight[] } — derived from the per-page store.
  highlightsByLesson: (state, getters) => {
    const out = {};
    const pageMap = getters.pageToLessonMap;
    Object.entries(state.highlights || {}).forEach(([pageId, highlights]) => {
      const lessonId = pageMap[pageId];
      if (!lessonId) return;
      out[lessonId] = [...(out[lessonId] || []), ...highlights];
    });
    return out;
  },
  // Hub Highlights tab — grouped straight from the flat API list by lessonSlug.
  // Unlike `highlightsByLesson` (in-lesson, page-keyed, needs loaded pages), this
  // works in the hub where lesson pages are NOT loaded. lessonId === lessonSlug.
  allHighlightsByLesson: state => {
    const out = {};
    (state.allHighlights || []).forEach(h => {
      const lid = h.lessonSlug;
      if (!lid) return;
      out[lid] = [...(out[lid] || []), h];
    });
    return out;
  },
  // { [lessonId]: Note[] } — sourced from the API-loaded `allNotes` list
  // (GET /api/notes returns both standalone and highlight-attached notes, each
  // carrying its lessonSlug). lessonId === lessonSlug (normalizeApiLesson sets
  // lesson.id = slug), so we group by lessonSlug. The hub consumes `content`
  // (display text) and `tags` (id array) — both mapped by mapApiNote.
  notesByLesson: state => {
    const out = {};
    (state.allNotes || []).forEach(note => {
      const lid = note.lessonSlug;
      if (!lid) return;
      out[lid] = [...(out[lid] || []), note];
    });
    return out;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
